import {
  ChangeEvent,
  FormEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ADMIN_EMAIL, KEYS, SUPABASE_KEY, SUPABASE_URL } from "./cloud-config";
import { clone, localDateKey, makeId, safeNumber, safeRead } from "./storage";
import {
  createFundingWorksheet,
  fundingCellDisplay,
  FundingWorksheet,
  normalizeFundingWorksheet,
  resizeFundingWorksheet,
  sheetCellRef,
  sheetColumnName,
} from "./funding-sheet";
import {
  createProjectFromTemplate,
  EVENT_CATEGORY_LABEL,
  EVENT_STATUS_LABEL,
  FieldType,
  FundingSource,
  FundingYearPlan,
  fundingPlanPaid,
  fundingPlanPayments,
  fundingPlanUnpaidSchedules,
  fundingSummary,
  getProjectLastEvent,
  getSuggestedNextAction,
  normalizeProject,
  PROJECT_TYPE_LABEL,
  Project,
  ContractPaymentPlan,
  ContractRecord,
  completeContractPlanAmounts,
  contractPaymentProgress,
  PaymentRecord,
  ProcurementBasisKind,
  ProcurementForm,
  ProcurementPackage,
  ProcurementResult,
  ProjectEvent,
  ProjectEventCategory,
  ProjectEventStatus,
  projectProgress,
  projectRules,
  Service,
  syncContractParticipant,
  updateContractWithParticipant,
  unlinkContractParticipant,
  StageStatus,
  ConfigField,
  templateStageNames,
} from "./project-model";
type Todo = {
  id?: string;
  ownerId?: string;
  text: string;
  done: boolean;
  createDate?: string;
  completedAt?: string;
  completedOn?: string;
  scheduledDate?: string;
  linkedProjectId?: string;
  linkedEventId?: string;
  eventCategory?: ProjectEventCategory;
  linkedStageId?: string;
  kind?: "todo" | "service" | "administrative" | "other";
  stage?: string;
  note?: string;
};
type OvertimeRecord = {
  id: string;
  date: string;
  days: string;
  note: string;
  status: "active" | "used";
  usedDate?: string;
  /** 法定节假日加班：记录存在但不计倒休。 */
  holiday?: boolean;
};
type LeaveRecord = {
  id?: string;
  type: "年假" | "存休" | "其他";
  date: string;
  days: string;
  note: string;
};
type AttendanceAdjustment = {
  id: string;
  date: string;
  kind: "annual" | "saved";
  days: string;
  note?: string;
};
type Attendance = {
  annual: number | string;
  saved: number | string;
  records: OvertimeRecord[];
  leaveRecords: LeaveRecord[];
  adjustments?: AttendanceAdjustment[];
};
type AppPage = "home" | "projects" | "projectDetail" | "attendance" | "calendar" | "nonProjects" | "tools";
type ModalName =
  | "todoHistory"
  | "todoCreate"
  | "todoEdit"
  | "todoCompletion"
  | "matter"
  | "dayLog"
  | "dayOverview"
  | "createProject"
  | "renameProject"
  | "stagePicker"
  | "stageStart"
  | "stageEditor"
  | "stageConfig"
  | "nodeLibrary"
  | "service"
  | "projectEvent"
  | "nextAction"
  | "projectSettings"
  | "projectRecycleBin"
  | "funding"
  | "fundingYear"
  | "fundingPaymentPreview"
  | "fundingUnpaidPreview"
  | "fundingClawbackPreview"
  | "procurement"
  | "contract"
  | "contractAmount"
  | "contractSettlement"
  | "payment"
  | "overtime"
  | "useOvertime"
  | "leave"
  | "attendanceSettings"
  | "attendanceAdjust"
  | "search"
  | "toolDate"
  | "toolSettlement"
  | "toolCalculator"
  | "toolBackup"
  | "trash"
  | "appearance"
  | "importPreview"
  | "account"
  | "admin"
  | "securityCheck"
  | "e2eEnable"
  | "recoveryCode"
  | "recovery"
  | "backupPassword"
  | null;

type RealtimeChannel = {
  on: (
    event: "postgres_changes",
    config: { event?: string; schema?: string; table?: string; filter?: string },
    callback: (payload: { new?: Record<string, unknown> }) => void,
  ) => RealtimeChannel;
  subscribe: () => RealtimeChannel;
};
type SupabaseClient = {
  auth: {
    getSession: () => Promise<{ data: { session: { user: CloudUser } | null } }>;
    signInWithPassword: (payload: { email: string; password: string }) => Promise<{ data: { user: CloudUser | null }; error: { message: string } | null }>;
    signUp: (payload: { email: string; password: string }) => Promise<{ data: { user: CloudUser | null; session: unknown }; error: { message: string } | null }>;
    signInWithOtp: (payload: { email: string }) => Promise<{ error: { message: string } | null }>;
    verifyOtp: (payload: { email?: string; token?: string; token_hash?: string; type: string }) => Promise<{ data: { user: CloudUser | null }; error: { message: string } | null }>;
    resetPasswordForEmail: (payload: { email: string }) => Promise<{ error: { message: string } | null }>;
    updateUser: (attributes: { email?: string; password?: string; data?: Record<string, unknown> }) => Promise<{ error: { message: string } | null }>;
    signOut: (options?: { scope?: "global" | "local" | "others" }) => Promise<unknown>;
  };
  rpc: (
    functionName: string,
    parameters?: Record<string, unknown>,
  ) => Promise<{ data: unknown; error: { message: string } | null }>;
  from: (table: string) => {
    select: (columns: string) => {
      eq: (column: string, value: string) => {
        maybeSingle: () => Promise<{ data: { content: CloudPayload } | null; error: { message: string } | null }>;
      };
      then: (
        resolve: (result: { data: Array<{ user_id: string; content: CloudPayload }> | null; error: { message: string } | null }) => unknown,
      ) => Promise<unknown>;
    };
    upsert: (payload: unknown) => Promise<{ error: { message: string } | null }>;
    delete: () => {
      eq: (column: string, value: string) => Promise<{ error: { message: string } | null }>;
    };
  };
  channel: (name: string) => RealtimeChannel;
  removeChannel: (channel: RealtimeChannel) => Promise<unknown>;
};
type CloudUser = { id: string; email?: string };
type AdminUsageRow = {
  user_id: string;
  email: string;
  first_seen_at: string;
  last_seen_at: string;
};
type SyncSection = "projects" | "config" | "attendance" | "todo" | "calendar" | "settlement";
type SyncCollection = "projects" | "projectEvents" | "projectParts" | "todo" | "overtime" | "leave" | "attendance" | "calendar" | "config" | "settlement";
type ItemVersion = { updated_at: number; device_id?: string; deleted?: boolean };
type SyncMeta = {
  version: number;
  device_id: string;
  sections: Record<SyncSection, number>;
  records: Record<SyncCollection, Record<string, ItemVersion>>;
};
type StoredSyncMeta = {
  version?: number;
  device_id?: string;
  sections?: Partial<Record<SyncSection, number>>;
  records?: Partial<Record<SyncCollection, Record<string, ItemVersion>>>;
};
type SyncData = {
  projects: Project[];
  config: NodeConfig;
  attendance: Attendance;
  todo: Todo[];
  calendar: CalendarEntries;
  settlement: SettlementRecord[];
};
export type WorkRecord = {
  id: string;
  ownerId?: string;
  sourceTodoId?: string;
  linkedProjectId?: string;
  content: string;
  occurredAt?: string;
  amount?: string;
  organization?: string;
  contact?: string;
  nextStep?: string;
  dueDate?: string;
  attachments?: WorkRecordAttachment[];
  createdAt?: string;
};
export type WorkRecordAttachment = {
  id: string;
  name: string;
  size: number;
  content: string;
  addedAt: string;
};
export type CalendarEntries = Record<string, WorkRecord[]>;
type SettlementRecord = {
  id: string;
  createdAt: string;
  submittedAmount: string;
  approvedAmount: string;
  contractAmount: string;
  change: number;
  contractRatio: number;
  submittedRatio: number;
};
type StoredCalendarEntries = Record<string, string | WorkRecord | Array<string | WorkRecord>>;
type CloudPayload = {
  owner_id?: string;
  projects?: Project[];
  config?: NodeConfig;
  attendance?: Attendance;
  todo?: Todo[];
  calendar?: StoredCalendarEntries;
  settlement?: SettlementRecord[];
  updated_at?: string;
  sync_meta?: StoredSyncMeta;
};
type NodeConfig = Record<string, ConfigField[] | Record<string, string[]>>;

// Earlier versions stored a single string for each date.  Keep every old value
// as one complete record so upgrading never splits or loses user-entered text.
const stableWorkRecordId = (date: string, content: string, occurrence: number) => {
  let hash = 2166136261;
  const source = `${date}\u0000${content}`;
  for (let index = 0; index < source.length; index += 1) {
    hash ^= source.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return `legacy-work-${date}-${(hash >>> 0).toString(36)}-${occurrence}`;
};
export const normalizeCalendar = (value: StoredCalendarEntries | null | undefined): CalendarEntries =>
  Object.fromEntries(
    Object.entries(value || {}).flatMap(([date, stored]) => {
      const occurrences = new Map<string, number>();
      const records = (Array.isArray(stored) ? stored : [stored]).flatMap((item) => {
        const content = String(typeof item === "string" ? item : item?.content || "").trim();
        if (!content) return [];
        const occurrence = occurrences.get(content) || 0;
        occurrences.set(content, occurrence + 1);
        return [{
          id: typeof item === "object" && item?.id
            ? String(item.id)
            : stableWorkRecordId(date, content, occurrence),
          content,
          ...(typeof item === "object" && item?.ownerId ? { ownerId: String(item.ownerId) } : {}),
          ...(typeof item === "object" && item?.sourceTodoId ? { sourceTodoId: String(item.sourceTodoId) } : {}),
          ...(typeof item === "object" && item?.linkedProjectId ? { linkedProjectId: String(item.linkedProjectId) } : {}),
          ...(typeof item === "object" && item?.occurredAt ? { occurredAt: String(item.occurredAt) } : {}),
          ...(typeof item === "object" && item?.amount ? { amount: String(item.amount) } : {}),
          ...(typeof item === "object" && item?.organization ? { organization: String(item.organization) } : {}),
          ...(typeof item === "object" && item?.contact ? { contact: String(item.contact) } : {}),
          ...(typeof item === "object" && item?.nextStep ? { nextStep: String(item.nextStep) } : {}),
          ...(typeof item === "object" && item?.dueDate ? { dueDate: String(item.dueDate) } : {}),
          ...(typeof item === "object" && Array.isArray(item?.attachments) ? { attachments: item.attachments } : {}),
          ...(typeof item === "object" && item?.createdAt ? { createdAt: String(item.createdAt) } : {}),
        }];
      });
      return records.length ? [[date, records]] : [];
    }),
  );
export const mergeCalendarEntries = (
  base: CalendarEntries,
  overlay: CalendarEntries,
): CalendarEntries => {
  const recordsById = new Map<string, { date: string; record: WorkRecord }>();
  [base, overlay].forEach((calendar) => {
    Object.entries(calendar).forEach(([date, records]) => {
      records.forEach((record) => recordsById.set(record.id, { date, record: clone(record) }));
    });
  });
  const merged: CalendarEntries = {};
  recordsById.forEach(({ date, record }) => {
    merged[date] = [...(merged[date] || []), record];
  });
  return merged;
};
export const calendarForOwner = (calendar: CalendarEntries, owner: string): CalendarEntries =>
  Object.fromEntries(
    Object.entries(calendar).flatMap(([date, records]) => {
      const owned = records.filter((record) => !record.ownerId || record.ownerId === owner);
      return owned.length ? [[date, owned]] : [];
    }),
  );
export const isolateOwnedRecords = (data: SyncData, owner: string): SyncData => ({
  ...data,
  todo: data.todo.filter((item) => !item.ownerId || item.ownerId === owner),
  calendar: calendarForOwner(data.calendar, owner),
});
export const claimImportedRecords = (data: SyncData, owner: string): SyncData => ({
  ...data,
  todo: data.todo.map((item) => ({ ...item, ownerId: owner })),
  calendar: Object.fromEntries(
    Object.entries(data.calendar).map(([date, records]) => [
      date,
      records.map((record) => ({ ...record, ownerId: owner })),
    ]),
  ),
});
type WorkRecordSyncEntry = WorkRecord & {
  date: string;
  order: number;
};
type UndoState = {
  owner: string;
  message: string;
};
type BackupImportSummary = {
  projects: number;
  projectReplacements: number;
  todos: number;
  todoDuplicates: number;
  overtime: number;
  leave: number;
  workRecords: number;
  settlement: number;
};
type PendingImport = {
  fileName: string;
  merged: SyncData;
  summary: BackupImportSummary;
};
type ProjectPartEntry = {
  projectId: string;
  kind: "stage" | "service" | "fund" | "fundingPlan" | "procurement" | "contract" | "payment" | "worksheet";
  parentId?: string;
  grandParentId?: string;
  order: number;
  value: unknown;
};

declare global {
  interface Window {
    supabase?: {
      createClient: (url: string, key: string, options?: unknown) => SupabaseClient;
    };
  }
}

const NODE_CATEGORIES: Record<string, string[]> = {
  "项目前期手续": ["方案设计", "市局报审", "区政府会", "发改委立项", "事前评估", "财政评审"],
  "采购手续": ["委托代理签合同", "编制采购文件", "意向公开", "发布公告", "完成评标", "发布中标公示"],
  "管理处内部手续": ["党组会", "主任办公会", "纸质请示", "付款"],
  "工程实施过程及后续手续": ["开工备案", "设计交底", "施工入场", "竣工验收", "结算审核", "资料归档"],
};

const DEFAULT_CONFIG: Record<string, ConfigField[]> = {
  方案设计: [
    { key: "finishDate", label: "图纸完成时间", type: "date" },
    { key: "designUnit", label: "设计单位", type: "text" },
  ],
  市局报审: [
    { key: "submitDate", label: "报送时间", type: "date" },
    { key: "replyDate", label: "批复时间", type: "date" },
  ],
  区政府会: [
    { key: "meetDate", label: "上会时间", type: "date" },
    { key: "minutesNo", label: "纪要文号", type: "text" },
  ],
  发改委立项: [
    { key: "submitDate", label: "申报时间", type: "date" },
    { key: "approvalDate", label: "批复时间", type: "date" },
    { key: "projectCode", label: "项目代码", type: "text" },
  ],
  事前评估: [
    { key: "evalDate", label: "评估会议时间", type: "date" },
    { key: "reportDate", label: "报告出具时间", type: "date" },
  ],
  财政评审: [
    { key: "sendDate", label: "送审时间", type: "date" },
    { key: "reportDate", label: "评审报告时间", type: "date" },
    { key: "auditAmount", label: "审定金额（元）", type: "number" },
  ],
  委托代理签合同: [
    { key: "signDate", label: "签订时间", type: "date" },
    { key: "agencyName", label: "代理机构名称", type: "text" },
  ],
  编制采购文件: [
    { key: "finishDate", label: "编制完成时间", type: "date" },
    { key: "controlPrice", label: "控制价／预算（元）", type: "number" },
    { key: "purchaseMode", label: "采购方式", type: "select", options: ["公开招标", "竞争性磋商", "单一来源", "询价"] },
  ],
  意向公开: [
    { key: "publicDate", label: "挂网时间", type: "date" },
    { key: "link", label: "公告链接", type: "text" },
  ],
  发布公告: [
    { key: "publishDate", label: "发布时间", type: "date" },
    { key: "openDate", label: "预计开标时间", type: "date" },
  ],
  完成评标: [
    { key: "evalDate", label: "评标时间", type: "date" },
    { key: "evalLocation", label: "评标地点", type: "text" },
  ],
  发布中标公示: [
    { key: "noticeDate", label: "公示发布时间", type: "date" },
    { key: "winner", label: "中标单位", type: "text" },
    { key: "winAmount", label: "中标金额（元）", type: "number" },
  ],
  党组会: [
    { key: "meetDate", label: "会议时间", type: "date" },
    { key: "topic", label: "议题名称", type: "text" },
  ],
  主任办公会: [
    { key: "meetDate", label: "会议时间", type: "date" },
    { key: "topic", label: "议题名称", type: "text" },
  ],
  纸质请示: [{ key: "approveDate", label: "批准时间", type: "date" }],
  付款: [
    { key: "submitFinanceDate", label: "资金使用申请单交单时间", type: "date" },
    { key: "payAmount", label: "支付金额（元）", type: "number" },
    { key: "payType", label: "款项性质", type: "select", options: ["首笔款", "进度款", "尾款", "质保金"] },
  ],
  开工备案: [
    { key: "submitDate", label: "备案时间", type: "date" },
    { key: "recordType", label: "备案类型", type: "select", options: ["监督站备案", "工程科备案"] },
  ],
  设计交底: [{ key: "meetDate", label: "交底时间", type: "date" }],
  施工入场: [{ key: "entryDate", label: "进场日期", type: "date" }],
  竣工验收: [{ key: "checkDate", label: "竣工验收日期", type: "date" }],
  结算审核: [
    { key: "finishAuditDate", label: "审定日期", type: "date" },
    { key: "finalAmount", label: "审定金额（元）", type: "number" },
    { key: "reduceAmount", label: "核减金额（元）", type: "number" },
  ],
  资料归档: [{ key: "archiveDate", label: "归档日期", type: "date" }],
};

const EMPTY_ATTENDANCE: Attendance = { annual: 0, saved: 0, records: [], leaveRecords: [], adjustments: [] };
type AppearanceSettings = {
  theme: "system" | "light" | "dark" | "contrast";
  accent: "green" | "blue" | "plum" | "terracotta";
  fontSize: "standard" | "large";
  density: "compact" | "standard" | "comfortable";
};
const APPEARANCE_KEY = "workassistant_appearance_v1";
const DEFAULT_APPEARANCE: AppearanceSettings = { theme: "light", accent: "green", fontSize: "standard", density: "standard" };
const MAX_ATTACHMENT_CHARS = 40000;
const MAX_ATTACHMENTS_PER_RECORD = 8;
const TEXT_FILE_EXTENSIONS = new Set([
  "txt", "md", "markdown", "csv", "json", "xml", "log",
  "js", "ts", "jsx", "tsx", "html", "css", "yml", "yaml",
  "ini", "conf", "sql", "py", "java", "c", "cpp", "h", "sh", "bat", "ps1", "svg",
]);
const OFFICE_TEXT_FORMATS = new Set(["docx", "xlsx", "pptx"]);
const LEGACY_OFFICE_FORMATS = new Set(["doc", "wps", "et", "dps"]);
const isTextLikeFile = (file: File) => {
  const extension = file.name.split(".").pop()?.toLowerCase() || "";
  return file.type.startsWith("text/") || TEXT_FILE_EXTENSIONS.has(extension);
};
const fileExtension = (name: string) => name.split(".").pop()?.toLowerCase() || "";
const isSupportedSource = (file: File) => isTextLikeFile(file) || OFFICE_TEXT_FORMATS.has(fileExtension(file.name));
const isZipArchive = async (file: File) => {
  const head = new Uint8Array(await file.slice(0, 4).arrayBuffer());
  return head.length >= 4 && head[0] === 0x50 && head[1] === 0x4b && head[2] === 0x03 && head[3] === 0x04;
};
const readTextFile = (file: File) => new Promise<string>((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result || ""));
  reader.onerror = () => reject(new Error("读取失败"));
  reader.readAsText(file, "utf-8");
});
const decodeXmlEntities = (text: string) =>
  text
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, "&");
export const extractTagGroups = (xml: string, groupTag: string, textTag: string) => {
  const groups: string[] = [];
  const groupPattern = new RegExp(`<${groupTag}\\b[^>]*>([\\s\\S]*?)</${groupTag}>`, "g");
  let groupMatch: RegExpExecArray | null;
  while ((groupMatch = groupPattern.exec(xml))) {
    const textPattern = new RegExp(`<${textTag}\\b[^>]*>([^<]*)</${textTag}>`, "g");
    const parts: string[] = [];
    let textMatch: RegExpExecArray | null;
    while ((textMatch = textPattern.exec(groupMatch[1]))) parts.push(decodeXmlEntities(textMatch[1]));
    if (parts.length) groups.push(parts.join(""));
  }
  return groups.join("\n");
};
const readUint16 = (bytes: Uint8Array, offset: number) => (bytes[offset] | (bytes[offset + 1] << 8)) >>> 0;
const readUint32 = (bytes: Uint8Array, offset: number) => (bytes[offset] | (bytes[offset + 1] << 8) | (bytes[offset + 2] << 16) | (bytes[offset + 3] << 24)) >>> 0;
const inflateRaw = async (bytes: Uint8Array) => {
  const stream = new Blob([bytes.slice()]).stream().pipeThrough(new DecompressionStream("deflate-raw"));
  return new Uint8Array(await new Response(stream).arrayBuffer());
};
const textFromOfficeEntry = (name: string, xml: string) => {
  const lower = name.toLowerCase();
  if (lower.endsWith("document.xml")) return extractTagGroups(xml, "w:p", "w:t");
  if (lower.endsWith("sharedstrings.xml")) return extractTagGroups(xml, "si", "t");
  if (lower.includes("/worksheets/")) return extractTagGroups(xml, "c", "t");
  if (lower.includes("/slides/")) return extractTagGroups(xml, "a:p", "a:t");
  return "";
};
const officeEntryOfInterest = (name: string) => {
  const lower = name.toLowerCase();
  return lower.endsWith("document.xml")
    || lower.endsWith("sharedstrings.xml")
    || lower.includes("/worksheets/")
    || lower.includes("/slides/");
};
export const extractOfficeText = async (file: File) => {
  if (typeof DecompressionStream === "undefined") throw new Error("当前浏览器不支持读取办公文件");
  const buffer = new Uint8Array(await file.arrayBuffer());
  let eocd = -1;
  for (let offset = buffer.length - 22; offset >= Math.max(0, buffer.length - 22 - 65536); offset -= 1) {
    if (readUint32(buffer, offset) === 0x06054b50) { eocd = offset; break; }
  }
  if (eocd < 0) throw new Error("不是有效的 Office 文件");
  const entryCount = readUint16(buffer, eocd + 10);
  const directoryOffset = readUint32(buffer, eocd + 16);
  const parts: string[] = [];
  let cursor = directoryOffset;
  for (let index = 0; index < entryCount; index += 1) {
    if (readUint32(buffer, cursor) !== 0x02014b50) break;
    const method = readUint16(buffer, cursor + 10);
    const compressedSize = readUint32(buffer, cursor + 20);
    const nameLength = readUint16(buffer, cursor + 28);
    const extraLength = readUint16(buffer, cursor + 30);
    const commentLength = readUint16(buffer, cursor + 32);
    const localOffset = readUint32(buffer, cursor + 42);
    const name = new TextDecoder().decode(buffer.slice(cursor + 46, cursor + 46 + nameLength));
    if (officeEntryOfInterest(name)) {
      const localNameLength = readUint16(buffer, localOffset + 26);
      const localExtraLength = readUint16(buffer, localOffset + 28);
      const dataStart = localOffset + 30 + localNameLength + localExtraLength;
      const raw = buffer.slice(dataStart, dataStart + compressedSize);
      let content: Uint8Array;
      if (method === 8) {
        content = await inflateRaw(raw);
      } else if (method === 0) {
        content = raw;
      } else {
        cursor += 46 + nameLength + extraLength + commentLength;
        continue;
      }
      const xml = new TextDecoder().decode(content);
      const text = textFromOfficeEntry(name, xml);
      if (text.trim()) parts.push(text);
    }
    cursor += 46 + nameLength + extraLength + commentLength;
  }
  const result = parts.join("\n\n");
  if (!result.trim()) throw new Error("未能提取到文字内容（文件可能已加密或是扫描件）");
  return result;
};
const readAppearance = (): AppearanceSettings => {
  try {
    const stored = JSON.parse(localStorage.getItem(APPEARANCE_KEY) || "null") as Partial<AppearanceSettings> | null;
    if (!stored || typeof stored !== "object") return DEFAULT_APPEARANCE;
    return {
      theme: ["system", "light", "dark", "contrast"].includes(String(stored.theme)) ? stored.theme as AppearanceSettings["theme"] : DEFAULT_APPEARANCE.theme,
      accent: ["green", "blue", "plum", "terracotta"].includes(String(stored.accent)) ? stored.accent as AppearanceSettings["accent"] : DEFAULT_APPEARANCE.accent,
      fontSize: ["standard", "large"].includes(String(stored.fontSize)) ? stored.fontSize as AppearanceSettings["fontSize"] : DEFAULT_APPEARANCE.fontSize,
      density: ["compact", "standard", "comfortable"].includes(String(stored.density)) ? stored.density as AppearanceSettings["density"] : DEFAULT_APPEARANCE.density,
    };
  } catch {
    return DEFAULT_APPEARANCE;
  }
};
const deviceLabel = () => {
  const ua = navigator.userAgent;
  const os = /Windows/.test(ua) ? "Windows" : /Mac OS X/.test(ua) ? "macOS" : /iPhone|iPad/.test(ua) ? "iOS" : /Android/.test(ua) ? "Android" : "其他系统";
  const browser = /Edg\//.test(ua) ? "Edge" : /Chrome\//.test(ua) ? "Chrome" : /Firefox\//.test(ua) ? "Firefox" : /Safari\//.test(ua) ? "Safari" : "浏览器";
  return `${os} · ${browser}`;
};

/* ---------- 端到端加密 ---------- */
const E2E_KEY = "workassistant_e2e_v1";
const E2E_VERSION = "e2e1";
const E2E_ITERATIONS = 310000;
type E2EWrap = { salt: string; iv: string; value: string };
type E2EEnvelope = {
  v: typeof E2E_VERSION;
  pw: E2EWrap;
  rc: E2EWrap;
  data: { iv: string; value: string };
};
type E2ESettings = {
  user_id: string;
  enabled: boolean;
  masterKey: string;
  header: { pw: E2EWrap; rc: E2EWrap };
};
const toBase64 = (bytes: Uint8Array) => {
  let binary = "";
  bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
  return btoa(binary);
};
const fromBase64 = (value: string) => {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes;
};
const toArrayBuffer = (bytes: Uint8Array): Uint8Array<ArrayBuffer> => {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy;
};
const randomBytes = (length: number) => {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return bytes;
};
const deriveE2EKey = async (secret: string, salt: Uint8Array) => {
  const material = await crypto.subtle.importKey("raw", toArrayBuffer(new TextEncoder().encode(secret)), "PBKDF2", false, ["deriveKey"]);
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", salt: toArrayBuffer(salt), iterations: E2E_ITERATIONS, hash: "SHA-256" },
    material,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
};
const aesEncrypt = async (key: CryptoKey, data: Uint8Array) => {
  const iv = randomBytes(12);
  const encrypted = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, toArrayBuffer(data));
  return { iv: toBase64(iv), value: toBase64(new Uint8Array(encrypted)) };
};
const aesDecrypt = async (key: CryptoKey, wrap: { iv: string; value: string }) => {
  const decrypted = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: toArrayBuffer(fromBase64(wrap.iv)) },
    key,
    toArrayBuffer(fromBase64(wrap.value)),
  );
  return new Uint8Array(decrypted);
};
export const generateRecoveryCode = () => {
  const bytes = randomBytes(20);
  const hex = Array.from(bytes).map((byte) => byte.toString(16).padStart(2, "0")).join("").toUpperCase();
  return hex.match(/.{1,4}/g)!.join("-");
};
export const normalizeRecoveryCode = (code: string) => code.replace(/[^0-9A-Za-z]/g, "").toUpperCase();
const isE2EEnvelope = (value: unknown): value is E2EEnvelope =>
  typeof value === "object" && value !== null && (value as { v?: unknown }).v === E2E_VERSION;
export const encryptPayload = async (payload: unknown, password: string, recoveryCode: string): Promise<E2EEnvelope> => {
  const masterKey = await crypto.subtle.importKey("raw", randomBytes(32), "AES-GCM", true, ["encrypt", "decrypt"]);
  const rawMaster = new Uint8Array(await crypto.subtle.exportKey("raw", masterKey));
  const pwSalt = randomBytes(16);
  const pwKey = await deriveE2EKey(password, pwSalt);
  const pwWrap = await aesEncrypt(pwKey, rawMaster);
  const rcSalt = randomBytes(16);
  const rcKey = await deriveE2EKey(normalizeRecoveryCode(recoveryCode), rcSalt);
  const rcWrap = await aesEncrypt(rcKey, rawMaster);
  const data = await aesEncrypt(masterKey, new TextEncoder().encode(JSON.stringify(payload)));
  return {
    v: E2E_VERSION,
    pw: { salt: toBase64(pwSalt), ...pwWrap },
    rc: { salt: toBase64(rcSalt), ...rcWrap },
    data,
  };
};
export const encryptPayloadWithKey = async (payload: unknown, masterKey: CryptoKey, header: E2ESettings["header"]): Promise<E2EEnvelope> => {
  const data = await aesEncrypt(masterKey, new TextEncoder().encode(JSON.stringify(payload)));
  return { v: E2E_VERSION, pw: header.pw, rc: header.rc, data };
};
export const unwrapMasterKey = async (envelope: E2EEnvelope, password?: string, recoveryCode?: string): Promise<CryptoKey | null> => {
  if (password) {
    const pwKey = await deriveE2EKey(password, fromBase64(envelope.pw.salt));
    try {
      return await crypto.subtle.importKey("raw", await aesDecrypt(pwKey, envelope.pw), "AES-GCM", true, ["encrypt", "decrypt"]);
    } catch {
      // 密码不对，继续尝试恢复码
    }
  }
  if (recoveryCode) {
    const rcKey = await deriveE2EKey(normalizeRecoveryCode(recoveryCode), fromBase64(envelope.rc.salt));
    return crypto.subtle.importKey("raw", await aesDecrypt(rcKey, envelope.rc), "AES-GCM", true, ["encrypt", "decrypt"]);
  }
  return null;
};
export const decryptEnvelope = async (envelope: E2EEnvelope, masterKey: CryptoKey) => {
  const payload = await aesDecrypt(masterKey, envelope.data);
  return JSON.parse(new TextDecoder().decode(payload)) as CloudPayload;
};
export const encryptBackup = async (payload: unknown, password: string) => {
  const salt = randomBytes(16);
  const key = await deriveE2EKey(password, salt);
  const data = await aesEncrypt(key, new TextEncoder().encode(JSON.stringify(payload)));
  return { v: "backup1", salt: toBase64(salt), ...data };
};
export const decryptBackup = async (backup: { salt: string; iv: string; value: string }, password: string) => {
  const key = await deriveE2EKey(password, fromBase64(backup.salt));
  const payload = await aesDecrypt(key, { iv: backup.iv, value: backup.value });
  return JSON.parse(new TextDecoder().decode(payload)) as unknown;
};
const readE2ESettings = (userId?: string): E2ESettings | null => {
  try {
    const stored = JSON.parse(localStorage.getItem(E2E_KEY) || "null") as E2ESettings | null;
    if (stored && userId && stored.user_id === userId && stored.enabled && stored.masterKey && stored.header?.pw && stored.header?.rc) return stored;
    return null;
  } catch {
    return null;
  }
};
const masterKeyFromSettings = async (settings: E2ESettings) =>
  crypto.subtle.importKey("raw", fromBase64(settings.masterKey), "AES-GCM", false, ["encrypt", "decrypt"]);
const applyAppearance = (settings: AppearanceSettings) => {
  const root = document.documentElement;
  root.setAttribute("data-theme", settings.theme);
  root.setAttribute("data-accent", settings.accent);
  root.setAttribute("data-font-size", settings.fontSize);
  root.setAttribute("data-density", settings.density);
};
const backupPayload = (data: SyncData, reason?: string) => ({
  v: "5.0",
  d: new Date().toISOString(),
  ...(reason ? { reason } : {}),
  p: data.projects,
  c: data.config,
  ot: data.attendance,
  todo: data.todo,
  cal: data.calendar,
  st: data.settlement,
});
export const prepareBackupImport = (rawBackup: unknown, current: SyncData): PendingImport => {
  if (!rawBackup || typeof rawBackup !== "object") throw new Error("invalid backup");
  const backup = rawBackup as Record<string, unknown>;
  const hasOwn = (key: string) => Object.prototype.hasOwnProperty.call(backup, key);
  const recognizedGroups = [
    ["p", "projects"],
    ["c", "config"],
    ["ot", "attendance"],
    ["todo"],
    ["cal", "calendar"],
  ].filter((keys) => keys.some(hasOwn)).length;
  const hasBackupHeader = typeof backup.v === "string" && typeof backup.d === "string";
  // Current exports carry a version/date header. Older exports are still
  // accepted when they contain several known data sections, but an arbitrary
  // JSON object must never be shown as an empty backup.
  if ((hasBackupHeader && recognizedGroups === 0) || (!hasBackupHeader && recognizedGroups < 3)) {
    throw new Error("unrecognized backup");
  }
  const incomingProjectsRaw = (backup.p || backup.projects || []) as unknown;
  const incomingTodosRaw = (backup.todo || []) as unknown;
  const incomingAttendanceRaw = (backup.ot || backup.attendance || EMPTY_ATTENDANCE) as Partial<Attendance>;
  const incomingConfigRaw = (backup.c || backup.config || {}) as NodeConfig;
  const incomingCalendarRaw = (backup.cal || backup.calendar || {}) as unknown;
  const incomingSettlementRaw = (backup.st || backup.settlement || []) as unknown;
  if (
    !Array.isArray(incomingProjectsRaw) ||
    !Array.isArray(incomingTodosRaw) ||
    !incomingAttendanceRaw ||
    typeof incomingAttendanceRaw !== "object" ||
    Array.isArray(incomingAttendanceRaw) ||
    !incomingConfigRaw ||
    typeof incomingConfigRaw !== "object" ||
    Array.isArray(incomingConfigRaw) ||
    !incomingCalendarRaw ||
    typeof incomingCalendarRaw !== "object" ||
    Array.isArray(incomingCalendarRaw)
  ) throw new Error("invalid backup");
  const incomingProjects = incomingProjectsRaw.map((project) => normalizeProject(project as Project));
  const incomingTodos = incomingTodosRaw as Todo[];
  const incomingAttendance: Attendance = {
    ...clone(EMPTY_ATTENDANCE),
    ...incomingAttendanceRaw,
    records: Array.isArray(incomingAttendanceRaw.records) ? incomingAttendanceRaw.records : [],
    leaveRecords: Array.isArray(incomingAttendanceRaw.leaveRecords) ? incomingAttendanceRaw.leaveRecords : [],
    adjustments: Array.isArray(incomingAttendanceRaw.adjustments) ? incomingAttendanceRaw.adjustments : [],
  };
  const incomingCalendar = normalizeCalendar(incomingCalendarRaw as StoredCalendarEntries);
  const incomingConfig = incomingConfigRaw && typeof incomingConfigRaw === "object" ? incomingConfigRaw : {};

  const projectMap = new Map(current.projects.map((project) => [String(project.id), project]));
  const projectReplacements = incomingProjects.filter((project) => projectMap.has(String(project.id))).length;
  incomingProjects.forEach((project) => projectMap.set(String(project.id), project));

  const todoKeyForImport = (todo: Todo) => `${todo.text}_${todo.createDate || ""}`;
  const todoMap = new Map(current.todo.map((todo) => [todoKeyForImport(todo), todo]));
  const todoDuplicates = incomingTodos.filter((todo) => todoMap.has(todoKeyForImport(todo))).length;
  incomingTodos.forEach((todo) => todoMap.set(todoKeyForImport(todo), todo));

  const overtimeKeyForImport = (record: OvertimeRecord) =>
    record.id || `${record.date}_${record.days}_${record.note}`;
  const overtimeMap = new Map(current.attendance.records.map((record) => [overtimeKeyForImport(record), record]));
  incomingAttendance.records.forEach((record) => overtimeMap.set(overtimeKeyForImport(record), record));
  const leaveKeyForImport = (record: LeaveRecord) =>
    record.id || `${record.date}_${record.type}_${record.days}_${record.note}`;
  const leaveMap = new Map(current.attendance.leaveRecords.map((record) => [leaveKeyForImport(record), record]));
  incomingAttendance.leaveRecords.forEach((record) => leaveMap.set(leaveKeyForImport(record), record));
  const adjustmentMap = new Map((current.attendance.adjustments || []).map((record) => [record.id, record]));
  (incomingAttendance.adjustments || []).forEach((record) => adjustmentMap.set(record.id, record));

  return {
    fileName: "",
    merged: {
      projects: [...projectMap.values()],
      todo: [...todoMap.values()],
      attendance: {
        annual: current.attendance.annual !== "" ? current.attendance.annual : incomingAttendance.annual,
        saved: current.attendance.saved !== "" ? current.attendance.saved : incomingAttendance.saved,
        records: [...overtimeMap.values()],
        leaveRecords: [...leaveMap.values()],
        adjustments: [...adjustmentMap.values()],
      },
      calendar: mergeCalendarEntries(incomingCalendar, current.calendar),
      config: { ...clone(DEFAULT_CONFIG), ...incomingConfig, ...current.config },
      settlement: [
        ...(Array.isArray(incomingSettlementRaw) ? incomingSettlementRaw as SettlementRecord[] : []),
        ...(current.settlement || []),
      ],
    },
    summary: {
      projects: incomingProjects.length,
      projectReplacements,
      todos: incomingTodos.length,
      todoDuplicates,
      overtime: incomingAttendance.records.length,
      leave: incomingAttendance.leaveRecords.length,
      workRecords: Object.values(incomingCalendar).reduce((sum, records) => sum + records.length, 0),
      settlement: (Array.isArray(incomingSettlementRaw) ? incomingSettlementRaw.length : 0),
    },
  };
};
const typeLabel = PROJECT_TYPE_LABEL;
const projectTypeLabel = (project: Project) =>
  project.projectClass === "service"
    ? project.type === "special" ? "专项资金服务" : "内部资金服务"
    : typeLabel[project.type];
const PAYMENT_KIND_LABEL: Record<PaymentRecord["kind"], string> = {
  oneTime: "一次性付款",
  first: "首付款",
  progress: "进度款",
  final: "尾款",
  warranty: "质保金",
  other: "其他",
};
const PROCUREMENT_BASIS_LABEL: Record<ProcurementBasisKind, string> = {
  meeting: "会议纪要",
  approval: "请示签批",
};
const PROCUREMENT_FORM_LABEL: Record<ProcurementForm, string> = {
  compare: "比选",
  consultation: "竞争性磋商",
  openTender: "公开招标",
  direct: "直接委托",
};
const PROCUREMENT_RESULT_LABEL: Record<ProcurementResult, string> = {
  normal: "正常",
  rejected: "流标",
  voided: "废标",
};
const PAYMENT_CARD_LAYOUT_VERSION = "funding-visible-v2";

function moveListItem<T>(items: T[], from: number, offset: number) {
  const to = from + offset;
  if (to < 0 || to >= items.length) return items;
  const next = [...items];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

export function swapProjectOrder(
  items: Project[],
  projectId: string | number,
  targetId: string | number | undefined,
) {
  if (targetId === undefined) return items;
  const projectIndex = items.findIndex((item) => String(item.id) === String(projectId));
  const targetIndex = items.findIndex((item) => String(item.id) === String(targetId));
  if (projectIndex < 0 || targetIndex < 0 || projectIndex === targetIndex) return items;
  const next = [...items];
  [next[projectIndex], next[targetIndex]] = [next[targetIndex], next[projectIndex]];
  return next;
}

function plannedPaymentAmount(contract: ContractRecord, plan: ContractPaymentPlan) {
  if (plan.amount !== undefined && plan.amount !== "") return safeNumber(plan.amount);
  return Math.round((safeNumber(contract.amount) * safeNumber(plan.ratio) / 100 + Number.EPSILON) * 100) / 100;
}

function planRatioFromAmount(amount: string | number, contractAmount: string | number) {
  const total = safeNumber(contractAmount);
  if (total <= 0) return "";
  const ratio = Math.round((safeNumber(amount) / total * 100 + Number.EPSILON) * 10000) / 10000;
  return String(ratio);
}

function updatePlanRatios(form: HTMLFormElement, count: number) {
  const contractAmount = (form.elements.namedItem("amount") as HTMLInputElement | null)?.value || "";
  const enteredAmounts = Array.from({ length: count }, (_, index) =>
    (form.elements.namedItem(`planAmount_${index}`) as HTMLInputElement | null)?.value || "");
  const completedAmounts = completeContractPlanAmounts(contractAmount, enteredAmounts, count);
  completedAmounts.forEach((amount, index) => {
    const amountInput = form.elements.namedItem(`planAmount_${index}`) as HTMLInputElement | null;
    if (amountInput && (count === 1 || index === count - 1)) amountInput.value = String(amount);
  });
  for (let index = 0; index < count; index += 1) {
    const amount = (form.elements.namedItem(`planAmount_${index}`) as HTMLInputElement | null)?.value || "";
    const ratio = form.elements.namedItem(`ratio_${index}`) as HTMLInputElement | null;
    if (ratio) ratio.value = planRatioFromAmount(amount, contractAmount);
  }
}

function evaluateBasicCalculation(source: string) {
  const expression = source.replace(/[×x]/g, "*").replace(/÷/g, "/").trim();
  if (!expression || !/^[0-9+\-*/().\s]+$/.test(expression)) return null;
  try {
    const result = Function(`"use strict"; return (${expression})`)();
    return typeof result === "number" && Number.isFinite(result) ? result : null;
  } catch {
    return null;
  }
}

function paymentArchiveStatus(payment: PaymentRecord) {
  return payment.archiveReceiptStatus || (payment.archiveReceiptConfirmed ? "complete" : "pending");
}

function paymentPerformanceStatus(payment: PaymentRecord) {
  return payment.performanceConfirmationStatus || (payment.performanceConfirmationConfirmed ? "complete" : "pending");
}

function splitMinutesRef(value?: string) {
  const match = /^(.*?)〔(\d{4})〕(\d{1,2})号$/.exec(value || "");
  if (match) return { prefix: `${match[1]}〔${match[2]}〕`, number: match[3] };
  return { prefix: value || "", number: "" };
}

function isMaterialPayment(payment: PaymentRecord) {
  return payment.kind === "final" || payment.kind === "oneTime";
}

function settlementFinalPaymentAmount(contract: ContractRecord, editingPaymentId?: string) {
  const settlement = safeNumber(contract.settlementAuditAmount);
  if (!settlement) return 0;
  const paidBeforeFinal = (contract.payments || [])
    .filter((payment) => payment.id !== editingPaymentId && payment.kind !== "final")
    .reduce((sum, payment) => sum + safeNumber(payment.amount), 0);
  return Math.max(0, Math.round((settlement - paidBeforeFinal + Number.EPSILON) * 100) / 100);
}

function paymentForPlan(contract: ContractRecord, plan: ContractPaymentPlan, planIndex: number) {
  return (contract.payments || []).find((payment, paymentIndex) => payment.scheduleId === plan.id || (!payment.scheduleId && paymentIndex === planIndex));
}

function automaticPaymentScheduleId(
  contract: ContractRecord | null,
  kind: PaymentRecord["kind"],
  editingPaymentId?: string,
) {
  const plans = contract?.paymentPlan || [];
  if (!plans.length) return "";
  const suggestedIndex = kind === "oneTime" && plans.length === 1
    ? 0
    : kind === "first"
      ? 0
      : kind === "progress" && plans.length === 3
        ? 1
        : kind === "final"
          ? plans.length - 1
          : -1;
  const plan = plans[suggestedIndex];
  if (!plan) return "";
  const linkedPayment = paymentForPlan(contract as ContractRecord, plan, suggestedIndex);
  return linkedPayment && linkedPayment.id !== editingPaymentId ? "" : plan.id;
}

function automaticPaymentAmount(
  contract: ContractRecord | null,
  kind: PaymentRecord["kind"],
  editingPaymentId?: string,
  procurementKind?: ProcurementPackage["kind"],
) {
  if (!contract) return "";
  if (kind === "final" && procurementKind === "construction" && safeNumber(contract.settlementAuditAmount) > 0) {
    return String(settlementFinalPaymentAmount(contract, editingPaymentId));
  }
  const scheduleId = automaticPaymentScheduleId(contract, kind, editingPaymentId);
  const plan = (contract.paymentPlan || []).find((item) => item.id === scheduleId);
  return plan ? String(plannedPaymentAmount(contract, plan)) : "";
}

function paymentScheduleLabel(
  contract: ContractRecord,
  plan: ContractPaymentPlan,
  index: number,
  kind: PaymentRecord["kind"],
) {
  if (kind === "oneTime" && contract.paymentPlan?.length === 1 && index === 0) {
    return "一次性支付 · 100%";
  }
  return `第 ${index + 1} 笔 · ${plan.ratio}%`;
}

function automaticFundingPlanId(project: Project | null, paymentDate: string) {
  const year = paymentDate.slice(0, 4);
  const matchingPlans = (project?.funds || []).flatMap((fund) =>
    (fund.annualPlans || []).filter((plan) => plan.year === year),
  );
  return matchingPlans.length === 1 ? matchingPlans[0].id : undefined;
}

function automaticPaymentFundingPlanId(
  project: Project | null,
  contract: ContractRecord | null,
  scheduleId: string,
  paymentDate: string,
) {
  const scheduledFundingPlanId = (contract?.paymentPlan || []).find((plan) => plan.id === scheduleId)?.fundingPlanId;
  return scheduledFundingPlanId || automaticFundingPlanId(project, paymentDate) || "";
}
const STAGE_STATUS_LABEL: Record<StageStatus, string> = {
  waiting: "待开始",
  processing: "进行中",
  done: "已完成",
  historical: "历史未补录",
  skipped: "不适用",
};
const GUEST_OWNER = "guest";
export const ownerStorageKeys = (owner: string) => {
  const scoped = (key: string) => owner === GUEST_OWNER
    ? `${key}:visitor:v1`
    : `${key}:account:${owner}`;
  return {
    projects: scoped(KEYS.projects),
    config: scoped(KEYS.config),
    attendance: scoped(KEYS.attendance),
    todo: scoped(KEYS.todo),
    calendar: scoped(KEYS.calendar),
    settlement: scoped(KEYS.settlementRecords),
    syncMeta: scoped(KEYS.syncMeta),
    lastSync: scoped("office_last_sync_v1"),
    preImportBackup: scoped(KEYS.preImportBackup),
    preSyncBackup: scoped("office_pre_sync_backup_v1"),
    trash: scoped("office_trash_v1"),
  };
};
export const canApplySyncResult = (
  activeOwner: string,
  activeRun: number,
  requestOwner: string,
  requestRun: number,
) => activeOwner === requestOwner && activeRun === requestRun;
const SYNC_SECTIONS: SyncSection[] = ["projects", "config", "attendance", "todo", "calendar", "settlement"];

export type TrashType =
  | "workRecord"
  | "todo"
  | "matter"
  | "attendanceAdjustment"
  | "overtime"
  | "leave"
  | "event"
  | "stage"
  | "fund"
  | "fundingPlan"
  | "procurement"
  | "contract"
  | "payment"
  | "service";
export type TrashEntry = {
  id: string;
  type: TrashType;
  label: string;
  deletedAt: string;
  parentId?: string;
  parentLabel?: string;
  extra?: Record<string, unknown>;
  payload: unknown;
};
export const TRASH_TYPE_LABEL: Record<TrashType, string> = {
  workRecord: "工作记录",
  todo: "待办",
  matter: "事项",
  attendanceAdjustment: "额度变更",
  overtime: "加班记录",
  leave: "休假记录",
  event: "事件记录",
  stage: "阶段",
  fund: "资金来源",
  fundingPlan: "年度资金计划",
  procurement: "采购包",
  contract: "合同",
  payment: "付款记录",
  service: "参建单位",
};
const TRASH_LIMIT = 50;
const readTrash = (owner: string): TrashEntry[] => {
  try {
    const raw = localStorage.getItem(ownerStorageKeys(owner).trash);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? (parsed as TrashEntry[]) : [];
  } catch {
    return [];
  }
};
const writeTrash = (owner: string, entries: TrashEntry[]): boolean => {
  try {
    localStorage.setItem(ownerStorageKeys(owner).trash, JSON.stringify(entries.slice(0, TRASH_LIMIT)));
    return true;
  } catch {
    return false;
  }
};
const emptySectionTimes = (): Record<SyncSection, number> => ({
  projects: 0,
  config: 0,
  attendance: 0,
  todo: 0,
  calendar: 0,
  settlement: 0,
});
const emptyRecordVersions = (): Record<SyncCollection, Record<string, ItemVersion>> => ({
  projects: {},
  projectEvents: {},
  projectParts: {},
  todo: {},
  overtime: {},
  leave: {},
  attendance: {},
  calendar: {},
  config: {},
  settlement: {},
});
const collectionSection: Record<SyncCollection, SyncSection> = {
  projects: "projects",
  projectEvents: "projects",
  projectParts: "projects",
  todo: "todo",
  overtime: "attendance",
  leave: "attendance",
  attendance: "attendance",
  calendar: "calendar",
  config: "config",
  settlement: "settlement",
};
const projectKey = (project: Project) => String(project.id);
const todoKey = (todo: Todo) => String(todo.id || `legacy:${todo.text}:${todo.createDate || ""}`);
const overtimeKey = (record: OvertimeRecord) => String(record.id || `legacy:${record.date}:${record.days}:${record.note}`);
const leaveKey = (record: LeaveRecord) => String(record.id || `legacy:${record.date}:${record.type}:${record.days}:${record.note}`);

/** 新增加班须在下一个自然月内休完，返回截止日期（月末）。 */
export const overtimeDeadline = (date: string) => {
  const [year, month] = date.slice(0, 7).split("-").map((part) => Number(part));
  if (!year || !month) return "";
  return localDateKey(new Date(year, month + 1, 0));
};

/** 多关键词搜索：空格分隔，所有关键词必须同时出现在文本中。 */
const COMPACT_HAN_SEPARATOR = /([\p{Script=Han}])([\s·・‧•∙\-_/|]+)(?=[\p{Script=Han}])/gu;
const compactHanText = (text: string) => text.replace(COMPACT_HAN_SEPARATOR, "$1");
export const matchesAllTerms = (haystack: string, query: string) => {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) return true;
  const text = haystack.toLowerCase();
  const compact = compactHanText(text);
  return terms.every((term) => {
    const lowered = term.toLowerCase();
    if (text.includes(lowered)) return true;
    return compact.includes(compactHanText(lowered));
  });
};

const splitSearchTerms = (query: string) => query.trim().split(/\s+/).filter(Boolean);
const splitSentences = (text: string): string[] =>
  text.match(/[^。！？!?；;\n]+[。！？!?；;\n]?/g) || (text ? [text] : []);
const buildSearchSnippet = (text: string, query: string) => {
  if (!text) return { snippet: "", hitSentence: "" };
  const terms = splitSearchTerms(query);
  const sentences = splitSentences(text);
  const hitIndex = sentences.findIndex((sentence) =>
    terms.some((term) => sentence.toLowerCase().includes(term.toLowerCase())));
  if (hitIndex < 0) {
    const fallback = text.length > 80 ? `${text.slice(0, 80)}…` : text;
    return { snippet: fallback, hitSentence: "" };
  }
  const start = Math.max(0, hitIndex - 1);
  const end = Math.min(sentences.length, hitIndex + 2);
  let snippet = sentences.slice(start, end).join("");
  if (start > 0) snippet = `…${snippet}`;
  if (end < sentences.length) snippet = `${snippet}…`;
  return { snippet, hitSentence: sentences[hitIndex].trim() };
};

export const reconcileTodoProjectEvent = (
  projects: Project[],
  todo: Todo,
  eventId: string,
  eventDate?: string,
): Project[] => {
  const todoId = String(todo.id || "");
  const linkedProjectId = String(todo.linkedProjectId || "");
  if (!todoId) return projects;

  return projects.map((project) => {
    const currentEvents = project.events || [];
    const existingIndex = currentEvents.findIndex((event) =>
      event.sourceTodoId === todoId || Boolean(todo.linkedEventId && event.id === todo.linkedEventId));
    const existing = existingIndex >= 0 ? currentEvents[existingIndex] : undefined;
    const remaining = currentEvents.filter((_, index) => index !== existingIndex);
    const isTargetProject = linkedProjectId && String(project.id) === linkedProjectId;
    const hasCategoryPreference = Object.prototype.hasOwnProperty.call(todo, "eventCategory");
    const hasStagePreference = Object.prototype.hasOwnProperty.call(todo, "linkedStageId");

    if (!isTargetProject || !eventId) {
      return existing ? { ...project, events: remaining } : project;
    }

    const occurredOn = todo.scheduledDate?.slice(0, 10)
      || existing?.occurredOn
      || (todo.done ? eventDate || todo.completedOn : undefined)
      || todo.createDate?.slice(0, 10)
      || localDateKey();
    const linkedEvent: ProjectEvent = {
      ...existing,
      id: eventId,
      occurredOn,
      recordedAt: existing?.recordedAt || todo.createDate || new Date().toISOString(),
      title: todo.text,
      status: todo.done ? "done" : "todo",
      category: hasCategoryPreference ? todo.eventCategory || "other" : existing?.category || "other",
      dueDate: todo.scheduledDate || undefined,
      completedAt: todo.done ? existing?.completedAt || eventDate || new Date().toISOString() : undefined,
      sourceStageId: hasStagePreference ? todo.linkedStageId || undefined : existing?.sourceStageId,
      sourceTodoId: todoId,
    };
    const insertAt = existingIndex >= 0 ? Math.min(existingIndex, remaining.length) : remaining.length;
    const events = [...remaining];
    events.splice(insertAt, 0, linkedEvent);
    return { ...project, events };
  });
};

const readOwnerSnapshot = (owner: string, deviceId: string) => {
  const keys = ownerStorageKeys(owner);
  const rawProjects = safeRead<Project[]>(keys.projects, []);
  const projects = rawProjects.map((project) =>
    normalizeProject({
      ...project,
      stages: Array.isArray(project.stages)
        ? project.stages.map((stage) => ({
            ...stage,
            config: Array.isArray(stage.config)
              ? stage.config
              : clone((DEFAULT_CONFIG[stage.name] || []) as ConfigField[]),
            data: stage.data || {},
          }))
        : [],
    }),
  );
  const storedConfig = safeRead<NodeConfig>(keys.config, {});
  const config = { ...clone(DEFAULT_CONFIG), ...storedConfig };
  const attendance = {
    ...clone(EMPTY_ATTENDANCE),
    ...safeRead<Attendance>(keys.attendance, EMPTY_ATTENDANCE),
  };
  const todo = safeRead<Todo[]>(keys.todo, [])
    .filter((item) => !item.ownerId || item.ownerId === owner)
    .map((item) =>
      !item.kind && item.text.includes("西洋楼预警")
        ? { ...item, kind: "service" as const, stage: item.stage || "正在完善方案" }
        : item,
    );
  const calendar = calendarForOwner(
    normalizeCalendar(safeRead<StoredCalendarEntries>(keys.calendar, {})),
    owner,
  );
  const settlement = safeRead<SettlementRecord[]>(keys.settlement, []);
  const savedMeta = safeRead<StoredSyncMeta | null>(keys.syncMeta, null);
  const bootstrapTime = owner === GUEST_OWNER ? Date.now() : 0;
  const hasAttendance =
    safeNumber(attendance.annual) !== 0 ||
    safeNumber(attendance.saved) !== 0 ||
    attendance.records.length > 0 ||
    attendance.leaveRecords.length > 0 ||
    (attendance.adjustments || []).length > 0;
  const bootstrapSections = savedMeta?.sections || {
    projects: projects.length ? bootstrapTime : 0,
    config: Object.keys(storedConfig).length ? bootstrapTime : 0,
    attendance: hasAttendance ? bootstrapTime : 0,
    todo: todo.length ? bootstrapTime : 0,
    calendar: Object.keys(calendar).length ? bootstrapTime : 0,
    settlement: settlement.length ? bootstrapTime : 0,
  };
  const data: SyncData = { projects, config, attendance, todo, calendar, settlement };
  const meta = normalizeSyncMeta(
    { ...savedMeta, version: savedMeta?.version ?? 5, sections: bootstrapSections },
    data,
    deviceId,
  );
  return {
    data,
    meta,
    keys,
    snapshots: {
      projects: JSON.stringify(projects),
      config: JSON.stringify(config),
      attendance: JSON.stringify(attendance),
      todo: JSON.stringify(todo),
      calendar: JSON.stringify(calendar),
      settlement: JSON.stringify(settlement),
    } satisfies Record<SyncSection, string>,
  };
};

const syncEntries = (data: SyncData): Record<SyncCollection, Record<string, unknown>> => {
  const projectEntries: Record<string, unknown> = { __order: data.projects.map(projectKey) };
  const projectEventEntries: Record<string, unknown> = {};
  const projectPartEntries: Record<string, unknown> = {};
  data.projects.forEach((project) => {
    const projectId = projectKey(project);
    const {
      events: projectEvents,
      stages,
      services,
      funds,
      procurements,
      fundingWorksheet,
      ...projectRecord
    } = project;
    projectEntries[projectId] = projectRecord;
    (projectEvents || []).forEach((event) => {
      projectEventEntries[`${projectId}:${event.id}`] = {
        projectId,
        event,
      };
    });
    if (fundingWorksheet) {
      projectPartEntries[`${projectId}:worksheet:funding`] = {
        projectId,
        kind: "worksheet",
        order: 0,
        value: fundingWorksheet,
      } satisfies ProjectPartEntry;
    }
    (stages || []).forEach((stage, index) => {
      const id = stage.id || `legacy-stage-${projectId}-${index}`;
      projectPartEntries[`${projectId}:stage:${id}`] = {
        projectId,
        kind: "stage",
        order: index,
        value: { ...stage, id },
      } satisfies ProjectPartEntry;
    });
    (services || []).forEach((service, index) => {
      projectPartEntries[`${projectId}:service:${service.id}`] = {
        projectId,
        kind: "service",
        order: index,
        value: service,
      } satisfies ProjectPartEntry;
    });
    (funds || []).forEach((fund, index) => {
      const { annualPlans, ...fundRecord } = fund;
      projectPartEntries[`${projectId}:fund:${fund.id}`] = {
        projectId,
        kind: "fund",
        order: index,
        value: fundRecord,
      } satisfies ProjectPartEntry;
      (annualPlans || []).forEach((plan, planIndex) => {
        projectPartEntries[`${projectId}:fundingPlan:${fund.id}:${plan.id}`] = {
          projectId,
          kind: "fundingPlan",
          parentId: fund.id,
          order: planIndex,
          value: plan,
        } satisfies ProjectPartEntry;
      });
    });
    (procurements || []).forEach((procurement, index) => {
      const { contracts, ...procurementRecord } = procurement;
      projectPartEntries[`${projectId}:procurement:${procurement.id}`] = {
        projectId,
        kind: "procurement",
        order: index,
        value: procurementRecord,
      } satisfies ProjectPartEntry;
      (contracts || []).forEach((contract, contractIndex) => {
        const { payments, ...contractRecord } = contract;
        projectPartEntries[`${projectId}:contract:${procurement.id}:${contract.id}`] = {
          projectId,
          kind: "contract",
          parentId: procurement.id,
          order: contractIndex,
          value: contractRecord,
        } satisfies ProjectPartEntry;
        (payments || []).forEach((payment, paymentIndex) => {
          projectPartEntries[`${projectId}:payment:${procurement.id}:${contract.id}:${payment.id}`] = {
            projectId,
            kind: "payment",
            parentId: contract.id,
            grandParentId: procurement.id,
            order: paymentIndex,
            value: payment,
          } satisfies ProjectPartEntry;
        });
      });
    });
  });
  const todoEntries: Record<string, unknown> = { __order: data.todo.map(todoKey) };
  data.todo.forEach((todo) => {
    todoEntries[todoKey(todo)] = todo;
  });
  const overtimeEntries: Record<string, unknown> = {};
  data.attendance.records.forEach((record) => {
    overtimeEntries[overtimeKey(record)] = record;
  });
  const leaveEntries: Record<string, unknown> = {};
  data.attendance.leaveRecords.forEach((record) => {
    leaveEntries[leaveKey(record)] = record;
  });
  const calendarEntries: Record<string, unknown> = {};
  Object.entries(data.calendar).forEach(([date, records]) => {
    records.forEach((record, order) => {
      calendarEntries[record.id] = { ...record, date, order } satisfies WorkRecordSyncEntry;
    });
  });
  return {
    projects: projectEntries,
    projectEvents: projectEventEntries,
    projectParts: projectPartEntries,
    todo: todoEntries,
    overtime: overtimeEntries,
    leave: leaveEntries,
    attendance: {
      balances: {
        annual: data.attendance.annual,
        saved: data.attendance.saved,
        adjustments: data.attendance.adjustments || [],
      },
    },
    calendar: calendarEntries,
    config: { ...data.config },
    settlement: Object.fromEntries((data.settlement || []).map((record) => [record.id, record])),
  };
};

export const normalizeSyncMeta = (
  stored: StoredSyncMeta | null | undefined,
  data: SyncData,
  deviceId: string,
): SyncMeta => {
  const sections = { ...emptySectionTimes(), ...(stored?.sections || {}) };
  const records = emptyRecordVersions();
  const entries = syncEntries(data);
  (Object.keys(records) as SyncCollection[]).forEach((collection) => {
    Object.assign(records[collection], stored?.records?.[collection] || {});
    Object.values(records[collection]).forEach((version) => {
      if (!version.device_id) version.device_id = stored?.device_id || deviceId;
    });
    const fallbackTime = sections[collectionSection[collection]] || 0;
    Object.keys(entries[collection]).forEach((key) => {
      if (!records[collection][key]) {
        records[collection][key] = { updated_at: fallbackTime, device_id: stored?.device_id || deviceId };
      } else if (!records[collection][key].device_id) {
        records[collection][key].device_id = stored?.device_id || deviceId;
      }
    });
  });
  return {
    version: stored?.version ?? 0,
    device_id: deviceId,
    sections,
    records,
  };
};

const payloadData = (payload: CloudPayload): SyncData => ({
  projects: clone(payload.projects || []).map(normalizeProject),
  config: { ...clone(DEFAULT_CONFIG), ...(payload.config || {}) },
  attendance: { ...clone(EMPTY_ATTENDANCE), ...(payload.attendance || {}) },
  todo: clone(payload.todo || []),
  calendar: normalizeCalendar(clone(payload.calendar || {})),
  settlement: clone(payload.settlement || []),
});

const dataFromSyncEntries = (
  entries: Record<SyncCollection, Record<string, unknown>>,
): SyncData => {
  const orderedValues = <T,>(collection: "projects" | "todo"): T[] => {
    const collectionEntries = entries[collection];
    const order = Array.isArray(collectionEntries.__order)
      ? (collectionEntries.__order as string[])
      : [];
    const keys = [
      ...order,
      ...Object.keys(collectionEntries).filter((key) => key !== "__order" && !order.includes(key)),
    ];
    return keys
      .filter((key) => Object.prototype.hasOwnProperty.call(collectionEntries, key))
      .map((key) => collectionEntries[key] as T);
  };
  const balances = (entries.attendance.balances || {}) as Partial<Pick<Attendance, "annual" | "saved">> & { adjustments?: AttendanceAdjustment[] };
  const calendar = Object.values(entries.calendar).reduce<CalendarEntries>((days, rawEntry) => {
    const entry = rawEntry as Partial<WorkRecordSyncEntry>;
    const date = String(entry.date || "");
    const content = String(entry.content || "").trim();
    const id = String(entry.id || "");
    if (!date || !content || !id) return days;
    const records = days[date] || [];
    records.push({
      id,
      content,
      ...(entry.ownerId ? { ownerId: String(entry.ownerId) } : {}),
      ...(entry.sourceTodoId ? { sourceTodoId: String(entry.sourceTodoId) } : {}),
      ...(entry.linkedProjectId ? { linkedProjectId: String(entry.linkedProjectId) } : {}),
      ...(entry.occurredAt ? { occurredAt: String(entry.occurredAt) } : {}),
      ...(entry.amount ? { amount: String(entry.amount) } : {}),
      ...(entry.organization ? { organization: String(entry.organization) } : {}),
      ...(entry.contact ? { contact: String(entry.contact) } : {}),
      ...(entry.nextStep ? { nextStep: String(entry.nextStep) } : {}),
      ...(entry.dueDate ? { dueDate: String(entry.dueDate) } : {}),
      ...(Array.isArray(entry.attachments) ? { attachments: entry.attachments } : {}),
      ...(entry.createdAt ? { createdAt: String(entry.createdAt) } : {}),
      order: Number.isFinite(entry.order) ? Number(entry.order) : records.length,
    } as WorkRecord & { order: number });
    days[date] = records;
    return days;
  }, {});
  Object.keys(calendar).forEach((date) => {
    const ordered = (calendar[date] as Array<WorkRecord & { order?: number }>).sort((left, right) =>
      (left.order ?? 0) - (right.order ?? 0) ||
      String(left.createdAt || "").localeCompare(String(right.createdAt || "")) ||
      left.id.localeCompare(right.id));
    calendar[date] = ordered.map(({ order: _order, ...record }) => record);
  });
  const projects = orderedValues<Project>("projects").map((project) => {
    const events = Object.values(entries.projectEvents)
      .filter((entry) => (entry as { projectId?: string }).projectId === String(project.id))
      .map((entry) => (entry as { event: ProjectEvent }).event);
    const parts = Object.values(entries.projectParts)
      .filter((entry) => (entry as ProjectPartEntry).projectId === String(project.id)) as ProjectPartEntry[];
    const orderedParts = <T,>(
      kind: ProjectPartEntry["kind"],
      parentId?: string,
      grandParentId?: string,
    ) => parts
      .filter((entry) =>
        entry.kind === kind &&
        (parentId === undefined || entry.parentId === parentId) &&
        (grandParentId === undefined || entry.grandParentId === grandParentId))
      .sort((a, b) => a.order - b.order)
      .map((entry) => clone(entry.value) as T);
    const stages = orderedParts<Project["stages"][number]>("stage");
    const services = orderedParts<NonNullable<Project["services"]>[number]>("service");
    const funds = orderedParts<FundingSource>("fund").map((fund) => ({
      ...fund,
      annualPlans: orderedParts<FundingYearPlan>("fundingPlan", fund.id),
    }));
    const procurements = orderedParts<ProcurementPackage>("procurement").map((procurement) => ({
      ...procurement,
      contracts: orderedParts<ContractRecord>("contract", procurement.id).map((contract) => ({
        ...contract,
        payments: orderedParts<PaymentRecord>("payment", contract.id, procurement.id),
      })),
    }));
    const fundingWorksheet = orderedParts<FundingWorksheet>("worksheet")[0];
    return normalizeProject({ ...project, stages, services, funds, procurements, events, fundingWorksheet });
  });
  return {
    projects,
    todo: orderedValues<Todo>("todo"),
    attendance: {
      annual: balances.annual ?? 0,
      saved: balances.saved ?? 0,
      adjustments: Array.isArray(balances.adjustments) ? balances.adjustments : [],
      records: Object.values(entries.overtime) as OvertimeRecord[],
      leaveRecords: Object.values(entries.leave) as LeaveRecord[],
    },
    calendar,
    config: { ...clone(DEFAULT_CONFIG), ...(entries.config as NodeConfig) },
    settlement: Object.values(entries.settlement || {}) as SettlementRecord[],
  };
};

export const mergeSyncState = (
  localData: SyncData,
  localMeta: SyncMeta,
  remoteData: SyncData,
  remoteMeta: SyncMeta,
): { data: SyncData; meta: SyncMeta; downloaded: boolean; uploadNeeded: boolean } => {
  const localEntries = syncEntries(localData);
  const remoteEntries = syncEntries(remoteData);
  const mergedEntries = {
    projects: {},
    projectEvents: {},
    projectParts: {},
    todo: {},
    overtime: {},
    leave: {},
    attendance: {},
    calendar: {},
    config: {},
    settlement: {},
  } as Record<SyncCollection, Record<string, unknown>>;
  const mergedMeta: SyncMeta = {
    version: 5,
    device_id: localMeta.device_id,
    sections: { ...emptySectionTimes() },
    records: emptyRecordVersions(),
  };
  let downloaded = false;
  let uploadNeeded = false;

  SYNC_SECTIONS.forEach((section) => {
    mergedMeta.sections[section] = Math.max(localMeta.sections[section] || 0, remoteMeta.sections[section] || 0);
  });

  (Object.keys(mergedEntries) as SyncCollection[]).forEach((collection) => {
    const keys = new Set([
      ...Object.keys(localEntries[collection]),
      ...Object.keys(remoteEntries[collection]),
      ...Object.keys(localMeta.records[collection]),
      ...Object.keys(remoteMeta.records[collection]),
    ]);
    keys.forEach((key) => {
      const localHas = Object.prototype.hasOwnProperty.call(localEntries[collection], key);
      const remoteHas = Object.prototype.hasOwnProperty.call(remoteEntries[collection], key);
      const partEntry = (localHas
        ? localEntries[collection][key]
        : remoteEntries[collection][key]) as ProjectPartEntry | undefined;
      const legacyMissingVersion = (
        meta: SyncMeta,
        hasPart: boolean,
        otherHasPart: boolean,
      ): ItemVersion | undefined => {
        if (
          collection !== "projectParts" ||
          meta.version >= 4 ||
          hasPart ||
          !otherHasPart ||
          !partEntry?.projectId
        ) return undefined;
        const projectVersion = meta.records.projects[partEntry.projectId];
        const updatedAt = projectVersion?.updated_at || meta.sections.projects || 0;
        if (!updatedAt) return undefined;
        return {
          updated_at: updatedAt,
          device_id: projectVersion?.device_id || meta.device_id,
          deleted: true,
        };
      };
      const localVersion =
        localMeta.records[collection][key] ||
        legacyMissingVersion(localMeta, localHas, remoteHas);
      const remoteVersion =
        remoteMeta.records[collection][key] ||
        legacyMissingVersion(remoteMeta, remoteHas, localHas);
      const localKnown = localHas || Boolean(localVersion);
      const remoteKnown = remoteHas || Boolean(remoteVersion);
      const localTime = localVersion?.updated_at || 0;
      const remoteTime = remoteVersion?.updated_at || 0;
      const localDevice = localVersion?.device_id || localMeta.device_id;
      const remoteDevice = remoteVersion?.device_id || remoteMeta.device_id;
      const contentDiffers =
        localHas !== remoteHas ||
        Boolean(localVersion?.deleted) !== Boolean(remoteVersion?.deleted) ||
        JSON.stringify(localEntries[collection][key]) !== JSON.stringify(remoteEntries[collection][key]);
      const remoteWins =
        !localKnown ||
        (remoteKnown &&
          (remoteTime > localTime ||
            (remoteTime === localTime && contentDiffers && remoteDevice > localDevice)));
      const useRemote = remoteKnown && remoteWins;
      const winningVersion = clone(
        (useRemote ? remoteVersion : localVersion) || {
          updated_at: useRemote ? remoteTime : localTime,
          device_id: useRemote ? remoteDevice : localDevice,
        },
      );
      const winningHas = useRemote ? remoteHas : localHas;
      const winningValue = useRemote
        ? remoteEntries[collection][key]
        : localEntries[collection][key];

      if (!winningHas && !winningVersion.deleted) winningVersion.deleted = true;
      mergedMeta.records[collection][key] = winningVersion;
      if (!winningVersion.deleted && winningHas) {
        mergedEntries[collection][key] = clone(winningValue);
      }

      if (useRemote && contentDiffers) downloaded = true;
      if (!useRemote && remoteKnown) {
        const versionDiffers =
          localTime !== remoteTime ||
          Boolean(localVersion?.deleted) !== Boolean(remoteVersion?.deleted);
        if (contentDiffers || versionDiffers) uploadNeeded = true;
      }
      if (!remoteKnown && localKnown) uploadNeeded = true;
    });
  });

  return {
    data: dataFromSyncEntries(mergedEntries),
    meta: mergedMeta,
    downloaded,
    uploadNeeded,
  };
};

function Modal({
  open,
  title,
  children,
  onClose,
  wide = false,
  className = "",
  dismissable = true,
}: {
  open: boolean;
  title: string;
  children: ReactNode;
  onClose: () => void;
  wide?: boolean;
  className?: string;
  dismissable?: boolean;
}) {
  const [keyboardInset, setKeyboardInset] = useState(0);

  useEffect(() => {
    if (!open) return;
    const close = (event: KeyboardEvent) => event.key === "Escape" && dismissable && onClose();
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [open, onClose, dismissable]);
  useEffect(() => {
    if (!open || !window.matchMedia("(max-width: 760px)").matches) {
      setKeyboardInset(0);
      return;
    }

    const viewport = window.visualViewport;
    if (!viewport) return;
    const updateForViewport = () => {
      const inset = Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop);
      setKeyboardInset(Math.round(inset));
      window.requestAnimationFrame(() => {
        const active = document.activeElement;
        if (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement || active instanceof HTMLSelectElement) {
          active.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "auto" });
        }
      });
    };

    updateForViewport();
    viewport.addEventListener("resize", updateForViewport);
    viewport.addEventListener("scroll", updateForViewport);
    document.addEventListener("focusin", updateForViewport);
    return () => {
      viewport.removeEventListener("resize", updateForViewport);
      viewport.removeEventListener("scroll", updateForViewport);
      document.removeEventListener("focusin", updateForViewport);
    };
  }, [open]);

  if (!open) return null;
  return (
    <div className={`modal-backdrop ${keyboardInset ? "keyboard-open" : ""}`} role="presentation" style={keyboardInset ? { paddingBottom: `${keyboardInset + 8}px` } : undefined} onMouseDown={(event) => event.target === event.currentTarget && dismissable && onClose()}>
      <section className={`modal-card ${wide ? "modal-wide" : ""} ${className}`.trim()} role="dialog" aria-modal="true" aria-label={title}>
        <header className="modal-header">
          <h2>{title}</h2>
          {dismissable && <button className="icon-button" type="button" onClick={onClose} aria-label="关闭弹窗">×</button>}
        </header>
        <div className="modal-body">{children}</div>
      </section>
    </div>
  );
}

function EmptyState({ title }: { title: string }) {
  return (
    <div className="empty-state">
      <span className="empty-mark">·</span>
      <strong>{title}</strong>
    </div>
  );
}

function SmartDateInput({
  name,
  value,
  defaultValue = "",
  required = false,
  onChange,
}: {
  name?: string;
  value?: string;
  defaultValue?: string;
  required?: boolean;
  onChange?: (value: string, form: HTMLFormElement | null) => void;
}) {
  const initial = value ?? defaultValue;
  const [year, setYear] = useState(initial.slice(0, 4));
  const [month, setMonth] = useState(initial.slice(5, 7));
  const [day, setDay] = useState(initial.slice(8, 10));
  const wrapRef = useRef<HTMLSpanElement>(null);
  const monthRef = useRef<HTMLInputElement>(null);
  const dayRef = useRef<HTMLInputElement>(null);
  const nativeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (value === undefined) return;
    setYear(value.slice(0, 4));
    setMonth(value.slice(5, 7));
    setDay(value.slice(8, 10));
  }, [value]);

  const publish = (nextYear: string, nextMonth: string, nextDay: string) => {
    const candidate = `${nextYear}-${nextMonth}-${nextDay}`;
    const complete = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/.test(candidate);
    const parsed = complete ? new Date(`${candidate}T12:00:00`) : null;
    const dateValue = parsed && localDateKey(parsed) === candidate ? candidate : "";
    onChange?.(dateValue, wrapRef.current?.closest("form") || null);
  };
  const updatePart = (part: "year" | "month" | "day", raw: string) => {
    const digits = raw.replace(/\D/g, "").slice(0, part === "year" ? 4 : 2);
    const nextYear = part === "year" ? digits : year;
    const nextMonth = part === "month" ? digits : month;
    const nextDay = part === "day" ? digits : day;
    if (part === "year") setYear(digits);
    if (part === "month") setMonth(digits);
    if (part === "day") setDay(digits);
    publish(nextYear, nextMonth, nextDay);
    if (part === "year" && digits.length === 4) monthRef.current?.focus();
    if (part === "month" && digits.length === 2) dayRef.current?.focus();
  };
  const dateCandidate = `${year}-${month}-${day}`;
  const candidateDate = /^\d{4}-\d{2}-\d{2}$/.test(dateCandidate) ? new Date(`${dateCandidate}T12:00:00`) : null;
  const completeValue = candidateDate && localDateKey(candidateDate) === dateCandidate ? dateCandidate : "";

  return <span className="smart-date-input" ref={wrapRef}>
    {name && <input type="hidden" name={name} value={completeValue} />}
    <input aria-label="年" inputMode="numeric" autoComplete="off" value={year} maxLength={4} pattern="\d{4}" required={required} onChange={(event) => updatePart("year", event.target.value)} onFocus={(event) => event.currentTarget.select()} />
    <i>年</i>
    <input ref={monthRef} aria-label="月" inputMode="numeric" autoComplete="off" value={month} maxLength={2} pattern="0[1-9]|1[0-2]" required={required} onChange={(event) => updatePart("month", event.target.value)} onFocus={(event) => event.currentTarget.select()} />
    <i>月</i>
    <input ref={dayRef} aria-label="日" inputMode="numeric" autoComplete="off" value={day} maxLength={2} pattern="0[1-9]|[12]\d|3[01]" required={required} onChange={(event) => updatePart("day", event.target.value)} onFocus={(event) => event.currentTarget.select()} />
    <i>日</i>
    <button type="button" aria-label="打开日期选择器" onClick={() => nativeRef.current?.showPicker?.()}>▦</button>
    <input ref={nativeRef} className="smart-date-native" type="date" tabIndex={-1} value={completeValue} onChange={(event) => {
      const next = event.target.value;
      setYear(next.slice(0, 4));
      setMonth(next.slice(5, 7));
      setDay(next.slice(8, 10));
      onChange?.(next, wrapRef.current?.closest("form") || null);
    }} />
  </span>;
}

function TodoDateRail({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const dates = useMemo(() => {
    const weekdays = ["周日", "周一", "周二", "周三", "周四", "周五", "周六"];
    return Array.from({ length: 92 }, (_, index) => {
      const date = new Date();
      date.setHours(12, 0, 0, 0);
      date.setDate(date.getDate() + index);
      return {
        key: localDateKey(date),
        day: date.getDate(),
        month: date.getMonth() + 1,
        weekday: weekdays[date.getDay()],
      };
    });
  }, []);
  const today = localDateKey();
  return (
    <div className="todo-date-picker">
      <input name="scheduledDate" type="hidden" value={value} />
      <div className="todo-date-rail" role="listbox" aria-label="事件日期">
        <button className={!value ? "selected" : ""} type="button" aria-pressed={!value} onClick={() => onChange("")}><small>不设</small><strong>—</strong><span>日期</span></button>
        {dates.map((date) => (
          <button className={value === date.key ? "selected" : ""} type="button" key={date.key} aria-pressed={value === date.key} onClick={() => onChange(date.key)}>
            <small>{date.key === today ? `今天 · ${date.weekday}` : date.weekday}</small>
            <strong>{date.day}</strong>
            <span>{date.month}月</span>
          </button>
        ))}
      </div>
      <label className="todo-date-more">其他日期<SmartDateInput value={value} onChange={(next) => onChange(next)} /></label>
    </div>
  );
}

const syncedLabel = (timestamp: number) => {
  const date = new Date(timestamp);
  const today = localDateKey(date) === localDateKey();
  return `已同步 ${today
    ? date.toLocaleTimeString("zh-CN", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false })
    : date.toLocaleString("zh-CN", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit", hour12: false })}`;
};

export default function OfficeAssistant() {
  const [hydrated, setHydrated] = useState(false);
  const [page, setPage] = useState<AppPage>("home");
  const [modal, setModal] = useState<ModalName>(null);
  const [toast, setToast] = useState("");
  const [undoState, setUndoState] = useState<UndoState | null>(null);
  const [pendingImport, setPendingImport] = useState<PendingImport | null>(null);
  const [guestPendingSummary, setGuestPendingSummary] = useState<{ projects: number; todos: number; workRecords: number; overtime: number; leave: number } | null>(null);
  const [lastBackupAt, setLastBackupAt] = useState(() => localStorage.getItem("workassistant_last_backup_at") || "");
  const [appearance, setAppearance] = useState<AppearanceSettings>(readAppearance);
  const [securityReport, setSecurityReport] = useState<string[]>([]);
  const [trashTick, setTrashTick] = useState(0);
  const [newEmail, setNewEmail] = useState("");
  const [changeEmailPassword, setChangeEmailPassword] = useState("");
  const [otpEmail, setOtpEmail] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [devices, setDevices] = useState<Array<{ device_id: string; device_name: string; last_seen_at: string; created_at: string; revoked_at?: string | null }>>([]);
  const [e2eSettings, setE2ESettings] = useState<E2ESettings | null>(null);
  const [recoveryCodeShown, setRecoveryCodeShown] = useState("");
  const [pendingRecovery, setPendingRecovery] = useState(false);
  const [e2ePassword, setE2ePassword] = useState("");
  const [recoveryInput, setRecoveryInput] = useState("");
  const [recoveryPassword, setRecoveryPassword] = useState("");
  const [showRecoveryFields, setShowRecoveryFields] = useState(false);
  const [encryptBackupEnabled, setEncryptBackupEnabled] = useState(false);
  const [backupPassword, setBackupPassword] = useState("");
  const [pendingEncryptedBackup, setPendingEncryptedBackup] = useState<{ raw: { salt: string; iv: string; value: string }; fileName: string } | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [todos, setTodos] = useState<Todo[]>([]);
  const [attendance, setAttendance] = useState<Attendance>(EMPTY_ATTENDANCE);
  const [calendar, setCalendar] = useState<CalendarEntries>({});
  const [nodeConfig, setNodeConfig] = useState<NodeConfig>(() => clone(DEFAULT_CONFIG));
  const [currentProjectId, setCurrentProjectId] = useState<string | number | null>(null);
  const [projectBrowserMode, setProjectBrowserMode] = useState<"root" | "special" | "internal" | "serviceSpecial" | "serviceInternal">("root");
  const [projectTab, setProjectTab] = useState<"overview" | "timeline" | "workflow" | "funds" | "services" | "sheet">("overview");
  const [stageEventFilterId, setStageEventFilterId] = useState<string>("");
  const [editingStageIndex, setEditingStageIndex] = useState<number | null>(null);
  const [editingProcurementId, setEditingProcurementId] = useState<string>("");
  const [editingContractId, setEditingContractId] = useState<string>("");
  const [editingFundingSourceId, setEditingFundingSourceId] = useState<string>("");
  const [editingFundingPlanId, setEditingFundingPlanId] = useState<string>("");
  const [fundingPaymentPreviewPlanId, setFundingPaymentPreviewPlanId] = useState<string>("");
  const [fundingUnpaidPreviewPlanId, setFundingUnpaidPreviewPlanId] = useState<string>("");
  const [fundingClawbackPreviewPlanId, setFundingClawbackPreviewPlanId] = useState<string>("");
  const [editingProjectEventId, setEditingProjectEventId] = useState<string>("");
  const [editingServiceId, setEditingServiceId] = useState<string>("");
  const [editingPaymentId, setEditingPaymentId] = useState<string>("");
  const [paymentKind, setPaymentKind] = useState<PaymentRecord["kind"]>("progress");
  const [editingOvertimeId, setEditingOvertimeId] = useState<string>("");
  const [editingTodoId, setEditingTodoId] = useState<string>("");
  const [todoFormProjectId, setTodoFormProjectId] = useState<string>("");
  const [todoFormStageId, setTodoFormStageId] = useState<string>("");
  const [todoFormDate, setTodoFormDate] = useState<string>("");
  const [editingMatterId, setEditingMatterId] = useState<string>("");
  const [pendingTodoCompletionId, setPendingTodoCompletionId] = useState<string>("");
  const [newProjectKind, setNewProjectKind] = useState<"special" | "odd" | "serviceSpecial" | "serviceInternal">("special");
  const [newProjectEntryMode, setNewProjectEntryMode] = useState<"full" | "current">("full");
  const [newProjectHeritage, setNewProjectHeritage] = useState(true);
  const [selectedLogDate, setSelectedLogDate] = useState(localDateKey());
  const [dayLogMode, setDayLogMode] = useState<"create" | "edit">("create");
  const [editingWorkRecordId, setEditingWorkRecordId] = useState("");
  const [workRecordQuery, setWorkRecordQuery] = useState("");
  const [workRecordProjectFilter, setWorkRecordProjectFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchHit, setSearchHit] = useState<{ recordId: string; sentence: string; attachmentId?: string; kind: "content" | "attachment" | "field" } | null>(null);
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [syncError, setSyncError] = useState(false);
  const [workRecordMonthLimit, setWorkRecordMonthLimit] = useState(6);
  const [draftAttachments, setDraftAttachments] = useState<WorkRecordAttachment[]>([]);
  const [pasteEditorOpen, setPasteEditorOpen] = useState(false);
  const [pasteName, setPasteName] = useState("");
  const [pasteText, setPasteText] = useState("");
  const attachmentInputRef = useRef<HTMLInputElement>(null);
  const [contractInstallmentCount, setContractInstallmentCount] = useState(1);
  const [contractAmountStatus, setContractAmountStatus] = useState<"confirmed" | "pending">("confirmed");
  const [procurementBasis, setProcurementBasis] = useState<ProcurementBasisKind | "">("");
  const [procurementForm, setProcurementForm] = useState<ProcurementForm | "">("");
  const [contractSettlementAmount, setContractSettlementAmount] = useState("");
  const [selectedFundingCell, setSelectedFundingCell] = useState("A1");
  const [editingFundingCell, setEditingFundingCell] = useState("");
  const [calculatorDisplay, setCalculatorDisplay] = useState("");
  const [settlementRecords, setSettlementRecords] = useState<SettlementRecord[]>([]);
  const [viewDate, setViewDate] = useState(() => new Date());
  const [cloud, setCloud] = useState<SupabaseClient | null>(null);
  const [user, setUser] = useState<CloudUser | null>(null);
  const [authResolved, setAuthResolved] = useState(false);
  const [guestMode, setGuestMode] = useState(false);
  const [authMode, setAuthMode] = useState<"login" | "register" | "forgot" | "otp">("login");
  const [authError, setAuthError] = useState("");
  const [syncLabel, setSyncLabel] = useState("本机保存");
  const [syncReady, setSyncReady] = useState(false);
  const [syncRevision, setSyncRevision] = useState(0);
  const [adminRows, setAdminRows] = useState<AdminUsageRow[]>([]);
  const [selectedConfigNode, setSelectedConfigNode] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toastTimerRef = useRef<number | null>(null);
  const undoTimerRef = useRef<number | null>(null);
  const undoActionRef = useRef<(() => void) | null>(null);
  const syncMetaRef = useRef<SyncMeta>({
    version: 5,
    device_id: "",
    sections: emptySectionTimes(),
    records: emptyRecordVersions(),
  });
  const sectionSnapshotsRef = useRef<Record<SyncSection, string>>({
    projects: "[]",
    config: "{}",
    attendance: "{}",
    todo: "[]",
    calendar: "{}",
    settlement: "[]",
  });
  const syncRunRef = useRef(0);
  const syncInFlightOwnerRef = useRef("");
  const syncedUserRef = useRef("");
  const lastSyncUploadAtRef = useRef(0);
  const activeOwnerRef = useRef(GUEST_OWNER);
  const latestDataRef = useRef<SyncData>({
    projects: [],
    config: clone(DEFAULT_CONFIG),
    attendance: clone(EMPTY_ATTENDANCE),
    todo: [],
    calendar: {},
    settlement: [],
  });
  latestDataRef.current = {
    projects,
    config: nodeConfig,
    attendance,
    todo: todos,
    calendar,
    settlement: settlementRecords,
  };

  const showToast = useCallback((message: string) => {
    if (toastTimerRef.current !== null) window.clearTimeout(toastTimerRef.current);
    if (undoTimerRef.current !== null) window.clearTimeout(undoTimerRef.current);
    undoTimerRef.current = null;
    undoActionRef.current = null;
    setUndoState(null);
    setToast(message);
    toastTimerRef.current = window.setTimeout(() => {
      setToast("");
      toastTimerRef.current = null;
    }, 2400);
  }, []);

  const runWithUndo = (message: string, action: () => void, undoAction: () => void) => {
    if (toastTimerRef.current !== null) window.clearTimeout(toastTimerRef.current);
    if (undoTimerRef.current !== null) window.clearTimeout(undoTimerRef.current);
    setToast("");
    setUndoState({
      owner: activeOwnerRef.current,
      message,
    });
    undoActionRef.current = undoAction;
    action();
    undoTimerRef.current = window.setTimeout(() => {
      setUndoState(null);
      undoActionRef.current = null;
      undoTimerRef.current = null;
    }, 8000);
  };

  const undoLastDeletion = () => {
    if (!undoState || undoState.owner !== activeOwnerRef.current || !undoActionRef.current) {
      setUndoState(null);
      undoActionRef.current = null;
      return;
    }
    if (undoTimerRef.current !== null) window.clearTimeout(undoTimerRef.current);
    const undoAction = undoActionRef.current;
    undoActionRef.current = null;
    undoAction();
    setUndoState(null);
    undoTimerRef.current = null;
    showToast("已撤销删除");
  };

  useEffect(() => () => {
    if (toastTimerRef.current !== null) window.clearTimeout(toastTimerRef.current);
    if (undoTimerRef.current !== null) window.clearTimeout(undoTimerRef.current);
    undoActionRef.current = null;
  }, []);

  useEffect(() => {
    setStageEventFilterId("");
  }, [currentProjectId]);

  useEffect(() => {
    const closeFloatingMenus = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      document
        .querySelectorAll<HTMLDetailsElement>("details.stage-menu[open], details.row-menu[open]")
        .forEach((menu) => {
          if (!menu.contains(target)) menu.removeAttribute("open");
        });
    };

    document.addEventListener("pointerdown", closeFloatingMenus);
    return () => document.removeEventListener("pointerdown", closeFloatingMenus);
  }, []);

  useEffect(() => {
    applyAppearance(appearance);
    localStorage.setItem(APPEARANCE_KEY, JSON.stringify(appearance));
  }, [appearance]);

  useEffect(() => {
    const rawStoredProjects = safeRead<Project[]>(KEYS.projects, []);
    const storedConfig = safeRead<NodeConfig>(KEYS.config, {});
    const storedTodos = safeRead<Todo[]>(KEYS.todo, []);
    const storedAttendance = { ...clone(EMPTY_ATTENDANCE), ...safeRead<Attendance>(KEYS.attendance, EMPTY_ATTENDANCE) };
    const storedCalendar = normalizeCalendar(safeRead<StoredCalendarEntries>(KEYS.calendar, {}));
    if (
      !localStorage.getItem(KEYS.migrationBackup) &&
      (rawStoredProjects.length || storedTodos.length || Object.keys(storedCalendar).length)
    ) {
      localStorage.setItem(KEYS.migrationBackup, JSON.stringify({
        version: "升级前只读备份",
        createdAt: new Date().toISOString(),
        projects: rawStoredProjects,
        config: storedConfig,
        attendance: storedAttendance,
        todo: storedTodos,
        calendar: storedCalendar,
      }));
    }
    let deviceId = localStorage.getItem(KEYS.deviceId) || "";
    if (!deviceId) {
      deviceId = makeId();
      localStorage.setItem(KEYS.deviceId, deviceId);
    }
    const guestSnapshot = readOwnerSnapshot(GUEST_OWNER, deviceId);
    activeOwnerRef.current = GUEST_OWNER;
    syncMetaRef.current = guestSnapshot.meta;
    sectionSnapshotsRef.current = guestSnapshot.snapshots;
    latestDataRef.current = guestSnapshot.data;
    localStorage.setItem(guestSnapshot.keys.syncMeta, JSON.stringify(guestSnapshot.meta));
    setProjects(guestSnapshot.data.projects);
    setTodos(guestSnapshot.data.todo);
    setAttendance(guestSnapshot.data.attendance);
    setCalendar(guestSnapshot.data.calendar);
    setNodeConfig(guestSnapshot.data.config);
    setSettlementRecords(guestSnapshot.data.settlement);
    setHydrated(true);

    const onPopState = (event: PopStateEvent) => {
      const next = (event.state?.page as AppPage) || "home";
      setPage(next);
      if (next !== "projectDetail") setProjectTab("overview");
    };
    const pendingHash = window.location.hash || "";
    if (!pendingHash.includes("type=email_change")) {
      history.replaceState({ page: "home" }, "", "#home");
    }
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    const currentData = isolateOwnedRecords({
      projects,
      config: nodeConfig,
      attendance,
      todo: todos,
      calendar,
      settlement: settlementRecords,
    }, activeOwnerRef.current);
    const previousData: SyncData = {
      projects: JSON.parse(sectionSnapshotsRef.current.projects) as Project[],
      config: JSON.parse(sectionSnapshotsRef.current.config) as NodeConfig,
      attendance: JSON.parse(sectionSnapshotsRef.current.attendance) as Attendance,
      todo: JSON.parse(sectionSnapshotsRef.current.todo) as Todo[],
      calendar: normalizeCalendar(JSON.parse(sectionSnapshotsRef.current.calendar) as StoredCalendarEntries),
      settlement: JSON.parse(sectionSnapshotsRef.current.settlement || "[]") as SettlementRecord[],
    };
    const previousEntries = syncEntries(previousData);
    const currentEntries = syncEntries(currentData);
    const ownerKeys = ownerStorageKeys(activeOwnerRef.current);
    const storageKeys: Record<SyncSection, string> = {
      projects: ownerKeys.projects,
      config: ownerKeys.config,
      attendance: ownerKeys.attendance,
      todo: ownerKeys.todo,
      calendar: ownerKeys.calendar,
      settlement: ownerKeys.settlement,
    };
    let localChanged = false;
    const now = Date.now();
    SYNC_SECTIONS.forEach((section) => {
      const serialized = JSON.stringify(currentData[section]);
      if (sectionSnapshotsRef.current[section] === serialized) return;
      sectionSnapshotsRef.current[section] = serialized;
      localStorage.setItem(storageKeys[section], serialized);
      (Object.keys(currentEntries) as SyncCollection[])
        .filter((collection) => collectionSection[collection] === section)
        .forEach((collection) => {
          const before = previousEntries[collection];
          const after = currentEntries[collection];
          const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
          keys.forEach((key) => {
            const existed = Object.prototype.hasOwnProperty.call(before, key);
            const exists = Object.prototype.hasOwnProperty.call(after, key);
            if (existed === exists && JSON.stringify(before[key]) === JSON.stringify(after[key])) return;
            syncMetaRef.current.records[collection][key] = {
              updated_at: now,
              device_id: syncMetaRef.current.device_id,
              ...(exists ? {} : { deleted: true }),
            };
          });
        });
      syncMetaRef.current.sections[section] = now;
      localChanged = true;
    });
    localStorage.setItem(ownerKeys.syncMeta, JSON.stringify(syncMetaRef.current));
    if (localChanged) setSyncRevision((revision) => revision + 1);
  }, [attendance, calendar, hydrated, nodeConfig, projects, todos, settlementRecords]);

  useEffect(() => {
    let attempts = 0;
    const connect = () => {
      if (window.supabase) {
        const client = window.supabase.createClient(SUPABASE_URL, SUPABASE_KEY, {
          auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
        });
        setCloud(client);
        client.auth.getSession().then(({ data }) => {
          if (data.session?.user) setUser(data.session.user);
        }).finally(() => setAuthResolved(true));
        return true;
      }
      attempts += 1;
      if (attempts > 80) setAuthResolved(true);
      return attempts > 80;
    };
    if (connect()) return;
    const timer = window.setInterval(() => connect() && window.clearInterval(timer), 250);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!cloud) return;
    const hash = window.location.hash || "";
    if (!hash.includes("type=email_change")) return;
    const confirmEmailChange = async () => {
      try {
        const { error } = await cloud.auth.verifyOtp({ token_hash: hash, type: "email_change" });
        if (error) {
          showToast(`邮箱更换确认失败：${error.message}`);
        } else {
          const session = (await cloud.auth.getSession()).data.session;
          if (session?.user) setUser(session.user);
          showToast("邮箱已更换成功，请使用新邮箱登录");
        }
      } catch (error) {
        showToast(`邮箱更换确认失败：${error instanceof Error ? error.message : "未知错误"}`);
      } finally {
        history.replaceState({ page: "home" }, "", "#home");
      }
    };
    void confirmEmailChange();
  }, [cloud, showToast]);

  useEffect(() => {
    if (!hydrated) return;
    const nextOwner = user?.id || GUEST_OWNER;
    if (activeOwnerRef.current === nextOwner) return;
    if (undoTimerRef.current !== null) window.clearTimeout(undoTimerRef.current);
    undoTimerRef.current = null;
    undoActionRef.current = null;
    setUndoState(null);
    syncRunRef.current += 1;
    syncInFlightOwnerRef.current = "";
    const deviceId = localStorage.getItem(KEYS.deviceId) || makeId();
    const snapshot = readOwnerSnapshot(nextOwner, deviceId);
    activeOwnerRef.current = nextOwner;
    syncMetaRef.current = snapshot.meta;
    sectionSnapshotsRef.current = snapshot.snapshots;
    latestDataRef.current = snapshot.data;
    localStorage.setItem(snapshot.keys.syncMeta, JSON.stringify(snapshot.meta));
    syncedUserRef.current = "";
    setSyncReady(false);
    setSyncRevision(0);
    setCurrentProjectId(null);
    setPage("home");
    setProjects(snapshot.data.projects);
    setTodos(snapshot.data.todo);
    setAttendance(snapshot.data.attendance);
    setCalendar(snapshot.data.calendar);
    setNodeConfig(snapshot.data.config);
    setSettlementRecords(snapshot.data.settlement);
    const savedSyncAt = Number(localStorage.getItem(snapshot.keys.lastSync) || 0);
    setSyncLabel(user ? (savedSyncAt ? syncedLabel(savedSyncAt) : "已连接") : "本机保存");
    setE2ESettings(readE2ESettings(user?.id));
  }, [hydrated, user]);

  const syncWithCloud = useCallback(
    async (announce = false) => {
      if (!cloud || !user || activeOwnerRef.current !== user.id || syncInFlightOwnerRef.current === user.id) return;
      const ownerId = user.id;
      const ownerKeys = ownerStorageKeys(ownerId);
      const runId = syncRunRef.current + 1;
      syncRunRef.current = runId;
      syncInFlightOwnerRef.current = ownerId;
      const isCurrentOwner = () =>
        canApplySyncResult(activeOwnerRef.current, syncRunRef.current, ownerId, runId);
      const markSynced = () => {
        const syncedAt = Date.now();
        localStorage.setItem(ownerKeys.lastSync, String(syncedAt));
        setSyncLabel(syncedLabel(syncedAt));
        setSyncError(false);
      };
      if (announce) setSyncLabel("正在同步");
      try {
        const { data, error } = await cloud.from("user_data").select("content").eq("user_id", ownerId).maybeSingle();
        if (!isCurrentOwner()) return;
        if (error) throw new Error(error.message);

        const localData = isolateOwnedRecords(clone(latestDataRef.current), ownerId);
        let remotePayload = data?.content as CloudPayload | null;
        if (remotePayload && isE2EEnvelope(remotePayload)) {
          const settings = readE2ESettings(ownerId);
          const masterKey = settings ? await masterKeyFromSettings(settings) : null;
          if (!masterKey) {
            setPendingRecovery(true);
            setSyncLabel("数据已加密，需要密码或恢复码解锁");
            return;
          }
          try {
            remotePayload = await decryptEnvelope(remotePayload as E2EEnvelope, masterKey);
          } catch {
            setPendingRecovery(true);
            setSyncLabel("解密失败，需要密码或恢复码解锁");
            return;
          }
        }
        if (!remotePayload) {
          const initialMeta = { ...clone(syncMetaRef.current), version: 5 };
          syncMetaRef.current = initialMeta;
          localStorage.setItem(ownerKeys.syncMeta, JSON.stringify(initialMeta));
          const initialPayload: CloudPayload = {
            ...localData,
            owner_id: ownerId,
            updated_at: new Date().toISOString(),
            sync_meta: initialMeta,
          };
          const e2eSettingsHere = readE2ESettings(ownerId);
          const initialContent = e2eSettingsHere
            ? await encryptPayloadWithKey(initialPayload, await masterKeyFromSettings(e2eSettingsHere), e2eSettingsHere.header)
            : initialPayload;
          const { error: uploadError } = await cloud
            .from("user_data")
            .upsert({ user_id: ownerId, content: initialContent });
          if (!isCurrentOwner()) return;
          if (uploadError) throw new Error(uploadError.message);
          lastSyncUploadAtRef.current = Date.now();
          setSyncReady(true);
          markSynced();
          if (announce) showToast("本机数据已保存到云端");
          return;
        }
        if (remotePayload.owner_id && remotePayload.owner_id !== ownerId) {
          throw new Error("账号数据归属不一致，已停止同步");
        }

        const legacyTime = Date.parse(remotePayload.updated_at || "") || 0;
        const remoteData = isolateOwnedRecords(payloadData(remotePayload), ownerId);
        const legacySections: Record<SyncSection, number> = {
          projects: remotePayload.projects !== undefined ? legacyTime : 0,
          config: remotePayload.config !== undefined ? legacyTime : 0,
          attendance: remotePayload.attendance !== undefined ? legacyTime : 0,
          todo: remotePayload.todo !== undefined ? legacyTime : 0,
          calendar: remotePayload.calendar !== undefined ? legacyTime : 0,
          settlement: remotePayload.settlement !== undefined ? legacyTime : 0,
        };
        const remoteDeviceId = remotePayload.sync_meta?.device_id || "cloud-legacy";
        const remoteMeta = normalizeSyncMeta(
          {
            ...remotePayload.sync_meta,
            device_id: remoteDeviceId,
            sections: { ...legacySections, ...(remotePayload.sync_meta?.sections || {}) },
          },
          remoteData,
          remoteDeviceId,
        );
        const localMeta = normalizeSyncMeta(
          syncMetaRef.current,
          localData,
          syncMetaRef.current.device_id,
        );
        const merged = mergeSyncState(localData, localMeta, remoteData, remoteMeta);
        const nextData = merged.data;
        const nextMeta = merged.meta;
        const downloaded = merged.downloaded;
        const uploadNeeded =
          merged.uploadNeeded ||
          remotePayload.sync_meta?.version !== 5 ||
          remotePayload.owner_id !== ownerId;

        syncMetaRef.current = nextMeta;
        if (!isCurrentOwner()) return;
        localStorage.setItem(ownerKeys.syncMeta, JSON.stringify(nextMeta));
        if (downloaded) {
          try {
            localStorage.setItem(
              ownerKeys.preSyncBackup,
              JSON.stringify(backupPayload(localData, "云端同步前自动保存")),
            );
          } catch {
            // 浏览器空间不足不应阻断正常同步。
          }
        }

        const applyRemoteSection = (section: SyncSection, value: SyncData[SyncSection]) => {
          if (JSON.stringify(localData[section]) === JSON.stringify(value)) return;
          const serialized = JSON.stringify(value);
          const storageKeys: Record<SyncSection, string> = {
            projects: ownerKeys.projects,
            config: ownerKeys.config,
            attendance: ownerKeys.attendance,
            todo: ownerKeys.todo,
            calendar: ownerKeys.calendar,
            settlement: ownerKeys.settlement,
          };
          sectionSnapshotsRef.current[section] = serialized;
          localStorage.setItem(storageKeys[section], serialized);
          if (section === "projects") setProjects(value as Project[]);
          if (section === "config") setNodeConfig({ ...clone(DEFAULT_CONFIG), ...(value as NodeConfig) });
          if (section === "attendance") {
            setAttendance({ ...clone(EMPTY_ATTENDANCE), ...(value as Attendance) });
          }
          if (section === "todo") setTodos(value as Todo[]);
          if (section === "calendar") setCalendar(normalizeCalendar(value as StoredCalendarEntries));
          if (section === "settlement") setSettlementRecords(value as SettlementRecord[]);
        };
        SYNC_SECTIONS.forEach((section) => applyRemoteSection(section, nextData[section]));

        if (uploadNeeded) {
          const mergedPayload: CloudPayload = {
            ...nextData,
            owner_id: ownerId,
            updated_at: new Date().toISOString(),
            sync_meta: clone(nextMeta),
          };
          const e2eSettingsHere = readE2ESettings(ownerId);
          const mergedContent = e2eSettingsHere
            ? await encryptPayloadWithKey(mergedPayload, await masterKeyFromSettings(e2eSettingsHere), e2eSettingsHere.header)
            : mergedPayload;
          const { error: uploadError } = await cloud
            .from("user_data")
            .upsert({ user_id: ownerId, content: mergedContent });
          if (!isCurrentOwner()) return;
          if (uploadError) throw new Error(uploadError.message);
          lastSyncUploadAtRef.current = Date.now();
        }

        setSyncReady(true);
        markSynced();
        if (announce) {
          showToast(downloaded ? "已合并手机和电脑上的最新数据" : "当前数据已同步");
        }
      } catch (error) {
        if (!isCurrentOwner()) return;
        setSyncLabel("同步失败");
        setSyncError(true);
        if (announce) {
          showToast(`同步失败：${error instanceof Error ? error.message : "请检查网络后重试"}`);
        }
      } finally {
        if (syncInFlightOwnerRef.current === ownerId && syncRunRef.current === runId) {
          syncInFlightOwnerRef.current = "";
        }
      }
    },
    [cloud, showToast, user],
  );

  useEffect(() => {
    if (!hydrated || !cloud || !user || syncedUserRef.current === user.id) return;
    syncedUserRef.current = user.id;
    setSyncReady(false);
    void syncWithCloud(false);
  }, [cloud, hydrated, syncWithCloud, user]);

  useEffect(() => {
    if (!syncReady || !cloud || !user || syncRevision === 0) return;
    const timer = window.setTimeout(() => void syncWithCloud(false), 1200);
    return () => window.clearTimeout(timer);
  }, [cloud, syncReady, syncRevision, syncWithCloud, user]);

  useEffect(() => {
    if (!cloud || !user) return;
    const refresh = () => void syncWithCloud(false);
    const onVisibility = () => {
      if (document.visibilityState === "visible") refresh();
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [cloud, syncReady, syncWithCloud, user]);

  useEffect(() => {
    if (!cloud || !user?.email) return;
    let lastRecordedAt = 0;
    const recordUsage = () => {
      const now = Date.now();
      if (now - lastRecordedAt < 15_000) return;
      lastRecordedAt = now;
      void cloud.rpc("record_app_usage").then(({ error }) => {
        if (error) console.warn("账号使用时间记录失败", error.message);
      });
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") recordUsage();
    };
    recordUsage();
    const timer = window.setInterval(recordUsage, 5 * 60_000);
    window.addEventListener("focus", recordUsage);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", recordUsage);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [cloud, user?.email, user?.id]);

  useEffect(() => {
    if (!cloud || !user) return;
    const deviceId = localStorage.getItem(KEYS.deviceId) || "";
    const touchDevice = () => {
      void cloud.rpc("touch_user_device", {
        p_device_id: deviceId,
        p_device_name: deviceLabel(),
      }).then(({ error }) => {
        if (error) console.warn("设备登记失败", error.message);
      });
    };
    touchDevice();
    const timer = window.setInterval(touchDevice, 5 * 60_000);
    return () => window.clearInterval(timer);
  }, [cloud, user]);

  useEffect(() => {
    if (modal === "account" && user) void loadDevices();
  }, [modal, user]);

  useEffect(() => {
    if (modal !== "dayOverview" || !searchHit) return;
    const timer = window.setTimeout(() => {
      const target = document.querySelector<HTMLElement>(".modal-card .search-hit-target");
      if (!target) return;
      target.scrollIntoView({ block: "center", behavior: "smooth" });
      target.classList.add("search-hit-flash");
      window.setTimeout(() => target.classList.remove("search-hit-flash"), 1800);
    }, 120);
    return () => window.clearTimeout(timer);
  }, [modal, searchHit]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(searchQuery), 180);
    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    if (pendingRecovery && modal !== "recovery") setModal("recovery");
  }, [pendingRecovery, modal]);

  useEffect(() => {
    if (modal !== "recovery") setShowRecoveryFields(false);
  }, [modal]);

  const navigate = (nextPage: AppPage) => {
    if (nextPage === "projects") setProjectBrowserMode("root");
    setPage(nextPage);
    history.pushState({ page: nextPage }, "", `#${nextPage}`);
    window.requestAnimationFrame(() => {
      document.querySelector<HTMLElement>(".main-area")?.scrollTo({ top: 0, behavior: "smooth" });
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
  };
  const goBack = () => {
    if (history.length > 1) history.back();
    else setPage("home");
  };

  useEffect(() => {
    const onBackspace = (event: KeyboardEvent) => {
      if (event.key !== "Backspace" || event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey) return;
      if (modal || !window.matchMedia("(pointer: fine)").matches) return;
      const target = event.target as HTMLElement | null;
      if (target?.closest("input, textarea, select, [contenteditable='true']")) return;
      if (page === "projectDetail" && projectTab === "sheet") {
        event.preventDefault();
        setProjectTab("overview");
        return;
      }
      if (page === "home") return;
      event.preventDefault();
      if (history.length > 1) history.back();
      else setPage("home");
    };
    window.addEventListener("keydown", onBackspace);
    return () => window.removeEventListener("keydown", onBackspace);
  }, [modal, page, projectTab]);

  const currentProject = useMemo(
    () => projects.find((project) => String(project.id) === String(currentProjectId)) || null,
    [currentProjectId, projects],
  );
  const linkedProjects = useMemo(
    () => projects.filter((project) => project.status !== "trashed"),
    [projects],
  );
  const editingFundingPlan = useMemo(
    () => currentProject?.funds
      ?.find((fund) => fund.id === editingFundingSourceId)
      ?.annualPlans?.find((plan) => plan.id === editingFundingPlanId) || null,
    [currentProject, editingFundingPlanId, editingFundingSourceId],
  );
  const editingFundingSource = useMemo(
    () => currentProject?.funds?.find((fund) => fund.id === editingFundingSourceId) || null,
    [currentProject, editingFundingSourceId],
  );
  const fundingPaymentPreview = useMemo(() => {
    if (!currentProject || !fundingPaymentPreviewPlanId) return null;
    const fund = (currentProject.funds || []).find((item) =>
      (item.annualPlans || []).some((plan) => plan.id === fundingPaymentPreviewPlanId));
    const plan = fund?.annualPlans?.find((item) => item.id === fundingPaymentPreviewPlanId);
    if (!fund || !plan) return null;
    return { fund, plan, payments: fundingPlanPayments(currentProject, plan.id) };
  }, [currentProject, fundingPaymentPreviewPlanId]);
  const fundingUnpaidPreview = useMemo(() => {
    if (!currentProject || !fundingUnpaidPreviewPlanId) return null;
    const fund = (currentProject.funds || []).find((item) =>
      (item.annualPlans || []).some((plan) => plan.id === fundingUnpaidPreviewPlanId));
    const plan = fund?.annualPlans?.find((item) => item.id === fundingUnpaidPreviewPlanId);
    if (!fund || !plan) return null;
    const schedules = fundingPlanUnpaidSchedules(currentProject, plan.id);
    return {
      fund,
      plan,
      schedules,
      amount: schedules.reduce((sum, item) => sum + safeNumber(item.amount), 0),
    };
  }, [currentProject, fundingUnpaidPreviewPlanId]);
  const fundingClawbackPreview = useMemo(() => {
    if (!currentProject || !fundingClawbackPreviewPlanId) return null;
    const fund = (currentProject.funds || []).find((item) =>
      (item.annualPlans || []).some((plan) => plan.id === fundingClawbackPreviewPlanId));
    const plan = fund?.annualPlans?.find((item) => item.id === fundingClawbackPreviewPlanId);
    if (!fund || !plan) return null;
    const payments = fundingPlanPayments(currentProject, plan.id);
    const paid = payments.reduce((sum, item) => sum + safeNumber(item.payment.amount), 0);
    const received = safeNumber(plan.requestedAmount);
    return { fund, plan, payments, paid, received, remaining: Math.max(0, received - paid) };
  }, [currentProject, fundingClawbackPreviewPlanId]);
  const editingProjectEvent = useMemo(
    () => currentProject?.events?.find((event) => event.id === editingProjectEventId) || null,
    [currentProject, editingProjectEventId],
  );
  const editingService = useMemo(
    () => currentProject?.services?.find((service) => service.id === editingServiceId) || null,
    [currentProject, editingServiceId],
  );
  const editingServiceContract = useMemo(
    () => editingService?.linkedContractId
      ? (currentProject?.procurements || []).flatMap((item) => item.contracts || []).find((contract) => contract.id === editingService.linkedContractId) || null
      : null,
    [currentProject, editingService],
  );
  const editingProcurement = useMemo(
    () => currentProject?.procurements?.find((item) => item.id === editingProcurementId) || null,
    [currentProject, editingProcurementId],
  );
  const editingContract = useMemo(
    () => editingProcurement?.contracts.find((item) => item.id === editingContractId) || null,
    [editingContractId, editingProcurement],
  );
  const editingPayment = useMemo(
    () => editingContract?.payments.find((item) => item.id === editingPaymentId) || null,
    [editingContract, editingPaymentId],
  );
  const paymentFundingPlans = useMemo(() => (currentProject?.funds || [])
    .flatMap((fund) => (fund.annualPlans || []).map((plan) => ({ fund, plan })))
    .sort((left, right) => right.plan.year.localeCompare(left.plan.year) || left.fund.name.localeCompare(right.fund.name)), [currentProject]);
  const editingOvertime = useMemo(
    () => attendance.records.find((record) => record.id === editingOvertimeId) || null,
    [attendance.records, editingOvertimeId],
  );
  const editingTodo = useMemo(
    () => todos.find((todo) => todoKey(todo) === editingTodoId) || null,
    [editingTodoId, todos],
  );
  const editingTodoEvent = useMemo(() => {
    if (!editingTodo?.linkedProjectId) return null;
    return projects
      .find((project) => String(project.id) === editingTodo.linkedProjectId)
      ?.events?.find((event) => event.sourceTodoId === editingTodo.id || event.id === editingTodo.linkedEventId) || null;
  }, [editingTodo, projects]);
  const editingTodoCategory = editingTodo?.eventCategory || editingTodoEvent?.category || "other";
  const editingTodoStageId = editingTodo?.linkedStageId || editingTodoEvent?.sourceStageId || "";
  useEffect(() => {
    if (modal === "todoCreate") {
      setTodoFormProjectId("");
      setTodoFormStageId("");
      setTodoFormDate("");
    } else if (modal === "todoEdit" && editingTodo) {
      setTodoFormProjectId(editingTodo.linkedProjectId || "");
      setTodoFormStageId(editingTodoStageId);
      setTodoFormDate(editingTodo.scheduledDate || "");
    }
  }, [editingTodo, editingTodoStageId, modal]);

  useEffect(() => {
    if (modal !== "dayLog") return;
    setPasteEditorOpen(false);
    setPasteName("");
    setPasteText("");
    if (dayLogMode === "edit" && editingWorkRecordId) {
      const record = calendar[selectedLogDate]?.find((item) => item.id === editingWorkRecordId);
      setDraftAttachments(record?.attachments ? clone(record.attachments) : []);
    } else {
      setDraftAttachments([]);
    }
  }, [calendar, dayLogMode, editingWorkRecordId, modal, selectedLogDate]);
  const pendingTodoCompletion = useMemo(
    () => todos.find((todo) => todoKey(todo) === pendingTodoCompletionId) || null,
    [pendingTodoCompletionId, todos],
  );
  const editingMatter = useMemo(
    () => todos.find((item) => String(item.id || "") === editingMatterId) || null,
    [editingMatterId, todos],
  );
  const newProjectStageNames = useMemo(
    () => templateStageNames(
      newProjectKind === "serviceSpecial" ? "special" : newProjectKind === "serviceInternal" ? "odd" : newProjectKind,
      newProjectKind === "special" && newProjectHeritage,
      newProjectKind === "serviceSpecial" || newProjectKind === "serviceInternal" ? "service" : "engineering",
    ),
    [newProjectHeritage, newProjectKind],
  );
  const activeTodos = useMemo(() => todos.filter((todo) => (!todo.kind || todo.kind === "todo") && !todo.done), [todos]);
  const doneTodos = useMemo(() => todos.filter((todo) => (!todo.kind || todo.kind === "todo") && todo.done), [todos]);
  // A linked todo has one project-event mirror.  In calendar views the todo is
  // the primary record, so hide that mirror only when both fall on this date.
  // This is display-only: neither the todo nor the project event is changed.
  const calendarProjectEventsForDate = useCallback((date: string) => {
    const representedTodoIds = new Set<string>();
    activeTodos
      .filter((todo) => todo.scheduledDate === date && todo.id)
      .forEach((todo) => representedTodoIds.add(String(todo.id)));
    (calendar[date] || [])
      .filter((record) => record.sourceTodoId)
      .forEach((record) => representedTodoIds.add(String(record.sourceTodoId)));

    return linkedProjects.flatMap((project) => (project.events || [])
      .filter((event) => (event.occurredOn === date || event.dueDate === date)
        && (!event.sourceTodoId || !representedTodoIds.has(String(event.sourceTodoId))))
      .map((event) => ({ project, event })));
  }, [activeTodos, calendar, linkedProjects]);
  const todayTodos = useMemo(
    () => activeTodos.filter((todo) => todo.scheduledDate === localDateKey()),
    [activeTodos],
  );
  const workRecords = useMemo(
    () => Object.entries(calendar)
      .flatMap(([date, records]) => records.map((record, index) => ({ date, ...record, index })))
      .sort((left, right) => {
        const dateCompare = right.date.localeCompare(left.date);
        if (dateCompare !== 0) return dateCompare;
        const leftTime = left.occurredAt || "99:99";
        const rightTime = right.occurredAt || "99:99";
        if (leftTime !== rightTime) return leftTime.localeCompare(rightTime);
        return left.index - right.index;
      }),
    [calendar],
  );
  const workRecordGroups = useMemo(() => {
    const years = new Map<string, Map<string, Map<string, typeof workRecords>>>();
    workRecords.forEach((record) => {
      const year = record.date.slice(0, 4);
      const month = record.date.slice(5, 7);
      const months = years.get(year) || new Map<string, Map<string, typeof workRecords>>();
      const days = months.get(month) || new Map<string, typeof workRecords>();
      days.set(record.date, [...(days.get(record.date) || []), record]);
      months.set(month, days);
      years.set(year, months);
    });
    return [...years.entries()].map(([year, months]) => ({
      year,
      months: [...months.entries()].map(([month, days]) => ({
        month,
        records: [...days.entries()].map(([date, entries]) => ({ date, entries })),
      })),
    }));
  }, [workRecords]);
  const searchResults = useMemo(() => {
    const query = debouncedQuery.trim();
    if (!query) return null;
    const allTermsIn = (text: string) => matchesAllTerms(text, query);
    const projectMatches = projects
      .filter((project) => project.status !== "trashed" && allTermsIn([
        project.name,
        String(project.id),
        ...(project.events || []).flatMap((event) => [event.title, event.note || ""]),
      ].join(" ")))
      .slice(0, 6);
    const todoMatches = todos
      .filter((todo) => (!todo.kind || todo.kind === "todo") && allTermsIn([todo.text, todo.note || ""].join(" ")))
      .slice(0, 8);
    const matterMatches = todos
      .filter((todo) => todo.kind && todo.kind !== "todo" && allTermsIn([todo.text, todo.note || ""].join(" ")))
      .slice(0, 5);
    const workMatches = Object.entries(calendar)
      .flatMap(([date, records]) => records
        .filter((record) => allTermsIn([
          date,
          record.content,
          record.amount || "",
          record.organization || "",
          record.contact || "",
          record.nextStep || "",
          record.dueDate || "",
          ...(record.attachments || []).flatMap((attachment) => [attachment.name, attachment.content]),
        ].join(" ")))
        .map((record) => {
          const contentHit = allTermsIn(record.content);
          const attachment = contentHit
            ? undefined
            : (record.attachments || []).find((item) => allTermsIn([item.name, item.content].join(" ")));
          const fieldsText = [record.amount || "", record.organization || "", record.contact || "", record.nextStep || "", record.dueDate || ""].filter(Boolean).join("；");
          const sourceText = contentHit
            ? record.content
            : attachment
              ? (attachment.content || attachment.name)
              : allTermsIn(fieldsText)
                ? fieldsText
                : record.content;
          const { snippet, hitSentence } = buildSearchSnippet(sourceText, query);
          return {
            date,
            ...record,
            snippet,
            hitSentence,
            hitAttachmentId: attachment?.id,
            hitKind: contentHit ? "content" as const : attachment ? "attachment" as const : "field" as const,
          };
        }))
      .slice(0, 8);
    return { projectMatches, todoMatches, matterMatches, workMatches };
  }, [calendar, debouncedQuery, projects, todos]);
  const renderSearchHighlight = (text: string, query: string): ReactNode => {
    const terms = splitSearchTerms(query);
    if (!terms.length) return text;
    const escaped = terms.map((term) => term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|");
    return text.split(new RegExp(`(${escaped})`, "gi")).map((part, index) =>
      terms.some((term) => part.toLowerCase() === term.toLowerCase())
        ? <mark key={index} className="search-hit">{part}</mark>
        : part);
  };
  const renderTextWithHit = (text: string, sentence: string): ReactNode =>
    splitSentences(text).map((segment, index) => {
      const trimmed = segment.trim();
      return sentence && (trimmed === sentence || trimmed.includes(sentence))
        ? <mark key={index} className="search-hit-target">{segment}</mark>
        : <span key={index}>{segment}</span>;
    });
  const leaveSummary = useMemo(() => {
    let usedAnnual = 0;
    let usedSaved = 0;
    let activeOvertime = 0;
    attendance.leaveRecords.forEach((record) => {
      if (record.type === "年假") usedAnnual += safeNumber(record.days);
      if (record.type === "存休") usedSaved += safeNumber(record.days);
    });
    attendance.records.forEach((record) => {
      if (record.status !== "used" && !record.holiday) activeOvertime += safeNumber(record.days);
    });
    const adjustments = attendance.adjustments || [];
    const annual = safeNumber(attendance.annual)
      + adjustments.filter((item) => item.kind === "annual").reduce((sum, item) => sum + safeNumber(item.days), 0)
      - usedAnnual;
    const saved = safeNumber(attendance.saved)
      + adjustments.filter((item) => item.kind === "saved").reduce((sum, item) => sum + safeNumber(item.days), 0)
      - usedSaved;
    return { annual, saved, activeOvertime, total: annual + saved + activeOvertime };
  }, [attendance]);

  const leaveCalendarKeys = useMemo(() => {
    const keys = new Set<string>();
    attendance.leaveRecords.forEach((record) => {
      const days = Math.max(1, Math.round(safeNumber(record.days) || 1));
      for (let offset = 0; offset < days; offset += 1) {
        const date = new Date(`${record.date}T12:00:00`);
        date.setDate(date.getDate() + offset);
        keys.add(localDateKey(date));
      }
    });
    return keys;
  }, [attendance.leaveRecords]);

  const leaveRecordsForDate = useCallback((date: string) =>
    (attendance.leaveRecords || []).filter((record) => {
      const days = Math.max(1, Math.round(safeNumber(record.days) || 1));
      const start = new Date(`${record.date}T12:00:00`);
      for (let offset = 0; offset < days; offset += 1) {
        const current = new Date(start);
        current.setDate(current.getDate() + offset);
        if (localDateKey(current) === date) return true;
      }
      return false;
    }), [attendance.leaveRecords]);

  const completeTodo = (todoId: string, recordDate?: string) => {
    const todo = todos.find((item) => todoKey(item) === todoId);
    if (!todo) return;
    const completedOn = recordDate || localDateKey();
    const completedTodo: Todo = {
      ...todo,
      id: todo.id || makeId(),
      ownerId: activeOwnerRef.current,
      done: true,
      completedOn,
      completedAt: new Date().toLocaleString("zh-CN", { month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" }),
      linkedEventId: todo.linkedProjectId ? todo.linkedEventId || makeId() : undefined,
    };
    setTodos((items) => items.map((item) => todoKey(item) === todoId ? completedTodo : item));
    if (completedTodo.linkedProjectId && completedTodo.linkedEventId) {
      setProjects((items) =>
        reconcileTodoProjectEvent(items, completedTodo, completedTodo.linkedEventId || "", completedOn));
    }
    const completedRecord = `已完成：${todo.text}`;
    setCalendar((items) => {
      const current = items[completedOn] || [];
      if (current.some((record) => record.sourceTodoId === completedTodo.id)) return items;
      return {
        ...items,
        [completedOn]: [{
          id: makeId(),
          ownerId: activeOwnerRef.current,
          sourceTodoId: completedTodo.id,
          linkedProjectId: completedTodo.linkedProjectId,
          content: completedRecord,
          createdAt: new Date().toISOString(),
        }, ...current],
      };
    });
    setPendingTodoCompletionId("");
    setModal(null);
    showToast("待办已完成，已归入工作记录");
  };
  const toggleTodo = (index: number) => {
    const todo = todos[index];
    if (!todo) return;
    if (!todo.done) {
      setPendingTodoCompletionId(todoKey(todo));
      setModal("todoCompletion");
      return;
    }
    const restoredTodo = { ...todo, done: false, completedAt: undefined, completedOn: undefined };
    setTodos((items) => items.map((item, itemIndex) => itemIndex === index ? restoredTodo : item));
    if (restoredTodo.linkedProjectId && restoredTodo.linkedEventId) {
      setProjects((items) =>
        reconcileTodoProjectEvent(items, restoredTodo, restoredTodo.linkedEventId || ""));
    }
    const completedText = `已完成：${todo.text}`;
    const nextCalendar = { ...calendar };
    const removedRecords: Array<{ date: string; record: WorkRecord; order: number }> = [];
    Object.keys(nextCalendar).forEach((date) => {
      const kept: WorkRecord[] = [];
      nextCalendar[date].forEach((record) => {
        if (record.sourceTodoId === todo.id && record.content === completedText) {
          removedRecords.push({ date, record, order: nextCalendar[date].indexOf(record) });
        } else {
          kept.push(record);
        }
      });
      if (kept.length) nextCalendar[date] = kept;
      else delete nextCalendar[date];
    });
    if (removedRecords.length) {
      runWithUndo(
        "待办已恢复未完成，自动生成的工作记录已撤回",
        () => setCalendar(nextCalendar),
        () => setCalendar((items) => {
          const restored = { ...items };
          removedRecords.forEach(({ date, record, order }) => {
            const current = restored[date] || [];
            if (current.some((item) => item.id === record.id)) return;
            const next = [...current];
            next.splice(Math.min(order, next.length), 0, record);
            restored[date] = next;
          });
          return restored;
        }),
      );
    } else {
      showToast("已恢复为未完成");
    }
  };
  const trashAdd = (entry: Omit<TrashEntry, "deletedAt">) => {
    const owner = activeOwnerRef.current;
    const entries = readTrash(owner);
    entries.unshift({ ...entry, deletedAt: new Date().toISOString() });
    if (!writeTrash(owner, entries)) showToast("回收站保存失败（空间不足）");
  };
  const trashRemoveEntry = (entryId: string) => {
    writeTrash(activeOwnerRef.current, readTrash(activeOwnerRef.current).filter((entry) => entry.id !== entryId));
  };
  const clearTrash = () => {
    writeTrash(activeOwnerRef.current, []);
    setTrashTick((tick) => tick + 1);
    showToast("回收站已清空");
  };
  const restoreTrashEntry = (entry: TrashEntry) => {
    const payload = entry.payload as Record<string, unknown>;
    const index = Math.max(Number(entry.extra?.index ?? 0), 0);
    const parentId = entry.parentId ? String(entry.parentId) : "";
    const done = (message?: string) => {
      trashRemoveEntry(entry.id);
      showToast(message || `已恢复“${entry.label}”`);
    };
    switch (entry.type) {
      case "workRecord": {
        const date = String(entry.extra?.date || "");
        if (!date) break;
        setCalendar((items) => {
          const current = items[date] || [];
          if (current.some((record) => record.id === payload.id)) return items;
          const next = [...current];
          next.splice(Math.min(index, next.length), 0, payload as unknown as WorkRecord);
          return { ...items, [date]: next };
        });
        done();
        return;
      }
      case "todo":
      case "matter": {
        const todo = payload as unknown as Todo;
        setTodos((items) => {
          if (items.some((item) => todoKey(item) === todoKey(todo))) return items;
          const next = [...items];
          next.splice(Math.min(index, next.length), 0, todo);
          return next;
        });
        if (todo.linkedProjectId && todo.linkedEventId) {
          setProjects((items) => reconcileTodoProjectEvent(items, todo, todo.linkedEventId || ""));
        }
        done();
        return;
      }
      case "attendanceAdjustment": {
        const adjustment = payload as unknown as AttendanceAdjustment;
        setAttendance((data) => {
          if ((data.adjustments || []).some((item) => item.id === adjustment.id)) return data;
          const next = [...(data.adjustments || [])];
          next.splice(Math.min(index, next.length), 0, adjustment);
          return { ...data, adjustments: next };
        });
        done();
        return;
      }
      case "overtime": {
        const record = payload as unknown as OvertimeRecord;
        setAttendance((data) => {
          if (data.records.some((item) => item.id === record.id)) return data;
          const next = [...data.records];
          next.splice(Math.min(index, next.length), 0, record);
          return { ...data, records: next };
        });
        done();
        return;
      }
      case "leave": {
        const record = payload as unknown as LeaveRecord;
        setAttendance((data) => {
          if (data.leaveRecords.some((item) => record.id ? item.id === record.id : item === record)) return data;
          const next = [...data.leaveRecords];
          next.splice(Math.min(index, next.length), 0, record);
          return { ...data, leaveRecords: next };
        });
        done();
        return;
      }
      case "event": {
        if (!parentId) break;
        updateProject(parentId, (project) => {
          const events = project.events || [];
          if (events.some((item) => item.id === payload.id)) return project;
          const next = [...events];
          next.splice(Math.min(index, next.length), 0, payload as unknown as ProjectEvent);
          return { ...project, events: next };
        });
        done();
        return;
      }
      case "stage": {
        if (!parentId) break;
        updateProject(parentId, (project) => {
          if (project.stages.some((item) => payload.id ? item.id === payload.id : item.name === payload.name)) return project;
          const next = [...project.stages];
          next.splice(Math.min(index, next.length), 0, payload as unknown as Project["stages"][number]);
          return { ...project, stages: next };
        });
        done();
        return;
      }
      case "fund": {
        if (!parentId) break;
        updateProject(parentId, (project) => {
          const funds = project.funds || [];
          if (funds.some((item) => item.id === payload.id)) return project;
          const next = [...funds];
          next.splice(Math.min(index, next.length), 0, payload as unknown as FundingSource);
          return { ...project, funds: next };
        });
        done();
        return;
      }
      case "fundingPlan": {
        const fundId = String(entry.extra?.fundId || "");
        if (!parentId || !fundId) break;
        updateProject(parentId, (project) => ({
          ...project,
          funds: (project.funds || []).map((fund) => {
            if (String(fund.id) !== fundId || (fund.annualPlans || []).some((plan) => plan.id === payload.id)) return fund;
            const next = [...(fund.annualPlans || [])];
            next.splice(Math.min(index, next.length), 0, payload as unknown as FundingYearPlan);
            return { ...fund, annualPlans: next };
          }),
        }));
        done();
        return;
      }
      case "procurement": {
        if (!parentId) break;
        updateProject(parentId, (project) => {
          const procurements = project.procurements || [];
          if (procurements.some((item) => item.id === payload.id)) return project;
          const next = [...procurements];
          next.splice(Math.min(index, next.length), 0, payload as unknown as ProcurementPackage);
          return { ...project, procurements: next };
        });
        done();
        return;
      }
      case "contract": {
        const procurementId = String(entry.extra?.procurementId || "");
        if (!parentId || !procurementId) break;
        updateProject(parentId, (project) => {
          const restoredProject = {
            ...project,
            procurements: (project.procurements || []).map((procurement) => {
              if (String(procurement.id) !== procurementId || procurement.contracts.some((contract) => contract.id === payload.id)) return procurement;
              const next = [...procurement.contracts];
              next.splice(Math.min(index, next.length), 0, payload as unknown as ContractRecord);
              return { ...procurement, contracts: next };
            }),
          };
          return syncContractParticipant(restoredProject, procurementId, payload as unknown as ContractRecord);
        });
        done();
        return;
      }
      case "payment": {
        const procurementId = String(entry.extra?.procurementId || "");
        const contractId = String(entry.extra?.contractId || "");
        if (!parentId || !procurementId || !contractId) break;
        updateProject(parentId, (project) => updateContractWithParticipant(
          project,
          procurementId,
          contractId,
          (contract) => {
            if (contract.payments.some((payment) => payment.id === payload.id)) return contract;
            const next = [...contract.payments];
            next.splice(Math.min(index, next.length), 0, payload as unknown as PaymentRecord);
            return { ...contract, payments: next };
          },
        ));
        done();
        return;
      }
      case "service": {
        if (!parentId) break;
        updateProject(parentId, (project) => {
          const services = project.services || [];
          if (services.some((item) => payload.id ? item.id === payload.id : item.name === payload.name && item.type === payload.type)) return project;
          const next = [...services];
          next.splice(Math.min(index, next.length), 0, payload as unknown as Service);
          return { ...project, services: next };
        });
        done();
        return;
      }
    }
    showToast("该记录暂无法恢复（所属项目可能已删除）");
  };

  const removeTodo = (index: number) => {
    const todo = todos[index];
    if (!todo) return;
    const detachedTodo = { ...todo, linkedProjectId: undefined };
    setTodos((items) => items.filter((_, itemIndex) => itemIndex !== index));
    if (todo.linkedProjectId && todo.linkedEventId) {
      setProjects((items) => reconcileTodoProjectEvent(items, detachedTodo, ""));
    }
    trashAdd({
      id: String(todo.id || todoKey(todo)),
      type: "todo",
      label: todo.text,
      parentId: todo.linkedProjectId ? String(todo.linkedProjectId) : undefined,
      parentLabel: todo.linkedProjectId ? projects.find((project) => String(project.id) === String(todo.linkedProjectId))?.name : undefined,
      extra: { index },
      payload: todo,
    });
    showToast("待办已移到回收站");
  };
  const unlinkTodoProjectEvent = (event: ProjectEvent, projectId: Project["id"]) => {
    const linkedTodo = todos.find((todo) => todo.id === event.sourceTodoId);
    if (!linkedTodo) return;
    const detachedTodo: Todo = {
      ...linkedTodo,
      linkedProjectId: undefined,
      linkedEventId: undefined,
      linkedStageId: undefined,
    };
    runWithUndo("已取消待办的项目关联", () => {
      setTodos((items) => items.map((todo) => todo.id === linkedTodo.id ? detachedTodo : todo));
      updateProject(projectId, (project) => ({
        ...project,
        events: (project.events || []).filter((item) => item.id !== event.id),
      }));
    }, () => {
      setTodos((items) => items.map((todo) => todo.id === linkedTodo.id ? linkedTodo : todo));
      updateProject(projectId, (project) => {
        const currentEvents = project.events || [];
        if (currentEvents.some((item) => item.id === event.id)) return project;
        return { ...project, events: [event, ...currentEvents] };
      });
    });
  };
  const removeWorkRecord = (date: string, recordId: string) => {
    const recordIndex = (calendar[date] || []).findIndex((record) => record.id === recordId);
    const removedRecord = calendar[date]?.[recordIndex];
    if (!removedRecord) return;
    setCalendar((items) => {
      const remaining = (items[date] || []).filter((record) => record.id !== recordId);
      if (remaining.length) return { ...items, [date]: remaining };
      const next = { ...items };
      delete next[date];
      return next;
    });
    trashAdd({
      id: String(removedRecord.id || `${date}_${recordId}`),
      type: "workRecord",
      label: removedRecord.content?.slice(0, 30) || "工作记录",
      extra: { date, index: recordIndex },
      payload: removedRecord,
    });
    showToast("工作记录已移到回收站");
  };

  const addQuickRecord = (content: string, linkedProjectId?: string) => {
    const date = localDateKey();
    const record: WorkRecord = {
      id: makeId(),
      ownerId: activeOwnerRef.current,
      content,
      ...(linkedProjectId ? { linkedProjectId } : {}),
      occurredAt: new Date().toTimeString().slice(0, 5),
      createdAt: new Date().toISOString(),
    };
    setCalendar((items) => ({ ...items, [date]: [record, ...(items[date] || [])] }));
    showToast("已记录到工作记录");
  };

  const removeAttendanceAdjustment = (adjustment: AttendanceAdjustment) => {
    const index = (attendance.adjustments || []).findIndex((item) => item.id === adjustment.id);
    setAttendance((data) => ({
      ...data,
      adjustments: (data.adjustments || []).filter((item) => item.id !== adjustment.id),
    }));
    trashAdd({
      id: String(adjustment.id),
      type: "attendanceAdjustment",
      label: `${adjustment.date || ""} 额度变更 ${adjustment.days} 天`,
      extra: { index },
      payload: adjustment,
    });
    showToast("额度变更已移到回收站");
  };

  const handleAttachmentFiles = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    const baseCount = draftAttachments.length;
    let added = 0;
    for (const file of files) {
      if (baseCount + added >= MAX_ATTACHMENTS_PER_RECORD) {
        showToast(`每条工作记录最多保存 ${MAX_ATTACHMENTS_PER_RECORD} 个文件内容`);
        continue;
      }
      const extension = fileExtension(file.name);
      let content: string | null = null;
      if (isSupportedSource(file)) {
        try {
          content = OFFICE_TEXT_FORMATS.has(extension) ? await extractOfficeText(file) : await readTextFile(file);
        } catch (error) {
          showToast(`“${file.name}”读取失败：${error instanceof Error ? error.message : "未知错误"}`);
          continue;
        }
      } else if (LEGACY_OFFICE_FORMATS.has(extension)) {
        try {
          if (await isZipArchive(file)) content = await extractOfficeText(file);
        } catch {
          content = null;
        }
        if (content === null) {
          showToast(`“${file.name}”是旧版二进制格式，无法直接读取；请在 WPS 中“另存为”Word(.docx)，或复制文字后点“粘贴文本”`);
          continue;
        }
      } else if (extension === "pdf") {
        showToast(`“${file.name}”是 PDF，无法直接读取；请复制文字后点“粘贴文本”`);
        continue;
      } else {
        showToast(`“${file.name}”不是可读取的文件，请复制文字后保存`);
        continue;
      }
      if (!content.trim()) {
        showToast(`“${file.name}”没有可保存的文字内容`);
        continue;
      }
      if (content.length > MAX_ATTACHMENT_CHARS) {
        showToast(`“${file.name}”内容过长（超过 ${MAX_ATTACHMENT_CHARS} 字），请精简后保存`);
        continue;
      }
      const attachment: WorkRecordAttachment = {
        id: makeId(),
        name: file.name,
        size: file.size,
        content,
        addedAt: new Date().toISOString(),
      };
      setDraftAttachments((items) => [...items, attachment]);
      added += 1;
    }
  };

  const addPastedAttachment = () => {
    const content = pasteText.trim();
    if (!content) {
      showToast("还没有可保存的文字");
      return;
    }
    if (content.length > MAX_ATTACHMENT_CHARS) {
      showToast(`内容过长（超过 ${MAX_ATTACHMENT_CHARS} 字），请精简`);
      return;
    }
    if (draftAttachments.length >= MAX_ATTACHMENTS_PER_RECORD) {
      showToast(`每条记录最多保存 ${MAX_ATTACHMENTS_PER_RECORD} 个文件内容`);
      return;
    }
    const name = pasteName.trim() || `粘贴文本_${localDateKey()}`;
    setDraftAttachments((items) => [...items, {
      id: makeId(),
      name,
      size: content.length * 2,
      content,
      addedAt: new Date().toISOString(),
    }]);
    setPasteName("");
    setPasteText("");
    setPasteEditorOpen(false);
    showToast("文本已加入文件内容");
  };

  const convertMatterToServiceProject = (matter: Todo) => {
    const project = createProjectFromTemplate({
      name: matter.text,
      kind: "odd",
      projectClass: "service",
      startStageName: matter.done ? undefined : "服务实施",
    });
    project.status = matter.done ? "completed" : "active";
    project.nextAction = matter.stage && !matter.done
      ? { title: matter.stage, status: "todo", note: matter.note }
      : undefined;
    project.events = [{
      id: makeId(),
      occurredOn: matter.createDate?.slice(0, 10) || localDateKey(),
      recordedAt: new Date().toISOString(),
      title: matter.stage || "由原服务事项转入项目",
      note: matter.note,
      status: matter.done ? "done" : "doing",
      category: "other",
    }];
    setProjects((items) => [project, ...items]);
    setTodos((items) => items.filter((item) => item.id !== matter.id));
    setCurrentProjectId(project.id);
    setProjectTab("overview");
    navigate("projectDetail");
    showToast("已转为服务项目，原记录完整带入");
  };

  const calendarCells = useMemo(() => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    // 星期表头从周一开始，因此周一为一周的第一列（getDay() 中周日为 0）。
    const first = (new Date(year, month, 1).getDay() + 6) % 7;
    const count = new Date(year, month + 1, 0).getDate();
    return [
      ...Array.from({ length: first }, () => null),
      ...Array.from({ length: count }, (_, index) => {
        const date = new Date(year, month, index + 1);
        return { day: index + 1, key: localDateKey(date) };
      }),
    ];
  }, [viewDate]);

  const openProject = (id: string | number) => {
    setCurrentProjectId(id);
    setProjectTab("overview");
    navigate("projectDetail");
  };
  const updateProject = (id: string | number, update: (project: Project) => Project) =>
    setProjects((items) => items.map((project) => (String(project.id) === String(id) ? update(project) : project)));
  const updateCurrentProject = (update: (project: Project) => Project) => {
    if (currentProjectId === null) return;
    updateProject(currentProjectId, update);
  };
  const moveProject = (projectId: string | number, targetId: string | number | undefined) =>
    setProjects((items) => swapProjectOrder(items, projectId, targetId));
  const moveStage = (index: number, direction: -1 | 1) =>
    updateCurrentProject((project) => {
      const stages = [...project.stages];
      const target = index + direction;
      if (target < 0 || target >= stages.length) return project;
      [stages[index], stages[target]] = [stages[target], stages[index]];
      return { ...project, stages };
    });

  const getNodeFields = (name: string) => {
    const fields = nodeConfig[name];
    return Array.isArray(fields) ? clone(fields as ConfigField[]) : clone(DEFAULT_CONFIG[name] || []);
  };

  const manualSync = async () => {
    if (!cloud || !user) {
      setModal("account");
      showToast("登录后可以同步到云端");
      return;
    }
    await syncWithCloud(true);
  };

  const runSecurityCheck = async () => {
    if (!cloud) {
      showToast("云端服务尚未连接，无法自检");
      return;
    }
    const report: string[] = [];
    try {
      const anonResponse = await fetch(`${SUPABASE_URL}/rest/v1/user_data?select=id&limit=1`, {
        headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${SUPABASE_KEY}` },
      });
      report.push(anonResponse.ok
        ? "✗ 匿名读取检查未通过：未登录状态竟然能读到 user_data，行级安全可能未启用，请立即检查 Supabase 的 RLS 设置！"
        : "✓ 匿名读取检查通过：未登录（包括拿到公开密钥的陌生人）无法读取任何数据。");
    } catch {
      report.push("− 匿名读取检查无法完成（网络或证书问题），请稍后重试。");
    }
    try {
      const session = (await cloud.auth.getSession()).data.session;
      if (!session) {
        report.push("− 登录态检查：当前未登录，无法验证本人数据读写。");
      } else {
        const accessToken = (session as unknown as { access_token?: string }).access_token;
        if (!accessToken) {
          report.push("− 登录态检查：未能取得访问令牌，跳过本人数据验证。");
        } else {
          const meResponse = await fetch(`${SUPABASE_URL}/rest/v1/user_data?select=user_id&limit=1`, {
            headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${accessToken}` },
          });
          report.push(meResponse.ok
            ? "✓ 本人数据检查通过：登录状态能正常访问自己的数据行。"
            : "✓ 登录态按行级策略校验（请求被拒绝，符合预期）。");
        }
      }
    } catch {
      report.push("− 登录态检查未能完成，请稍后重试。");
    }
    report.push("说明：云端数据在服务器上由 Supabase 平台保管；本机 localStorage 与导出的备份文件为明文。保管好账号密码与备份文件，不要在陌生设备上保持登录。");
    setSecurityReport(report);
    setModal("securityCheck");
  };

  const loadDevices = async () => {
    if (!cloud) return;
    const { data, error } = await cloud.rpc("list_user_devices");
    if (error) {
      console.warn("登录设备读取失败", error.message);
      return;
    }
    setDevices((data as unknown as Array<{ device_id: string; device_name: string; last_seen_at: string; created_at: string; revoked_at?: string | null }>) || []);
  };

  const signOutOtherDevices = async () => {
    if (!cloud) return;
    const deviceId = localStorage.getItem(KEYS.deviceId) || "";
    const { error: revokeError } = await cloud.rpc("revoke_other_devices", { p_current_device_id: deviceId });
    if (revokeError) {
      console.warn("设备拉黑失败", revokeError.message);
    }
    await cloud.auth.signOut({ scope: "others" });
    showToast(revokeError ? "已请求退出其他设备，但即时下线未生效（请先在 Supabase 执行新版 SQL）" : "已退出其他所有设备，对方将立即强制下线");
    void loadDevices();
  };

  const reactivateDevice = async () => {
    const deviceId = localStorage.getItem(KEYS.deviceId) || "";
    if (!cloud || !deviceId) return;
    const { error } = await cloud.rpc("reactivate_device", { p_device_id: deviceId });
    if (error) console.warn("设备重新激活失败", error.message);
  };

  const forceLocalSignOut = useCallback(async () => {
    if (cloud) await cloud.auth.signOut({ scope: "local" });
    setUser(null);
    setGuestMode(false);
    setSyncReady(false);
    syncedUserRef.current = "";
    setSyncLabel("本机保存");
    setModal(null);
    showToast("本设备已被其他设备退出登录，请重新登录");
  }, [cloud]);

  const checkDeviceRevoked = useCallback(async () => {
    if (!cloud || !user) return;
    const deviceId = localStorage.getItem(KEYS.deviceId) || "";
    if (!deviceId) return;
    const { data, error } = await cloud.rpc("is_device_revoked", { p_device_id: deviceId });
    if (error) return;
    if (data === true) await forceLocalSignOut();
  }, [cloud, user, forceLocalSignOut]);

  // 加密门禁：账号云端数据已加密、但本设备没有解锁钥匙时，强制弹解锁页。
  const e2eGateCheck = useCallback(async (userId?: string) => {
    const uid = userId || user?.id;
    if (!cloud || !uid) return;
    if (pendingRecovery) return;
    if (readE2ESettings(uid)) return;
    const { data, error } = await cloud.from("user_data").select("content").eq("user_id", uid).maybeSingle();
    if (error || !data?.content) return;
    if (isE2EEnvelope(data.content)) setPendingRecovery(true);
  }, [cloud, user, pendingRecovery]);

  // 实时推送：后台给本设备打上拉黑标记时，在线设备立即收到并强制退出。
  useEffect(() => {
    if (!cloud || !user) return;
    const deviceId = localStorage.getItem(KEYS.deviceId) || "";
    const channel = cloud
      .channel(`user-devices-${user.id}`)
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "user_devices", filter: `user_id=eq.${user.id}` },
        (payload) => {
          const row = (payload.new || {}) as { device_id?: string; revoked_at?: string | null };
          if (row.device_id === deviceId && row.revoked_at) void forceLocalSignOut();
        },
      )
      .subscribe();
    return () => { void cloud.removeChannel(channel); };
  }, [cloud, user, forceLocalSignOut]);

  // 实时推送：账号数据变为加密（或更新）时，已登录但未解锁的设备立即弹解锁页。
  useEffect(() => {
    if (!cloud || !user) return;
    const channel = cloud
      .channel(`user-data-${user.id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "user_data", filter: `user_id=eq.${user.id}` },
        () => {
          void e2eGateCheck(user.id);
          // 云端数据变化（本机或他机写入）时立即触发同步，替代原来的定时轮询。
          // 自己上传后的回显事件在 1.5 秒内跳过，避免多余的同步往返。
          if (Date.now() - lastSyncUploadAtRef.current > 1500) void syncWithCloud(false);
        },
      )
      .subscribe();
    return () => { void cloud.removeChannel(channel); };
  }, [cloud, e2eGateCheck, syncWithCloud, user]);

  // 打开应用、切回前台时做一次性核对，弥补推送断线期间被拉黑的情况（无定时轮询）。
  useEffect(() => {
    if (!user) return;
    void checkDeviceRevoked();
    void e2eGateCheck();
    const onVisible = () => { if (document.visibilityState === "visible") { void checkDeviceRevoked(); void e2eGateCheck(); } };
    const onFocus = () => { void checkDeviceRevoked(); void e2eGateCheck(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
    };
  }, [user, checkDeviceRevoked, e2eGateCheck]);

  const enableE2E = async () => {
    if (!cloud || !user) {
      showToast("请先登录云端账号");
      return;
    }
    const password = e2ePassword.trim();
    if (!password) {
      showToast("请输入登录密码");
      return;
    }
    await syncWithCloud(false);
    const { error: verifyError } = await cloud.auth.signInWithPassword({
      email: user.email || "",
      password,
    });
    if (verifyError) {
      showToast("密码验证失败，请重试");
      return;
    }
    const recoveryCode = generateRecoveryCode();
    const payload: CloudPayload = {
      ...clone(latestDataRef.current),
      owner_id: user.id,
      updated_at: new Date().toISOString(),
      sync_meta: clone(syncMetaRef.current),
    };
    const envelope = await encryptPayload(payload, password, recoveryCode);
    const { error: uploadError } = await cloud.from("user_data").upsert({ user_id: user.id, content: envelope });
    if (uploadError) {
      showToast(`开启失败：${uploadError.message}`);
      return;
    }
    const masterKey = await unwrapMasterKey(envelope, password);
    if (!masterKey) {
      showToast("密钥生成失败，请重试");
      return;
    }
    const rawMaster = new Uint8Array(await crypto.subtle.exportKey("raw", masterKey));
    const settings: E2ESettings = {
      user_id: user.id,
      enabled: true,
      masterKey: toBase64(rawMaster),
      header: { pw: envelope.pw, rc: envelope.rc },
    };
    localStorage.setItem(E2E_KEY, JSON.stringify(settings));
    setE2ESettings(settings);
    setE2ePassword("");
    setRecoveryCodeShown(recoveryCode);
    localStorage.setItem(ownerStorageKeys(user.id).lastSync, String(Date.now()));
    setSyncLabel(syncedLabel(Date.now()));
    setModal("recoveryCode");
    showToast("端到端加密已开启，请抄下恢复码");
  };

  const unlockE2E = async () => {
    if (!cloud || !user) {
      showToast("请先登录云端账号");
      return;
    }
    const { data, error } = await cloud.from("user_data").select("content").eq("user_id", user.id).maybeSingle();
    if (error || !data?.content || !isE2EEnvelope(data.content)) {
      showToast("云端没有需要解锁的加密数据");
      return;
    }
    const envelope = data.content as E2EEnvelope;
    const password = e2ePassword.trim();
    const code = normalizeRecoveryCode(recoveryInput);
    let masterKey = password ? await unwrapMasterKey(envelope, password) : null;
    if (!masterKey && code) masterKey = await unwrapMasterKey(envelope, undefined, code);
    if (!masterKey) {
      showToast("密码或恢复码不正确");
      return;
    }
    const rawMaster = new Uint8Array(await crypto.subtle.exportKey("raw", masterKey));
    let header: E2ESettings["header"] = { pw: envelope.pw, rc: envelope.rc };
    if (!password) {
      const newPassword = recoveryPassword.trim();
      if (!newPassword) {
        showToast("使用恢复码解锁后，请输入新的登录密码重新绑定");
        return;
      }
      const pwSalt = randomBytes(16);
      const pwKey = await deriveE2EKey(newPassword, pwSalt);
      const pwWrap = await aesEncrypt(pwKey, rawMaster);
      header = { pw: { salt: toBase64(pwSalt), ...pwWrap }, rc: envelope.rc };
      const payload = await decryptEnvelope(envelope, masterKey);
      const newEnvelope = await encryptPayloadWithKey(payload, masterKey, header);
      const { error: uploadError } = await cloud.from("user_data").upsert({ user_id: user.id, content: newEnvelope });
      if (uploadError) {
        showToast(`重新加密上传失败：${uploadError.message}`);
        return;
      }
    }
    const settings: E2ESettings = {
      user_id: user.id,
      enabled: true,
      masterKey: toBase64(rawMaster),
      header,
    };
    localStorage.setItem(E2E_KEY, JSON.stringify(settings));
    setE2ESettings(settings);
    setPendingRecovery(false);
    setE2ePassword("");
    setRecoveryInput("");
    setRecoveryPassword("");
    setModal(null);
    showToast("加密数据已解锁");
    void syncWithCloud(false);
  };

  const captureGuestPending = () => {
    const deviceId = localStorage.getItem(KEYS.deviceId) || "";
    const guestData = readOwnerSnapshot(GUEST_OWNER, deviceId).data;
    const guestSummary = {
      projects: guestData.projects.length,
      todos: guestData.todo.length,
      workRecords: Object.values(guestData.calendar).reduce((sum, records) => sum + records.length, 0),
      overtime: guestData.attendance.records.length,
      leave: guestData.attendance.leaveRecords.length,
    };
    setGuestPendingSummary(
      guestSummary.projects || guestSummary.todos || guestSummary.workRecords || guestSummary.overtime || guestSummary.leave
        ? guestSummary
        : null);
  };

  const switchAuthMode = (mode: "login" | "register" | "forgot" | "otp") => {
    setAuthMode(mode);
    setAuthError("");
    setOtpSent(false);
    setOtpCode("");
  };

  const handleAuth = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!cloud) {
      setAuthError("云端服务尚未连接，请稍后再试。");
      return;
    }
    const form = new FormData(event.currentTarget);
    const email = String(form.get("email") || "").trim();
    const password = String(form.get("password") || "");
    setAuthError("");
    const result =
      authMode === "login"
        ? await cloud.auth.signInWithPassword({ email, password })
        : await cloud.auth.signUp({ email, password });
    if (result.error) {
      setAuthError(result.error.message);
      return;
    }
    if (!result.data.user) {
      setAuthError("注册成功，请完成邮箱确认后再登录。");
      return;
    }
    if (authMode === "register" && "session" in result.data && !result.data.session) {
      setAuthError("注册成功，请先到邮箱完成确认，再返回登录。");
      setAuthMode("login");
      return;
    }
    setGuestMode(false);
    setUser(result.data.user);
    void reactivateDevice();
    void e2eGateCheck(result.data.user.id);
    captureGuestPending();
    setModal(null);
  };

  const handleForgotPassword = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!cloud) {
      setAuthError("云端服务尚未连接，请稍后再试。");
      return;
    }
    const form = new FormData(event.currentTarget);
    const email = String(form.get("email") || "").trim();
    if (!email) return;
    setAuthError("");
    const { error } = await cloud.auth.resetPasswordForEmail({ email });
    if (error) {
      setAuthError(error.message);
      return;
    }
    setAuthMode("login");
    showToast("重置邮件已发送，请查收邮箱并按邮件里的链接设置新密码");
  };

  const handleOtpSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!cloud) {
      setAuthError("云端服务尚未连接，请稍后再试。");
      return;
    }
    const email = otpEmail.trim();
    if (!email) {
      setAuthError("请输入邮箱");
      return;
    }
    if (!otpSent) {
      setAuthError("");
      const { error } = await cloud.auth.signInWithOtp({ email });
      if (error) {
        setAuthError(error.message);
        return;
      }
      setOtpSent(true);
      showToast("验证码已发送，请查收邮箱");
      return;
    }
    const token = otpCode.trim();
    if (!token) {
      setAuthError("请输入验证码");
      return;
    }
    setAuthError("");
    const { data, error } = await cloud.auth.verifyOtp({ email, token, type: "email" });
    if (error || !data.user) {
      setAuthError(error?.message || "验证码无效或已过期");
      return;
    }
    setGuestMode(false);
    setUser(data.user);
    void reactivateDevice();
    void e2eGateCheck(data.user.id);
    captureGuestPending();
    setModal(null);
    setOtpEmail("");
    setOtpCode("");
    setOtpSent(false);
  };

  const handleChangeEmail = async () => {
    if (!cloud || !user) {
      showToast("请先登录云端账号");
      return;
    }
    const email = newEmail.trim();
    if (!email) {
      showToast("请输入新邮箱");
      return;
    }
    if (!changeEmailPassword) {
      showToast("请输入当前密码确认身份");
      return;
    }
    const { error: verifyError } = await cloud.auth.signInWithPassword({
      email: user.email || "",
      password: changeEmailPassword,
    });
    if (verifyError) {
      showToast("密码验证失败，请重试");
      return;
    }
    const { error } = await cloud.auth.updateUser({ email });
    if (error) {
      showToast(`更换邮箱失败：${error.message}`);
      return;
    }
    setNewEmail("");
    setChangeEmailPassword("");
    showToast("确认邮件已发送到新邮箱，请点击邮件中的链接完成更换");
  };

  const logout = async () => {
    await cloud?.auth.signOut();
    setUser(null);
    setGuestMode(false);
    setSyncReady(false);
    syncedUserRef.current = "";
    setSyncLabel("本机保存");
    setModal(null);
    showToast("已安全退出");
  };

  const exportData = async (encrypted = false) => {
    const payload = backupPayload(latestDataRef.current);
    let content: string;
    if (encrypted) {
      const password = backupPassword.trim();
      if (!password) {
        showToast("请输入备份密码");
        return;
      }
      content = JSON.stringify(await encryptBackup(payload, password), null, 2);
    } else {
      content = JSON.stringify(payload, null, 2);
    }
    const url = URL.createObjectURL(new Blob([content], { type: "application/json" }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `工作助手备份_${localDateKey()}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
    localStorage.setItem("workassistant_last_backup_at", new Date().toISOString());
    setLastBackupAt(new Date().toISOString());
    showToast("备份文件已生成");
  };

  const exportMigrationBackup = () => {
    const raw = localStorage.getItem(KEYS.migrationBackup);
    if (!raw) {
      showToast("这台设备没有需要迁移的旧版数据");
      return;
    }
    const url = URL.createObjectURL(new Blob([raw], { type: "application/json" }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `工作助手_V3升级前备份_${localDateKey()}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
    showToast("升级前备份已下载");
  };

  const exportPreImportBackup = () => {
    const raw = localStorage.getItem(ownerStorageKeys(activeOwnerRef.current).preImportBackup);
    if (!raw) {
      showToast("当前账号还没有导入前备份");
      return;
    }
    const url = URL.createObjectURL(new Blob([raw], { type: "application/json" }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `工作助手_最近导入前备份_${localDateKey()}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
    showToast("导入前备份已下载");
  };

  const exportPreSyncBackup = () => {
    const raw = localStorage.getItem(ownerStorageKeys(activeOwnerRef.current).preSyncBackup);
    if (!raw) {
      showToast("当前账号还没有同步前备份");
      return;
    }
    const url = URL.createObjectURL(new Blob([raw], { type: "application/json" }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `工作助手_最近同步前备份_${localDateKey()}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
    showToast("同步前备份已下载");
  };

  const importData = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const parsed = JSON.parse(await file.text()) as Record<string, unknown>;
      if (parsed && typeof parsed === "object" && parsed.v === "backup1") {
        setPendingEncryptedBackup({ raw: parsed as { salt: string; iv: string; value: string }, fileName: file.name });
        setBackupPassword("");
        setModal("backupPassword");
        return;
      }
      const prepared = prepareBackupImport(parsed, latestDataRef.current);
      setPendingImport({ ...prepared, fileName: file.name });
      setModal("importPreview");
    } catch {
      showToast("备份文件无法识别");
    } finally {
      event.target.value = "";
    }
  };

  const confirmDecryptBackup = async () => {
    if (!pendingEncryptedBackup) return;
    const password = backupPassword.trim();
    if (!password) {
      showToast("请输入备份密码");
      return;
    }
    try {
      const payload = await decryptBackup(pendingEncryptedBackup.raw, password);
      const prepared = prepareBackupImport(payload, latestDataRef.current);
      setPendingImport({ ...prepared, fileName: pendingEncryptedBackup.fileName });
      setPendingEncryptedBackup(null);
      setBackupPassword("");
      setModal("importPreview");
    } catch {
      showToast("密码不正确或文件已损坏");
    }
  };

  const openGuestImport = () => {
    const deviceId = localStorage.getItem(KEYS.deviceId) || "";
    const guestData = readOwnerSnapshot(GUEST_OWNER, deviceId).data;
    try {
      const prepared = prepareBackupImport(
        backupPayload(guestData, "访客空间数据导入当前账号"),
        latestDataRef.current,
      );
      setPendingImport({ ...prepared, fileName: "访客空间数据" });
      setModal("importPreview");
    } catch {
      showToast("访客空间没有可导入的数据");
    }
  };

  const confirmImport = () => {
    if (!pendingImport) return;
    const owner = activeOwnerRef.current;
    const ownerKeys = ownerStorageKeys(owner);
    const importedData = claimImportedRecords(pendingImport.merged, owner);
    localStorage.setItem(
      ownerKeys.preImportBackup,
      JSON.stringify(backupPayload(latestDataRef.current, `导入 ${pendingImport.fileName} 前自动保存`), null, 2),
    );
    setProjects(importedData.projects);
    setTodos(importedData.todo);
    setAttendance(importedData.attendance);
    setCalendar(importedData.calendar);
    setNodeConfig(importedData.config);
    setPendingImport(null);
    setGuestPendingSummary(null);
    setModal(null);
    showToast("备份已合并，导入前数据已自动保存");
  };

  const renderCalendar = (compact = false) => (
    <section className={`panel calendar-panel ${compact ? "compact" : ""}`}>
      <div className="panel-heading">
        <div>
          <h2>{viewDate.getFullYear()}年{viewDate.getMonth() + 1}月</h2>
        </div>
        <div className="button-row">
          <button className="icon-button" type="button" aria-label="上个月" onClick={() => setViewDate((date) => new Date(date.getFullYear(), date.getMonth() - 1, 1))}>‹</button>
          <button className="text-button" type="button" onClick={() => setViewDate(new Date())}>今天</button>
          <button className="icon-button" type="button" aria-label="下个月" onClick={() => setViewDate((date) => new Date(date.getFullYear(), date.getMonth() + 1, 1))}>›</button>
        </div>
      </div>
      <div className="calendar-grid weekday-row">
        {["一", "二", "三", "四", "五", "六", "日"].map((day) => <span key={day}>{day}</span>)}
      </div>
      <div className="calendar-grid">
        {calendarCells.map((cell, index) => {
          if (!cell) return <span key={`blank_${index}`} />;
          const projectEventCount = calendarProjectEventsForDate(cell.key).length;
          const todoCount = activeTodos.filter((todo) => todo.scheduledDate === cell.key).length;
          const markedCount = projectEventCount + todoCount;
          const hasWorkRecord = (calendar[cell.key] || []).length > 0;
          const hasLeave = leaveCalendarKeys.has(cell.key);
          return (
            <button
              key={cell.key}
              type="button"
              className={`calendar-day ${cell.key === localDateKey() ? "today" : ""} ${hasWorkRecord ? "has-work-record" : ""} ${hasLeave ? "has-leave" : ""} ${markedCount > 0 ? "has-schedule" : ""}`}
              onClick={() => {
                setSelectedLogDate(cell.key);
                setModal("dayOverview");
              }}
              aria-label={`${cell.key}${hasWorkRecord ? "，有工作记录" : ""}${hasLeave ? "，有休假" : ""}${markedCount ? `，有${markedCount}项日程` : ""}`}
            >
              {cell.day}
              {markedCount > 0 && <small>{markedCount}</small>}
            </button>
          );
        })}
      </div>
      <div className="calendar-key" aria-label="日历标记说明"><span><i className="work-record" />工作记录</span><span><i className="leave-mark" />休假</span><span><i className="schedule-count">1</i>日程数</span><span><i className="today-mark" />今天</span></div>
    </section>
  );

  const renderHome = () => {
    const now = new Date();
    const visibleProjects = projects.filter((project) => project.status !== "trashed");
    const focusProjects = visibleProjects.slice(0, 3);
    const overdueTodos = activeTodos.filter((todo) => Boolean(todo.scheduledDate && todo.scheduledDate < localDateKey()));
    const otherTodos = activeTodos.filter((todo) => !todo.scheduledDate || todo.scheduledDate > localDateKey());
    const renderTodoRow = (todo: Todo) => {
      const originalIndex = todos.indexOf(todo);
      const linkedProject = todo.linkedProjectId
        ? projects.find((project) => String(project.id) === todo.linkedProjectId)
        : null;
      return (
        <div className="todo-item" key={todo.id || `${todo.text}_${originalIndex}`}>
          <button className="check-button" type="button" onClick={() => toggleTodo(originalIndex)} aria-label={`完成待办：${todo.text}`} />
          <span>
            {todo.text}
            {todo.scheduledDate && <small className={`todo-date ${todo.scheduledDate < localDateKey() ? "overdue" : ""}`}>{todo.scheduledDate === localDateKey() ? "今天" : todo.scheduledDate}{todo.scheduledDate < localDateKey() ? " 已逾期" : ""}</small>}
            {linkedProject && <small className="todo-project">{linkedProject.name}</small>}
          </span>
          <details className="row-menu">
            <summary aria-label={`${todo.text}的编辑功能`}>•••</summary>
            <div>
              <button type="button" onClick={() => { setEditingTodoId(todoKey(todo)); setModal("todoEdit"); }}>编辑</button>
              <button className="danger-text" type="button" onClick={() => removeTodo(originalIndex)}>删除</button>
            </div>
          </details>
        </div>
      );
    };
    return (
      <>
        <section className="hero">
          <div>
            <span className="hero-date">
              {now.toLocaleDateString("zh-CN", { year: "numeric", month: "long", day: "numeric", weekday: "long" })}
            </span>
            <h1>{todayTodos.length ? `今天还有 ${todayTodos.length} 件事需要关注` : "今日日程"}</h1>
          </div>
        </section>

        <form className="quick-record-bar" onSubmit={(event) => {
          event.preventDefault();
          const form = new FormData(event.currentTarget);
          const content = String(form.get("quickRecord") || "").trim();
          if (!content) return;
          const projectId = String(form.get("quickRecordProject") || "").trim() || undefined;
          addQuickRecord(content, projectId);
          event.currentTarget.reset();
        }}>
          <input name="quickRecord" type="text" placeholder="快速记录：今天做了什么…" aria-label="快速记录内容" autoComplete="off" />
          <select name="quickRecordProject" aria-label="关联项目">
            <option value="">不关联项目</option>
            {visibleProjects.map((project) => <option key={project.id} value={String(project.id)}>{project.name}</option>)}
          </select>
          <button className="primary-button" type="submit">记录</button>
        </form>

        <section className="metric-grid">
          <button className="metric-card accent" type="button" onClick={() => navigate("projects")}>
            <span>在管项目</span><strong>{visibleProjects.length}</strong>
          </button>
          <button className="metric-card" type="button" onClick={() => navigate("home")}>
            <span>未完成待办</span><strong>{activeTodos.length}</strong><small>{doneTodos.length} 项已归档</small>
          </button>
          <button className="metric-card" type="button" onClick={() => navigate("attendance")}>
            <span>可休总天数</span><strong>{leaveSummary.total}</strong>
          </button>
        </section>

        <div className="dashboard-grid">
          <section className="panel todo-panel">
            <div className="panel-heading">
              <div><h2>我的待办</h2></div>
              <div className="heading-actions">
                <button className="text-button" type="button" onClick={() => setModal("todoHistory")}>历史归档</button>
                <button className="primary-button small" type="button" onClick={() => setModal("todoCreate")}>＋ 新增待办</button>
              </div>
            </div>
            {overdueTodos.length > 0 && (
              <div className="schedule-block">
                <div className="subsection-heading"><h3>已逾期</h3><span>{overdueTodos.length}</span></div>
                <div className="todo-list">{overdueTodos.map(renderTodoRow)}</div>
              </div>
            )}
            <div className="schedule-block">
              <div className="subsection-heading"><h3>今日日程</h3><span>{todayTodos.length}</span></div>
              {todayTodos.length
                ? <div className="todo-list schedule-list">{todayTodos.map(renderTodoRow)}</div>
                : <p className="compact-empty">今天没有安排日期的待办</p>}
            </div>
            <div className="schedule-block">
              <div className="subsection-heading"><h3>待办事项</h3><span>{otherTodos.length}</span></div>
              <div className="todo-list">
                {otherTodos.length ? otherTodos.slice(0, 6).map(renderTodoRow) : <EmptyState title="暂无其他待办" />}
              </div>
            </div>
          </section>
          {renderCalendar(true)}
        </div>

        <section className="panel">
          <div className="panel-heading">
            <div><h2>项目</h2></div>
            <button className="text-button" type="button" onClick={() => navigate("projects")}>查看全部</button>
          </div>
          {focusProjects.length ? (
            <div className="focus-projects">
              {focusProjects.map((project) => {
                const progress = projectProgress(project);
                const next = getSuggestedNextAction(project);
                return (
                  <button className="focus-project" key={project.id} type="button" onClick={() => openProject(project.id)}>
                    <span className={`project-type ${project.type}`}>{projectTypeLabel(project)}</span>
                    <strong>{project.name}</strong>
                    <div className="progress-line"><span style={{ width: `${progress.percent}%` }} /></div>
                    <small>{next ? `下一步：${next.title}` : `${progress.done}/${progress.total} 个节点完成`}</small>
                  </button>
                );
              })}
            </div>
          ) : <EmptyState title="还没有项目" />}
        </section>
      </>
    );
  };

  const renderProjects = () => {
    const visibleProjects = projects.filter((project) => project.status !== "trashed");
    const trashedCount = projects.length - visibleProjects.length;
    const projectGroups = [
      { key: "special", label: "专项资金工程", match: (project: Project) => project.projectClass !== "service" && project.type === "special" },
      { key: "internal", label: "内部资金工程", match: (project: Project) => project.projectClass !== "service" && (project.type === "odd" || project.type === "repair") },
      { key: "serviceSpecial", label: "专项资金服务", match: (project: Project) => project.projectClass === "service" && project.type === "special" },
      { key: "serviceInternal", label: "内部资金服务", match: (project: Project) => project.projectClass === "service" && (project.type === "odd" || project.type === "repair") },
    ];
    const activeProjectGroups = projectBrowserMode === "root"
      ? []
      : projectGroups.filter((group) => group.key === projectBrowserMode);
    const countFor = (match: (project: Project) => boolean) => visibleProjects.filter(match).length;
    const engineeringCount = countFor((project) => project.projectClass !== "service");
    const serviceCount = countFor((project) => project.projectClass === "service");
    const specialCount = countFor((project) => project.projectClass !== "service" && project.type === "special");
    const internalCount = countFor((project) => project.projectClass !== "service" && (project.type === "odd" || project.type === "repair"));
    const serviceSpecialCount = countFor((project) => project.projectClass === "service" && project.type === "special");
    const serviceInternalCount = countFor((project) => project.projectClass === "service" && (project.type === "odd" || project.type === "repair"));
    return (
      <>
        <section className="page-title-row project-page-title">
          <div><h1>项目</h1></div>
          <div className="button-row">
            <button className="secondary-button" type="button" onClick={() => setModal("projectRecycleBin")}>回收站{trashedCount ? ` ${trashedCount}` : ""}</button>
            <button className="secondary-button" type="button" onClick={() => setModal("nodeLibrary")}>流程模板</button>
            <button className="primary-button" type="button" onClick={() => setModal("createProject")}>＋ 新建项目</button>
          </div>
        </section>
        {projectBrowserMode === "root" && (
          <section className="project-category-screen" aria-label="项目分类">
            <div className="project-category-grid">
              <article className="project-category-card project-category-card-expanded">
                <div><h3>工程项目</h3></div>
                <strong>{engineeringCount}</strong>
                <div className="project-category-subactions"><button type="button" onClick={() => setProjectBrowserMode("special")}><span>专项资金工程</span><strong>{specialCount}</strong></button><button type="button" onClick={() => setProjectBrowserMode("internal")}><span>内部资金工程</span><strong>{internalCount}</strong></button></div>
              </article>
              <article className="project-category-card project-category-card-expanded service-card-category">
                <div><h3>服务项目</h3></div>
                <strong>{serviceCount}</strong>
                <div className="project-category-subactions"><button type="button" onClick={() => setProjectBrowserMode("serviceSpecial")}><span>专项资金服务</span><strong>{serviceSpecialCount}</strong></button><button type="button" onClick={() => setProjectBrowserMode("serviceInternal")}><span>内部资金服务</span><strong>{serviceInternalCount}</strong></button></div>
              </article>
            </div>
          </section>
        )}
        {activeProjectGroups.map((group) => {
          const typedProjects = projects
            .filter((project) => group.match(project) && project.status !== "trashed");
          return (
            <section className={`project-section project-section-v3 project-group-${group.key}`} key={group.key}>
              <div className="section-title">
                <div className="project-group-heading"><button className="apple-back-button project-list-back" type="button" onClick={() => setProjectBrowserMode("root")}><span aria-hidden="true">‹</span>项目</button><h2>{group.label}</h2></div>
                <span>{typedProjects.length}</span>
              </div>
              {typedProjects.length ? (
                <div className="project-card-list">
                  {typedProjects.map((project, groupIndex) => {
                    const progress = projectProgress(project);
                    const next = getSuggestedNextAction(project);
                    const last = getProjectLastEvent(project);
                    const funds = fundingSummary(project);
                    return (
                      <article className="project-card-v3" key={project.id}>
                        <button className="project-card-open" type="button" onClick={() => openProject(project.id)}>
                          <h3>{project.name}</h3>
                          <div className="project-card-top">
                            <span className={`project-state ${project.status || "active"}`}>{project.status === "completed" ? "已完成" : project.status === "paused" ? "已暂停" : "推进中"}</span>
                          </div>
                          <div className="project-card-next">
                            <small>当前下一步</small>
                            <strong>{next?.title || "暂未设置"}</strong>
                            {(next?.dueDate || next?.waitingFor || next?.note) && <span>{next?.dueDate ? `截止 ${next.dueDate}` : next?.waitingFor ? `待外部反馈：${next.waitingFor}` : next?.note}</span>}
                          </div>
                          <div className="project-card-meta">
                            <span><small>最近记录</small>{last ? `${last.occurredOn} · ${last.title}` : "暂无事件"}</span>
                            <span className="money-value"><small>资金到账</small>{funds.received ? `${funds.received.toLocaleString("zh-CN")} 元` : "未填写"}</span>
                          </div>
                          <div className="project-progress-row">
                            <div className="progress-line"><span style={{ width: `${progress.percent}%` }} /></div>
                            <small>{progress.done}/{progress.total} 节点</small>
                          </div>
                        </button>
                        <div className="project-card-controls">
                          <div className="stage-move-controls" aria-label={`调整${project.name}的位置`}>
                            <button
                              type="button"
                              disabled={groupIndex === 0}
                              onClick={() => moveProject(project.id, typedProjects[groupIndex - 1]?.id)}
                              aria-label="上移项目"
                            >↑</button>
                            <button
                              type="button"
                              disabled={groupIndex === typedProjects.length - 1}
                              onClick={() => moveProject(project.id, typedProjects[groupIndex + 1]?.id)}
                              aria-label="下移项目"
                            >↓</button>
                          </div>
                          <details className="row-menu project-card-menu">
                            <summary aria-label={`${project.name}更多操作`}>•••</summary>
                            <div>
                              <button className="danger-text" type="button" onClick={(event) => {
                                event.currentTarget.closest("details")?.removeAttribute("open");
                                if (window.confirm(`确定把“${project.name}”移到回收站吗？此操作会同步到其他设备。`)) {
                                  updateProject(project.id, (item) => ({ ...item, status: "trashed" }));
                                  showToast("项目已移到回收站");
                                }
                              }}>移到回收站</button>
                            </div>
                          </details>
                        </div>
                      </article>
                    );
                  })}
                </div>
              ) : <EmptyState title={`暂无${group.label}`} />}
            </section>
          );
        })}
      </>
    );
  };

  const renderProjectDetail = () => {
    if (!currentProject) return <EmptyState title="未找到项目" />;
    const projectTodos = todos.filter((todo) =>
      (!todo.kind || todo.kind === "todo") && !todo.done &&
      todo.linkedProjectId && String(todo.linkedProjectId) === String(currentProject.id));
    const progress = projectProgress(currentProject);
    const next = getSuggestedNextAction(currentProject);
    const rules = projectRules(currentProject);
    const funds = fundingSummary(currentProject);
    const events = [...(currentProject.events || [])].sort((a, b) =>
      `${b.occurredOn}_${b.recordedAt}`.localeCompare(`${a.occurredOn}_${a.recordedAt}`),
    );
    const filteredStage = currentProject.stages.find((stage) => stage.id === stageEventFilterId);
    const visibleEvents = filteredStage
      ? events.filter((event) => event.sourceStageId === filteredStage.id)
      : events;
    return (
      <>
        <section className="detail-head project-detail-head">
          <div className="project-detail-hero">
            <div>
              <div className="project-detail-labels">
                <button className="apple-back-button project-back-button" type="button" onClick={goBack}><span aria-hidden="true">‹</span>项目</button>
                <span className={`project-type ${currentProject.type}`}>{projectTypeLabel(currentProject)}</span>
                {currentProject.projectClass !== "service" && currentProject.heritage && <span className="heritage-badge">文物口</span>}
                {currentProject.fundingMode === "mixed" && <span className="mixed-badge">拼盘资金</span>}
              </div>
              <h1>{currentProject.name}</h1>
              <p>{progress.done}/{progress.total} 个流程节点已完成 · {events.length} 条事件记录</p>
            </div>
            <div className="button-row project-primary-actions">
              <button className="secondary-button project-sheet-button" type="button" onClick={() => {
                if (!currentProject.fundingWorksheet) {
                  const entries = (currentProject.procurements || []).flatMap((procurement) =>
                    (procurement.contracts || []).map((contract) => ({ name: contract.name || procurement.name })));
                  updateCurrentProject((project) => ({
                    ...project,
                    fundingWorksheet: createFundingWorksheet(entries.length ? entries : (project.services || []).map((service) => ({ name: service.name }))),
                  }));
                }
                setSelectedFundingCell("A1");
                setProjectTab("sheet");
              }}>资金测算</button>
              <button className="secondary-button" type="button" onClick={() => setModal("nextAction")}>设置下一步</button>
              <button className="primary-button" type="button" onClick={() => { setEditingProjectEventId(""); setModal("projectEvent"); }}>＋ 记一件事</button>
              <button className="icon-button" type="button" aria-label="项目设置" onClick={() => setModal("projectSettings")}>•••</button>
            </div>
          </div>
          <div className="segmented project-tabs" role="tablist" aria-label="项目详情栏目">
            {([
              ["overview", "总览"],
              ["timeline", "事件记录"],
              ["workflow", "阶段计划"],
              ["funds", "资金合同"],
              ["services", "参建单位"],
            ] as const).map(([id, label]) => (
              <button key={id} role="tab" aria-selected={projectTab === id} className={projectTab === id ? "active" : ""} type="button" onClick={() => {
                setProjectTab(id);
                if (id === "timeline") setStageEventFilterId("");
              }}>{label}</button>
            ))}
          </div>
        </section>

        {projectTab === "overview" && (
          <div className="project-overview-v3">
            <section className={`next-action-card ${next?.status || "empty"}`}>
              <div><h2>{next?.title || "还没有设置下一步"}</h2></div>
              {next ? (
                <div className="next-action-detail">
                  <span>{next.status === "waiting" ? "待外部反馈" : next.status === "paused" ? "已暂停" : "待处理"}</span>
                  {next.dueDate && <span>截止 {next.dueDate}</span>}
                  {next.waitingFor && <span>待 {next.waitingFor} 反馈</span>}
                  {next.note && <p>{next.note}</p>}
                </div>
              ) : null}
              <button type="button" onClick={() => setModal("nextAction")}>{next ? "调整下一步" : "现在设置"}</button>
            </section>
            <section className="project-money-strip">
              <button type="button" onClick={() => setProjectTab("funds")}><span>{currentProject.projectClass === "service" ? "项目预算" : "预算评审"}</span><strong className="money-value">{safeNumber(currentProject.budgetReviewAmount) ? `${safeNumber(currentProject.budgetReviewAmount).toLocaleString("zh-CN")} 元` : "待填写"}</strong></button>
              <button type="button" onClick={() => setProjectTab("funds")}><span>实际到账</span><strong className="money-value">{funds.received ? `${funds.received.toLocaleString("zh-CN")} 元` : "待填写"}</strong></button>
              <button type="button" onClick={() => setProjectTab("funds")}><span>已支付</span><strong className="money-value">{funds.paid ? `${funds.paid.toLocaleString("zh-CN")} 元` : "0 元"}</strong></button>
              <button type="button" onClick={() => setProjectTab("funds")}><span>未支付合同金额</span><strong className="money-value">{funds.unpaid ? `${funds.unpaid.toLocaleString("zh-CN")} 元` : "0 元"}</strong></button>
            </section>
            {projectTodos.length > 0 && (
              <section className="panel project-todo-panel">
                <div className="panel-heading">
                  <div><h2>项目待办</h2></div>
                  <div className="heading-actions"><span className="matter-list-count">{projectTodos.length} 项</span><button className="text-button" type="button" onClick={() => navigate("home")}>查看全部</button></div>
                </div>
                <div className="todo-list">
                  {projectTodos.map((todo) => {
                    const originalIndex = todos.findIndex((item) => todoKey(item) === todoKey(todo));
                    return (
                      <div className="todo-item" key={todoKey(todo)}>
                        <button className="check-button" type="button" onClick={() => completeTodo(todoKey(todo))} aria-label={`完成待办：${todo.text}`} />
                        <span>{todo.text}{todo.scheduledDate && <small className={`todo-date ${todo.scheduledDate < localDateKey() ? "overdue" : ""}`}>{todo.scheduledDate === localDateKey() ? "今天" : todo.scheduledDate}{todo.scheduledDate < localDateKey() ? " 已逾期" : ""}</small>}</span>
                        <details className="row-menu">
                          <summary aria-label={`${todo.text}的编辑功能`}>•••</summary>
                          <div>
                            <button type="button" onClick={() => { setEditingTodoId(todoKey(todo)); setModal("todoEdit"); }}>编辑</button>
                            <button className="danger-text" type="button" onClick={() => removeTodo(originalIndex)}>删除</button>
                          </div>
                        </details>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}
            <div className="detail-grid overview-grid">
              <section className="panel">
                <div className="panel-heading"><div><h2>项目动态</h2></div><button className="text-button" type="button" onClick={() => setProjectTab("timeline")}>查看全部</button></div>
                {events.length ? <div className="compact-event-list">{events.slice(0, 5).map((event) => (
                  <div key={event.id}><time>{event.occurredOn}</time><span className={`event-category ${event.category}`}>{EVENT_CATEGORY_LABEL[event.category]}</span><strong>{event.title}</strong><small>{event.organization || event.note || EVENT_STATUS_LABEL[event.status]}</small></div>
                ))}</div> : <EmptyState title="还没有事件记录" />}
              </section>
              <aside className="panel rule-panel">
                <div className="panel-heading"><div><h2>规则提醒</h2></div></div>
                {rules.length ? <div className="rule-list">{rules.map((rule) => <article className={rule.tone} key={rule.id}><strong>{rule.title}</strong><p>{rule.detail}</p></article>)}</div> : <EmptyState title="暂无规则" />}
              </aside>
            </div>
          </div>
        )}

        {projectTab === "timeline" && (
          <section className="panel event-panel">
            <div className="panel-heading">
              <div>
                <h2>{filteredStage ? `${filteredStage.name} · 事件记录` : "事件记录"}</h2>
                {filteredStage && <p className="section-subtitle">只显示归入这个阶段的事件。</p>}
              </div>
              <div className="event-heading-actions">
                {filteredStage && <button className="secondary-button small" type="button" onClick={() => setStageEventFilterId("")}>查看全部</button>}
                <button className="primary-button small" type="button" onClick={() => { setEditingProjectEventId(""); setModal("projectEvent"); }}>＋ 记录事件</button>
              </div>
            </div>
            {visibleEvents.length ? <div className="event-timeline">{visibleEvents.map((event) => (
              <article className={`event-card ${event.status}`} key={event.id}>
                <div className="event-date"><strong>{event.occurredOn.slice(8, 10) || event.occurredOn}</strong><span>{event.occurredOn.slice(0, 7)}</span></div>
                <div className="event-body">
                  <div className="event-badges">
                    <span className={`event-category ${event.category}`}>{EVENT_CATEGORY_LABEL[event.category]}</span>
                    <span className={`event-status ${event.status}`}>{EVENT_STATUS_LABEL[event.status]}</span>
                    {event.sourceStageId && <span className="event-stage-link">关联阶段：{currentProject.stages.find((stage) => stage.id === event.sourceStageId)?.name || "原阶段"}</span>}
                  </div>
                  <h3>{event.title}</h3>
                  {(event.organization || event.contact) && <p className="event-party">{event.organization}{event.contact ? ` · ${event.contact}` : ""}</p>}
                  {event.note && <p>{event.note}</p>}
                  {safeNumber(event.amount) > 0 && <strong className="event-amount">{safeNumber(event.amount).toLocaleString("zh-CN")} 元</strong>}
                  {event.nextStep && <div className="event-next"><small>下一步</small><span>{event.nextStep}{event.dueDate ? ` · ${event.dueDate} 前` : ""}</span></div>}
                  {event.fields?.filter((field) => field.value).length ? <dl className="event-fields">{event.fields.filter((field) => field.value).map((field) => <div key={field.id}><dt>{field.label}</dt><dd>{field.value}</dd></div>)}</dl> : null}
                </div>
                <details className="row-menu event-row-menu">
                  <summary aria-label={`${event.title}的编辑功能`}>•••</summary>
                  <div>
                    {event.sourceTodoId && todos.some((todo) => todo.id === event.sourceTodoId) ? <>
                      <button type="button" onClick={() => {
                        const linkedTodo = todos.find((todo) => todo.id === event.sourceTodoId);
                        if (!linkedTodo) return;
                        setEditingTodoId(todoKey(linkedTodo));
                        setModal("todoEdit");
                      }}>编辑关联待办</button>
                      <button className="danger-text" type="button" onClick={() => {
                        if (window.confirm(`取消“${event.title}”与本项目的关联吗？`)) unlinkTodoProjectEvent(event, currentProject.id);
                      }}>取消项目关联</button>
                    </> : <>
                      <button type="button" onClick={() => { setEditingProjectEventId(event.id); setModal("projectEvent"); }}>编辑</button>
                      <button className="danger-text" type="button" onClick={() => {
                        const eventIndex = (currentProject.events || []).findIndex((item) => item.id === event.id);
                        updateProject(currentProject.id, (project) => ({ ...project, events: (project.events || []).filter((item) => item.id !== event.id) }));
                        trashAdd({
                          id: String(event.id),
                          type: "event",
                          label: event.title,
                          parentId: String(currentProject.id),
                          parentLabel: currentProject.name,
                          extra: { index: eventIndex },
                          payload: event,
                        });
                        showToast("事件记录已移到回收站");
                      }}>删除</button>
                    </>}
                  </div>
                </details>
              </article>
            ))}</div> : <EmptyState title={filteredStage ? "暂无关联事件" : "暂无事件记录"} />}
          </section>
        )}

        {projectTab === "workflow" && (
          <div className="detail-grid">
            <section className="panel">
              <div className="panel-heading workflow-panel-heading">
                <div><h2>阶段计划</h2></div>
                <div className="workflow-heading-actions">
                  <button className="secondary-button small" type="button" onClick={() => setModal("stageStart")}>当前阶段</button>
                  <button className="primary-button small" type="button" onClick={() => setModal("stagePicker")}>＋ 添加阶段</button>
                </div>
              </div>
              {currentProject.stages.length ? (
                <div className="timeline workflow-timeline">
                  {currentProject.stages.map((stage, index) => {
                    const linkedEvents = events.filter((event) => event.sourceStageId === stage.id);
                    const latestLinkedEvent = linkedEvents[0];
                    const nextLinkedEvent = linkedEvents.find((event) => Boolean(event.nextStep));
                    return (
                    <article className={`stage-card ${stage.status}`} key={stage.id || `${stage.name}_${index}`}>
                      <span className="stage-dot" />
                      <div className="stage-main">
                        <div className="stage-title"><strong>{stage.name}</strong><span>{STAGE_STATUS_LABEL[stage.status]}</span></div>
                        <div className="stage-data">
                          {stage.config.filter((field) => stage.data?.[field.key]).map((field) => (
                            <span key={field.key}><small>{field.label}</small>{stage.data[field.key]}</span>
                          ))}
                          {!stage.config.some((field) => stage.data?.[field.key]) && <em>尚未填写节点信息</em>}
                        </div>
                        {linkedEvents.length > 0 && (
                          <div className="stage-event-summary">
                            <span className="stage-linked-record">最近动态</span>
                            {latestLinkedEvent && <strong>{latestLinkedEvent.occurredOn} · {latestLinkedEvent.title}</strong>}
                            {latestLinkedEvent?.note && <p>{latestLinkedEvent.note}</p>}
                            {nextLinkedEvent?.nextStep && <span className="stage-next-step">下一步：{nextLinkedEvent.nextStep}{nextLinkedEvent.dueDate ? ` · ${nextLinkedEvent.dueDate} 前` : ""}</span>}
                          </div>
                        )}
                      </div>
                      {linkedEvents.length > 0 && (
                        <div className="stage-event-preview-wrap">
                          <button className="stage-event-button" type="button" onClick={() => {
                            setStageEventFilterId(String(stage.id || ""));
                            setProjectTab("timeline");
                          }}>关联事件（{linkedEvents.length}）</button>
                          <div className="stage-event-popover" role="tooltip">
                            <strong>{stage.name} · 最近事件</strong>
                            {linkedEvents.slice(0, 3).map((event) => (
                              <div key={event.id}>
                                <time>{event.occurredOn}</time>
                                <span>{event.title}</span>
                                <small>{EVENT_STATUS_LABEL[event.status]}</small>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="stage-tools">
                        <div className="stage-move-controls">
                          <button type="button" disabled={index === 0} onClick={() => moveStage(index, -1)} aria-label="上移阶段">↑</button>
                          <button type="button" disabled={index === currentProject.stages.length - 1} onClick={() => moveStage(index, 1)} aria-label="下移阶段">↓</button>
                        </div>
                        <details className="stage-menu">
                          <summary aria-label={`${stage.name}更多操作`}>•••</summary>
                          <div>
                            <button type="button" onClick={(event) => {
                              setEditingStageIndex(index);
                              setModal("stageEditor");
                              event.currentTarget.closest("details")?.removeAttribute("open");
                            }}>编辑阶段信息</button>
                            <button type="button" onClick={(event) => {
                              setEditingStageIndex(index);
                              setModal("stageConfig");
                              event.currentTarget.closest("details")?.removeAttribute("open");
                            }}>调整字段</button>
                            <button type="button" onClick={(event) => {
                              updateCurrentProject((project) => ({
                                ...project,
                                stages: project.stages.map((item, itemIndex) => itemIndex === index
                                  ? { ...item, status: item.status === "skipped" ? "waiting" : "skipped" }
                                  : item),
                              }));
                              event.currentTarget.closest("details")?.removeAttribute("open");
                            }}>{stage.status === "skipped" ? "恢复阶段" : "设为不适用"}</button>
                            <button className="danger-text" type="button" onClick={(event) => {
                               event.currentTarget.closest("details")?.removeAttribute("open");
                               updateProject(currentProject.id, (project) => ({ ...project, stages: project.stages.filter((_, itemIndex) => itemIndex !== index) }));
                               trashAdd({
                                 id: String(stage.id || stage.name),
                                 type: "stage",
                                 label: stage.name,
                                 parentId: String(currentProject.id),
                                 parentLabel: currentProject.name,
                                 extra: { index },
                                 payload: stage,
                               });
                               showToast("阶段已移到回收站");
                             }}>删除阶段</button>
                          </div>
                        </details>
                      </div>
                    </article>
                  );})}
                </div>
              ) : <EmptyState title="暂无流程" />}
            </section>
            <aside className="panel project-overview">
              <span className="eyebrow">流程概览</span><h2>{progress.percent}%</h2>
              <div className="progress-line large"><span style={{ width: `${progress.percent}%` }} /></div>
              <dl>
                <div><dt>流程节点</dt><dd>{progress.total}</dd></div>
                <div><dt>已完成</dt><dd>{progress.done}</dd></div>
                <div><dt>历史未补录</dt><dd>{currentProject.stages.filter((stage) => stage.status === "historical").length}</dd></div>
                <div><dt>不适用</dt><dd>{currentProject.stages.filter((stage) => stage.status === "skipped").length}</dd></div>
              </dl>
            </aside>
          </div>
        )}

        {projectTab === "funds" && (
          <div className="funds-layout">
            <section className="project-money-strip large">
              <button type="button" onClick={() => setModal("projectSettings")}><span>{currentProject.projectClass === "service" ? "项目预算" : "预算评审金额"}</span><strong className="money-value">{safeNumber(currentProject.budgetReviewAmount).toLocaleString("zh-CN")} 元</strong></button>
              <div><span>年度申请/到账</span><strong className="money-value">{funds.requested.toLocaleString("zh-CN")} 元</strong></div>
              <div><span>已支付</span><strong className="money-value">{funds.paid.toLocaleString("zh-CN")} 元</strong></div>
              <div><span>未支付合同金额</span><strong className="money-value">{funds.unpaid.toLocaleString("zh-CN")} 元</strong></div>
            </section>
            <section className="panel">
              <div className="panel-heading"><div><h2>年度资金计划</h2></div><button className="primary-button small" type="button" onClick={() => { setEditingFundingSourceId(""); setModal("funding"); }}>＋ 添加来源</button></div>
              {currentProject.funds?.length ? <div className="funding-list">{currentProject.funds.map((fund) => (
                <article className="funding-source-card" key={fund.id}>
                  <header className="funding-source-head">
                    <div><span>{fund.source === "unit" ? "单位自有" : fund.source === "district" ? "区财政" : fund.source === "cultural" ? "文物专项" : "其他专项"}</span><h3>{fund.name}</h3></div>
                    <div className="funding-source-actions">
                      <button className="secondary-button small" type="button" onClick={() => { setEditingFundingSourceId(fund.id); setEditingFundingPlanId(""); setModal("fundingYear"); }}>＋ 年度计划</button>
                      <details className="row-menu">
                        <summary aria-label={`${fund.name}资金来源的编辑功能`}>•••</summary>
                        <div>
                          <button type="button" onClick={() => { setEditingFundingSourceId(fund.id); setModal("funding"); }}>编辑来源</button>
                          <button className="danger-text" type="button" onClick={() => {
                            if (fund.annualPlans?.length) {
                              showToast("请先删除该来源下的年度计划");
                              return;
                            }
                            const fundIndex = (currentProject.funds || []).findIndex((item) => item.id === fund.id);
                            updateProject(currentProject.id, (project) => ({ ...project, funds: (project.funds || []).filter((item) => item.id !== fund.id) }));
                            trashAdd({
                              id: String(fund.id),
                              type: "fund",
                              label: fund.name,
                              parentId: String(currentProject.id),
                              parentLabel: currentProject.name,
                              extra: { index: fundIndex },
                              payload: fund,
                            });
                            showToast("资金来源已移到回收站");
                          }}>删除来源</button>
                        </div>
                      </details>
                    </div>
                  </header>
                  {fund.annualPlans?.length ? <div className="funding-year-list">{[...fund.annualPlans].sort((a, b) => b.year.localeCompare(a.year)).map((plan) => {
                    const paid = fundingPlanPaid(currentProject, plan.id);
                    const paymentLinks = fundingPlanPayments(currentProject, plan.id);
                    const unpaidSchedules = fundingPlanUnpaidSchedules(currentProject, plan.id);
                    const unpaidScheduledAmount = unpaidSchedules.reduce((sum, item) => sum + safeNumber(item.amount), 0);
                    const received = safeNumber(plan.requestedAmount);
                    const requested = safeNumber(plan.requestedAmount);
                    const balance = Math.round((received - paid + Number.EPSILON) * 100) / 100;
                    const remaining = Math.max(0, balance);
                    const overspent = Math.max(0, -balance);
                    const overdue = remaining > 0 && localDateKey() > plan.deadline;
                    const paymentRate = received > 0 ? Math.round((paid / received * 100 + Number.EPSILON) * 10) / 10 : 0;
                    return (
                      <section className="funding-year-row" key={plan.id}>
                        <div className="funding-year-title"><strong>{plan.year} 年度</strong><small>{plan.receivedOn ? `${plan.receivedOn} 到账` : "尚未填写到账日期"}</small></div>
                        <dl>
                          <div><dt>当年申请</dt><dd>{safeNumber(plan.requestedAmount).toLocaleString("zh-CN")} 元</dd></div>
                          <div className={`funding-linked-payment ${paymentLinks.length ? "has-links" : ""}`}><dt>已关联支付</dt><dd><span className="funding-payment-hover"><button className="funding-linked-payment-button" type="button" disabled={!paymentLinks.length} onClick={() => { setFundingPaymentPreviewPlanId(plan.id); setModal("fundingPaymentPreview"); }} aria-label={paymentLinks.length ? `查看 ${plan.year} 年度已关联支付明细` : "暂无已关联支付明细"}>{paid.toLocaleString("zh-CN")} 元</button>{paymentLinks.length > 0 && <span className="funding-payment-popover" role="tooltip"><strong>已关联支付 · {paymentLinks.length} 笔</strong>{paymentLinks.slice(0, 4).map((link) => <span key={link.payment.id}><time>{link.payment.date}</time><em>{link.contractName || link.procurementName}</em><b>{safeNumber(link.payment.amount).toLocaleString("zh-CN")} 元</b></span>)}{paymentLinks.length > 4 && <small>点击查看全部 {paymentLinks.length} 笔</small>}</span>}</span></dd></div>
                          <div className={`funding-unpaid ${unpaidSchedules.length ? "has-links" : ""}`}><dt>已关联未支付</dt><dd><span className="funding-unpaid-hover"><button type="button" disabled={!unpaidSchedules.length} onClick={() => { setFundingUnpaidPreviewPlanId(plan.id); setModal("fundingUnpaidPreview"); }} aria-label={unpaidSchedules.length ? `查看 ${plan.year} 年度已关联未支付明细` : "暂无已关联未支付节点"}>{unpaidSchedules.length ? `${unpaidSchedules.length} 笔 · ${unpaidScheduledAmount.toLocaleString("zh-CN")} 元` : "—"}</button>{unpaidSchedules.length > 0 && <span className="funding-unpaid-popover" role="tooltip"><strong>已关联未支付 · {unpaidSchedules.length} 笔</strong>{unpaidSchedules.slice(0, 4).map((item) => <span key={item.plan.id}><em>{item.contractName || item.procurementName} · 第 {item.planIndex + 1} 笔</em><b>{safeNumber(item.amount).toLocaleString("zh-CN")} 元</b></span>)}{unpaidSchedules.length > 4 && <small>点击查看全部 {unpaidSchedules.length} 笔</small>}</span>}</span></dd></div>
                          <div><dt>支付率</dt><dd>{paymentRate.toLocaleString("zh-CN")}%</dd></div>
                          <div className={`funding-clawback ${remaining > 0 ? "has-balance" : ""}`}><dt>{overdue ? "预计收回" : "尚待支付"}</dt><dd><span className="funding-clawback-hover"><button type="button" disabled={!received} onClick={() => { setFundingClawbackPreviewPlanId(plan.id); setModal("fundingClawbackPreview"); }}>{remaining.toLocaleString("zh-CN")} 元</button>{received > 0 && <span className="funding-clawback-popover" role="tooltip"><strong>{overdue ? "预计收回构成" : "尚待支付构成"}</strong><span><em>下达金额</em><b>{received.toLocaleString("zh-CN")} 元</b></span><span><em>减：已关联支付（{paymentLinks.length} 笔）</em><b>{paid.toLocaleString("zh-CN")} 元</b></span><span className="total"><em>{overdue ? "预计收回" : "余额"}</em><b>{remaining.toLocaleString("zh-CN")} 元</b></span></span>}</span></dd></div>
                        </dl>
                        <div className={`funding-year-status ${requested === 0 ? "" : overspent > 0 ? "overspent" : remaining === 0 ? "done" : overdue ? "overdue" : ""}`}>
                          <strong>{requested === 0 ? "未开始申请资金" : overspent > 0 ? `已超出下达金额 ${overspent.toLocaleString("zh-CN")} 元` : remaining === 0 ? "当年资金已付完" : overdue ? `预计收回 ${remaining.toLocaleString("zh-CN")} 元` : `${plan.deadline} 前付完`}</strong>
                          {plan.note && <small>{plan.note}</small>}
                        </div>
                        <details className="row-menu funding-year-menu">
                          <summary aria-label={`${plan.year}年度资金计划的编辑功能`}>•••</summary>
                          <div>
                            <button type="button" onClick={() => { setEditingFundingSourceId(fund.id); setEditingFundingPlanId(plan.id); setModal("fundingYear"); }}>编辑年度计划</button>
                            <button className="danger-text" type="button" onClick={() => {
                              if (paid > 0) {
                                showToast("该年度已有付款关联，不能直接删除");
                                return;
                              }
                              const planIndex = (fund.annualPlans || []).findIndex((entry) => entry.id === plan.id);
                              updateProject(currentProject.id, (project) => ({
                                ...project,
                                funds: (project.funds || []).map((item) => item.id === fund.id ? { ...item, annualPlans: (item.annualPlans || []).filter((entry) => entry.id !== plan.id) } : item),
                              }));
                              trashAdd({
                                id: String(plan.id),
                                type: "fundingPlan",
                                label: `${plan.year} 年度计划`,
                                parentId: String(currentProject.id),
                                parentLabel: currentProject.name,
                                extra: { index: planIndex, fundId: String(fund.id) },
                                payload: plan,
                              });
                              showToast("年度资金计划已移到回收站");
                            }}>删除年度计划</button>
                          </div>
                        </details>
                      </section>
                    );
                  })}</div> : null}
                </article>
              ))}</div> : <EmptyState title="暂无资金来源" />}
            </section>
            <section className="panel">
              <div className="panel-heading"><div><h2>采购包、合同与付款</h2></div><button className="primary-button small" type="button" onClick={() => { setEditingProcurementId(""); setProcurementBasis(""); setProcurementForm(""); setModal("procurement"); }}>＋ 新建采购包</button></div>
              {currentProject.procurements?.length ? <div className="procurement-list">{currentProject.procurements.map((procurement, procurementIndex) => (
                <article className="procurement-card" key={procurement.id}>
                  <div className="procurement-head">
                    <div><div className="procurement-kicker"><span>采购包</span><em>{EVENT_STATUS_LABEL[procurement.status]}</em></div><h3>{procurement.name}</h3></div>
                    <div className="procurement-actions">
                      <button className="secondary-button small" type="button" onClick={() => { setEditingProcurementId(procurement.id); setEditingContractId(""); setContractInstallmentCount(1); setContractAmountStatus("confirmed"); setModal("contract"); }}>＋ 合同</button>
                      <details className="row-menu procurement-menu">
                        <summary aria-label={`${procurement.name}采购包的编辑功能`}>•••</summary>
                        <div>
                          <button type="button" onClick={() => { setEditingProcurementId(procurement.id); setProcurementBasis(procurement.basis || ""); setProcurementForm(procurement.procurementForm || ""); setModal("procurement"); }}>编辑采购包</button>
                          <button className="danger-text" type="button" onClick={() => {
                            updateProject(currentProject.id, (project) => ({ ...project, procurements: (project.procurements || []).filter((item) => item.id !== procurement.id) }));
                            trashAdd({
                              id: String(procurement.id),
                              type: "procurement",
                              label: procurement.name,
                              parentId: String(currentProject.id),
                              parentLabel: currentProject.name,
                              extra: { index: procurementIndex },
                              payload: procurement,
                            });
                            showToast("采购包已移到回收站（含其下合同）");
                          }}>删除采购包</button>
                        </div>
                      </details>
                      <div className="procurement-move-controls" aria-label="调整采购包顺序">
                        <button type="button" title="上移采购包" aria-label="上移采购包" disabled={procurementIndex === 0} onClick={() => updateCurrentProject((project) => ({ ...project, procurements: moveListItem(project.procurements || [], procurementIndex, -1) }))}>↑</button>
                        <button type="button" title="下移采购包" aria-label="下移采购包" disabled={procurementIndex === (currentProject.procurements?.length || 0) - 1} onClick={() => updateCurrentProject((project) => ({ ...project, procurements: moveListItem(project.procurements || [], procurementIndex, 1) }))}>↓</button>
                      </div>
                    </div>
                  </div>
                  {(procurement.basis || procurement.procurementForm || procurement.announceDate || procurement.evaluationDate || procurement.awardAnnounceDate || procurement.awardLink || procurement.announceLink || procurement.procurementResult || procurement.intentAnnounceDate || procurement.approvalDocName) && (
                    <div className="procurement-procedure-summary">
                      {procurement.basis && <span>{PROCUREMENT_BASIS_LABEL[procurement.basis]}{procurement.basis === "meeting" && procurement.minutesRef ? ` · ${procurement.minutesRef}` : ""}</span>}
                      {procurement.basis === "approval" && procurement.approvalDocName && <span>{procurement.approvalDocName}{procurement.approvalDocDate ? ` · ${procurement.approvalDocDate}` : ""}</span>}
                      {procurement.procurementForm && <span>{PROCUREMENT_FORM_LABEL[procurement.procurementForm]}</span>}
                      {procurement.intentAnnounceDate && <span>意向公开 {procurement.intentAnnounceDate}</span>}
                      {procurement.announceDate && <span>公告 {procurement.announceDate}</span>}
                      {procurement.evaluationDate && <span>评标 {procurement.evaluationDate}</span>}
                      {procurement.awardAnnounceDate && <span>中标公示 {procurement.awardAnnounceDate}</span>}
                      {procurement.announceLink && <a href={procurement.announceLink} target="_blank" rel="noreferrer">公告链接</a>}
                      {procurement.awardLink && <a href={procurement.awardLink} target="_blank" rel="noreferrer">中标公示链接</a>}
                      {procurement.procurementResult && <span className={`procurement-result ${procurement.procurementResult}`}>{PROCUREMENT_RESULT_LABEL[procurement.procurementResult]}</span>}
                    </div>
                  )}
                  {procurement.contracts.length ? <div className="contract-list">{procurement.contracts.map((contract) => {
                    const paymentPlan = contract.paymentPlan || [];
                    const nextPlanIndex = paymentPlan.findIndex((plan, index) => !paymentForPlan(contract, plan, index));
                    const nextPlan = nextPlanIndex >= 0 ? paymentPlan[nextPlanIndex] : null;
                    const pendingMaterials = (contract.payments || []).filter(isMaterialPayment).reduce((count, payment) =>
                      count + (paymentPerformanceStatus(payment) === "pending" ? 1 : 0) + (paymentArchiveStatus(payment) === "pending" ? 1 : 0), 0);
                    return <div className="contract-block" key={contract.id}>
                      <div className="contract-row">
                        <div className="contract-title"><span>合同</span><strong>{contract.name}</strong><small>{contract.party || "未填写合同单位"}{contract.signedOn ? ` · ${contract.signedOn}` : ""}</small></div>
                        <dl><div><dt>合同金额</dt><dd>{contract.amountStatus === "pending" || !safeNumber(contract.amount) ? "待中标后补充" : `${safeNumber(contract.amount).toLocaleString("zh-CN")} 元`}</dd></div><div><dt>已提交付款</dt><dd>{contract.payments.reduce((sum, payment) => sum + safeNumber(payment.amount), 0).toLocaleString("zh-CN")} 元</dd></div>{procurement.kind === "construction" && safeNumber(contract.settlementAuditAmount) > 0 && <div><dt>结算审定</dt><dd>{safeNumber(contract.settlementAuditAmount).toLocaleString("zh-CN")} 元</dd></div>}</dl>
                        {paymentPlan.length ? <div className="contract-next-payment"><span>{nextPlan ? "下一笔待付款" : "付款节点"}</span><strong>{nextPlan ? `第 ${nextPlanIndex + 1} 笔 · ${nextPlan.ratio}%` : "均已提交"}</strong>{nextPlan && <small>{(procurement.kind === "construction" && nextPlanIndex === paymentPlan.length - 1 && safeNumber(contract.settlementAuditAmount) > 0 ? settlementFinalPaymentAmount(contract) : plannedPaymentAmount(contract, nextPlan)).toLocaleString("zh-CN")} 元</small>}</div> : null}
                        <div className="contract-actions">
                          {contract.amountStatus === "pending" || !safeNumber(contract.amount) ? <button className="primary-button small" type="button" onClick={() => { setEditingProcurementId(procurement.id); setEditingContractId(contract.id); setContractInstallmentCount(contract.paymentPlan?.length || 1); setModal("contractAmount"); }}>补充金额</button> : <button className="secondary-button small" type="button" onClick={() => { setEditingProcurementId(procurement.id); setEditingContractId(contract.id); setEditingPaymentId(""); setPaymentKind((contract.paymentPlan || []).length === 1 ? "oneTime" : "progress"); setModal("payment"); }}>＋ 付款</button>}
                          {procurement.kind === "construction" && <button className="secondary-button small" type="button" onClick={() => { setEditingProcurementId(procurement.id); setEditingContractId(contract.id); setContractSettlementAmount(contract.settlementAuditAmount || ""); setModal("contractSettlement"); }}>{safeNumber(contract.settlementAuditAmount) > 0 ? "修改审定" : "结算审定"}</button>}
                          {pendingMaterials > 0 && <span className="contract-material-alert">待补材料 {pendingMaterials} 项</span>}
                          <details className="row-menu contract-menu">
                            <summary aria-label={`${contract.name}合同的编辑功能`}>•••</summary>
                            <div>
                              <button type="button" onClick={() => { setEditingProcurementId(procurement.id); setEditingContractId(contract.id); setContractInstallmentCount(contract.paymentPlan?.length || 1); setContractAmountStatus(contract.amountStatus || (safeNumber(contract.amount) > 0 ? "confirmed" : "pending")); setModal("contract"); }}>编辑合同</button>
                              <button className="danger-text" type="button" onClick={() => {
                                const contractIndex = procurement.contracts.findIndex((entry) => entry.id === contract.id);
                                updateProject(currentProject.id, (project) => unlinkContractParticipant({
                                  ...project,
                                  procurements: (project.procurements || []).map((item) => item.id === procurement.id ? { ...item, contracts: item.contracts.filter((entry) => entry.id !== contract.id) } : item),
                                }, contract.id));
                                trashAdd({
                                  id: String(contract.id),
                                  type: "contract",
                                  label: contract.name,
                                  parentId: String(currentProject.id),
                                  parentLabel: currentProject.name,
                                  extra: { index: contractIndex, procurementId: String(procurement.id) },
                                  payload: contract,
                                });
                                showToast("合同已移到回收站（含其下付款记录）");
                              }}>删除合同</button>
                            </div>
                          </details>
                        </div>
                      </div>
                      {contract.payments.length ? <div className="payment-list">{contract.payments.map((payment) => {
                        // Keep the payment card's funding source visible during rollout for verification.
                        const schedule = payment.scheduleId ? paymentPlan.find((plan) => plan.id === payment.scheduleId) : undefined;
                        const linkedFundingPlan = (currentProject.funds || []).flatMap((fund) => (fund.annualPlans || []).map((plan) => ({ fund, plan }))).find(({ plan }) => plan.id === payment.fundingPlanId);
                        const paymentLabel = payment.kind === "oneTime"
                          ? "一次性 · 100%"
                          : schedule
                            ? `第 ${paymentPlan.findIndex((plan) => plan.id === schedule.id) + 1} 笔 · ${schedule.ratio}%`
                            : PAYMENT_KIND_LABEL[payment.kind];
                        return <div className="payment-row" data-layout-version={PAYMENT_CARD_LAYOUT_VERSION} key={payment.id}>
                          <div><span>{paymentLabel}</span><strong>付款申请交单 {payment.date} · {safeNumber(payment.amount).toLocaleString("zh-CN")} 元</strong><small className="payment-funding-link">关联资金：{linkedFundingPlan ? `${linkedFundingPlan.plan.year} 年 · ${linkedFundingPlan.fund.name}` : "未关联年度计划"}</small>{isMaterialPayment(payment) && <span className="payment-material-status">{paymentPerformanceStatus(payment) === "pending" && <em>履约确认单待补</em>}{paymentArchiveStatus(payment) === "pending" && <em>归档单待补</em>}{paymentArchiveStatus(payment) === "notRequired" && <em className="neutral">归档单不需要</em>}</span>}{payment.note && <small>{payment.note}</small>}</div>
                          <details className="row-menu payment-menu">
                            <summary aria-label={`${payment.date}付款记录的编辑功能`}>•••</summary>
                            <div>
                              <button type="button" onClick={() => { setEditingProcurementId(procurement.id); setEditingContractId(contract.id); setEditingPaymentId(payment.id); setPaymentKind(payment.kind); setModal("payment"); }}>编辑付款</button>
                              <button className="danger-text" type="button" onClick={() => {
                                const paymentIndex = contract.payments.findIndex((record) => record.id === payment.id);
                                updateProject(currentProject.id, (project) => updateContractWithParticipant(
                                  project,
                                  procurement.id,
                                  contract.id,
                                  (entry) => ({ ...entry, payments: entry.payments.filter((record) => record.id !== payment.id) }),
                                ));
                                trashAdd({
                                  id: String(payment.id),
                                  type: "payment",
                                  label: `${payment.date} 付款 ${safeNumber(payment.amount).toLocaleString("zh-CN")} 元`,
                                  parentId: String(currentProject.id),
                                  parentLabel: currentProject.name,
                                  extra: { index: paymentIndex, procurementId: String(procurement.id), contractId: String(contract.id) },
                                  payload: payment,
                                });
                                showToast("付款记录已移到回收站");
                              }}>删除付款记录</button>
                            </div>
                          </details>
                        </div>;
                      })}</div> : null}
                    </div>;
                  })}</div> : <p className="muted-copy">暂无合同</p>}
                </article>
              ))}</div> : <EmptyState title="暂无采购包" />}
            </section>
          </div>
        )}

        {projectTab === "services" && (
          <section className="panel">
            <div className="panel-heading">
              <div><h2>参建单位</h2></div>
              <button className="primary-button small" type="button" onClick={() => { setEditingServiceId(""); setModal("service"); }}>＋ 录入单位</button>
            </div>
            {currentProject.services?.length ? (
              <div className="service-grid">
                {currentProject.services.map((service, index) => {
                  const linkedContract = service.linkedContractId
                    ? (currentProject.procurements || []).flatMap((item) => item.contracts || []).find((contract) => contract.id === service.linkedContractId)
                    : null;
                  const progress = linkedContract ? contractPaymentProgress(linkedContract) : service.progress || 0;
                  const amount = linkedContract?.amount ?? service.amount;
                  return <article className="service-card" key={service.id || `${service.name}_${index}`}>
                    <div className="service-head"><span>{service.type}</span><details className="row-menu"><summary aria-label={`${service.name}的编辑功能`}>•••</summary><div><button type="button" onClick={() => { setEditingServiceId(service.id); setModal("service"); }}>编辑</button><button className="danger-text" type="button" onClick={() => {
                      updateProject(currentProject.id, (project) => ({ ...project, services: (project.services || []).filter((_, itemIndex) => itemIndex !== index) }));
                      trashAdd({
                        id: String(service.id || `${service.type}_${service.name}`),
                        type: "service",
                        label: service.name,
                        parentId: String(currentProject.id),
                        parentLabel: currentProject.name,
                        extra: { index },
                        payload: service,
                      });
                      showToast("参建单位已移到回收站");
                    }}>删除</button></div></details></div>
                    <h3>{service.name}</h3><p>{service.contact || "未填写联系人"}</p>
                    <strong>{safeNumber(amount).toLocaleString("zh-CN")} 元</strong>
                    <div className="progress-line"><span style={{ width: `${progress}%` }} /></div><small>{linkedContract ? "履约／支付进度" : "履约进度"} {progress}%</small>
                  </article>;
                })}
              </div>
            ) : <EmptyState title="暂无参建单位" />}
          </section>
        )}

        {projectTab === "sheet" && (() => {
          const sheet = normalizeFundingWorksheet(currentProject.fundingWorksheet);
          const updateSheet = (update: (worksheet: FundingWorksheet) => FundingWorksheet) =>
            updateCurrentProject((project) => ({
              ...project,
              fundingWorksheet: update(normalizeFundingWorksheet(project.fundingWorksheet)),
            }));
          const selectedValue = sheet.cells[selectedFundingCell] || "";
          return <section className="panel funding-worksheet-panel">
            <div className="panel-heading funding-worksheet-heading">
              <div><button className="apple-back-button" type="button" onClick={() => setProjectTab("funds")}><span aria-hidden="true">‹</span>资金合同</button><h2>项目资金测算</h2></div>
              <div className="funding-sheet-actions">
                <button className="secondary-button small" type="button" onClick={() => updateSheet((item) => resizeFundingWorksheet(item, item.rows + 1, item.columns))}>＋ 行</button>
                <button className="secondary-button small" type="button" onClick={() => updateSheet((item) => resizeFundingWorksheet(item, item.rows, item.columns + 1))}>＋ 列</button>
                <details className="row-menu"><summary aria-label="测算表更多操作">•••</summary><div>
                  <button type="button" onClick={() => updateSheet((item) => resizeFundingWorksheet(item, item.rows - 1, item.columns))}>删除末行</button>
                  <button type="button" onClick={() => updateSheet((item) => resizeFundingWorksheet(item, item.rows, item.columns - 1))}>删除末列</button>
                </div></details>
              </div>
            </div>
            <div className="funding-formula-bar">
              <strong>{selectedFundingCell}</strong>
              <span>fx</span>
              <input aria-label="公式栏" value={selectedValue} placeholder="输入数值或 =C2+D2" onChange={(event) => {
                const value = event.target.value;
                updateSheet((item) => ({ ...item, cells: { ...item.cells, [selectedFundingCell]: value }, updatedAt: new Date().toISOString() }));
              }} />
            </div>
            <div className="funding-sheet-scroll">
              <table className="funding-sheet" aria-label="项目资金测算表">
                <thead><tr><th className="sheet-corner" />{Array.from({ length: sheet.columns }, (_, column) => <th key={column}>{sheetColumnName(column)}</th>)}</tr></thead>
                <tbody>{Array.from({ length: sheet.rows }, (_, row) => <tr key={row}><th>{row + 1}</th>{Array.from({ length: sheet.columns }, (_, column) => {
                  const reference = sheetCellRef(row, column);
                  const raw = sheet.cells[reference] || "";
                  return <td className={selectedFundingCell === reference ? "selected" : ""} key={reference}>
                    <input
                      aria-label={`单元格 ${reference}`}
                      value={editingFundingCell === reference ? raw : fundingCellDisplay(sheet, reference)}
                      onFocus={() => { setSelectedFundingCell(reference); setEditingFundingCell(reference); }}
                      onChange={(event) => {
                        const value = event.target.value;
                        updateSheet((item) => ({ ...item, cells: { ...item.cells, [reference]: value }, updatedAt: new Date().toISOString() }));
                      }}
                      onBlur={() => setEditingFundingCell("")}
                    />
                  </td>;
                })}</tr>)}</tbody>
              </table>
            </div>
          </section>;
        })()}
      </>
    );
  };

  const renderAttendance = () => {
    const todayKey = localDateKey();
    const todayTime = new Date(`${todayKey}T12:00:00`).getTime();
    const deadlineState = (date: string) => {
      const deadline = overtimeDeadline(date);
      const overdue = Boolean(deadline && deadline < todayKey);
      const soon = Boolean(deadline && !overdue && new Date(`${deadline}T12:00:00`).getTime() - todayTime <= 7 * 86400000);
      return { deadline, overdue, soon };
    };
    const activeOvertime = (attendance.records || []).filter((record) => record.status !== "used" && !record.holiday);
    const overdueCount = activeOvertime.filter((record) => deadlineState(record.date).overdue).length;
    const soonCount = activeOvertime.filter((record) => deadlineState(record.date).soon).length;
    return (
    <>
      <section className="page-title-row">
        <div><h1>休假与加班</h1></div>
        <div className="button-row">
          <button className="secondary-button" type="button" onClick={() => { setEditingStageIndex(null); setModal("leave"); }}>＋ 录入休假</button>
          <button className="primary-button" type="button" onClick={() => { setEditingOvertimeId(""); setModal("overtime"); }}>＋ 录入加班</button>
        </div>
      </section>
      <section className="leave-summary">
        <div><span>当前可休</span><strong>{leaveSummary.total}</strong><small>天</small></div>
        <dl>
          <div><dt>剩余年假</dt><dd>{leaveSummary.annual}</dd></div>
          <div><dt>剩余存休</dt><dd>{leaveSummary.saved}</dd></div>
          <div><dt>加班池</dt><dd>{leaveSummary.activeOvertime}</dd></div>
        </dl>
      </section>
      {(overdueCount > 0 || soonCount > 0) && (
        <div className="notice-card">
          <span className="notice-mark">!</span>
          <div><strong>加班倒休即将到期</strong><p>{overdueCount > 0 ? `${overdueCount} 条加班已超过“次月内休完”期限；` : ""}{soonCount > 0 ? `${soonCount} 条加班将在 7 天内到期，请尽快安排休用。` : ""}</p></div>
        </div>
      )}
      <section className="panel base-settings">
        <div><strong>初始额度</strong><span>年假 {attendance.annual || 0} 天 · 存休 {attendance.saved || 0} 天</span></div>
        <button className="secondary-button compact" type="button" onClick={() => setModal("attendanceSettings")}>编辑额度</button>
      </section>
      <div className="two-column">
        <section className="panel">
          <div className="panel-heading"><div><h2>加班记录</h2></div></div>
          <div className="record-list">
            {attendance.records.length ? [...attendance.records].sort((a, b) => b.date.localeCompare(a.date)).map((record) => {
              const deadline = deadlineState(record.date);
              return (
              <article className="record-row" key={record.id}>
                <div><span className={`status-pill ${record.status}`}>{record.status === "used" ? "已休" : "待休"}</span><strong>{record.date} · ＋{record.days} 天{record.status === "used" && record.usedDate ? `（已休于 ${record.usedDate}）` : ""}</strong>{record.holiday ? <em className="holiday-tag">节假日不计倒休</em> : null}<p>{record.note || "无备注"}</p>{!record.holiday && record.status === "active" && deadline.deadline ? <small className={`overtime-deadline ${deadline.overdue ? "overdue" : deadline.soon ? "soon" : ""}`}>需在 {deadline.deadline} 前休完{deadline.overdue ? "（已过期）" : ""}</small> : null}</div>
                <details className="row-menu record-row-menu">
                  <summary aria-label={`${record.date}加班记录的编辑功能`}>•••</summary>
                  <div>
                    <button type="button" onClick={() => { setEditingOvertimeId(record.id); setModal("overtime"); }}>编辑加班记录</button>
                    {record.status !== "used" && <button type="button" onClick={() => {
                      setEditingStageIndex(attendance.records.findIndex((item) => item.id === record.id));
                      setModal("useOvertime");
                    }}>登记休用</button>}
                    <button className="danger-text" type="button" onClick={() => {
                      const recordIndex = attendance.records.findIndex((item) => item.id === record.id);
                      setAttendance((data) => ({ ...data, records: data.records.filter((item) => item.id !== record.id) }));
                      trashAdd({
                        id: String(record.id),
                        type: "overtime",
                        label: `${record.date} 加班 ${record.days} 天`,
                        extra: { index: recordIndex },
                        payload: record,
                      });
                      showToast("加班记录已移到回收站");
                    }}>删除加班记录</button>
                  </div>
                </details>
              </article>
              );
            }) : <EmptyState title="暂无加班记录" />}
          </div>
        </section>
        <section className="panel">
          <div className="panel-heading"><div><h2>休假记录</h2></div></div>
          <div className="record-list">
            {attendance.leaveRecords.length ? [...attendance.leaveRecords].sort((a, b) => b.date.localeCompare(a.date)).map((record) => {
              const originalIndex = attendance.leaveRecords.indexOf(record);
              return (
                <article className="record-row" key={record.id || `${record.date}_${originalIndex}`}>
                  <div><span className="status-pill leave">{record.type}</span><strong>{record.date} · －{record.days} 天</strong><p>{record.note || "无说明"}</p></div>
                  <details className="row-menu record-row-menu">
                    <summary aria-label={`${record.date}休假记录的编辑功能`}>•••</summary>
                    <div>
                      <button type="button" onClick={() => {
                        setEditingStageIndex(originalIndex);
                        setModal("leave");
                      }}>编辑休假记录</button>
                      <button className="danger-text" type="button" onClick={() => {
                        setAttendance((data) => ({ ...data, leaveRecords: data.leaveRecords.filter((_, index) => index !== originalIndex) }));
                        trashAdd({
                          id: String(record.id || `${record.date}_${originalIndex}`),
                          type: "leave",
                          label: `${record.date} 休假 ${record.days} 天`,
                          extra: { index: originalIndex },
                          payload: record,
                        });
                        showToast("休假记录已移到回收站");
                      }}>删除休假记录</button>
                    </div>
                  </details>
                </article>
              );
            }) : <EmptyState title="暂无休假记录" />}
          </div>
        </section>
      </div>
    </>
  );
  };

  const renderMatters = () => {
    const matters = todos.filter((item) => item.kind && item.kind !== "todo");
    const projectNameFor = (id?: string) =>
      id ? projects.find((project) => String(project.id) === String(id))?.name : "";
    const query = workRecordQuery.trim();
    const projectFilter = workRecordProjectFilter ? String(workRecordProjectFilter) : "";
    const visibleGroups = workRecordGroups.map(({ year, months }) => ({
      year,
      months: months.map(({ month, records }) => ({
        month,
        records: records
          .map(({ date, entries }) => ({
            date,
            entries: entries.filter((record) => {
              if (projectFilter && String(record.linkedProjectId || "") !== projectFilter) return false;
              const haystack = [
                date,
                record.content,
                record.amount || "",
                record.organization || "",
                record.contact || "",
                record.nextStep || "",
                record.dueDate || "",
                ...(record.attachments || []).flatMap((attachment) => [attachment.name, attachment.content]),
              ].join(" ");
              return matchesAllTerms(haystack, query);
            }),
          }))
          .filter((day) => day.entries.length > 0),
      })).filter((monthGroup) => monthGroup.records.length > 0),
    })).filter((yearGroup) => yearGroup.months.length > 0);
    const totalMonthCount = visibleGroups.reduce((sum, group) => sum + group.months.length, 0);
    const cappedGroups = query || projectFilter || totalMonthCount <= workRecordMonthLimit
      ? visibleGroups
      : (() => {
          const flattened = visibleGroups.flatMap(({ year, months }) => months.map((month) => ({ year, ...month })));
          const capped = flattened.slice(0, workRecordMonthLimit);
          const grouped = new Map<string, typeof capped>();
          capped.forEach((item) => {
            const existing = grouped.get(item.year);
            if (existing) existing.push(item);
            else grouped.set(item.year, [item]);
          });
          return [...grouped.entries()].map(([year, months]) => ({ year, months }));
        })();
    const visibleCount = query
      ? visibleGroups.reduce(
          (sum, year) => sum + year.months.reduce(
            (monthSum, month) => monthSum + month.records.reduce(
              (daySum, day) => daySum + day.entries.length, 0), 0), 0)
      : workRecords.length;
    return (
      <>
        <section className="page-title-row">
          <div><h1>工作记录</h1></div>
        </section>
        <div className="matters-dashboard">
          <section className={`panel work-record-panel ${visibleGroups.length ? "" : "is-empty"}`}>
            <div className="panel-heading">
              <div><h2>工作日志</h2></div>
              <div className="heading-actions"><span className="work-record-count">{visibleCount} 条</span><button className="primary-button small" type="button" onClick={() => { setSelectedLogDate(localDateKey()); setEditingWorkRecordId(""); setDayLogMode("create"); setModal("dayLog"); }}>＋ 录入</button></div>
            </div>
            <div className="work-record-search-row">
              <input className="work-record-search" type="search" placeholder="搜索内容或日期（多个关键词用空格分隔）" value={workRecordQuery} onChange={(event) => setWorkRecordQuery(event.target.value)} aria-label="搜索工作记录" />
              <select className="work-record-search-select" value={workRecordProjectFilter} onChange={(event) => setWorkRecordProjectFilter(event.target.value)} aria-label="按项目筛选">
                <option value="">全部项目</option>
                {projects.filter((project) => project.status !== "trashed").map((project) => <option key={project.id} value={String(project.id)}>{project.name}</option>)}
              </select>
            </div>
            {visibleGroups.length ? (<><div className="work-record-list">{cappedGroups.map(({ year, months }) => (
              <section className="work-record-year" key={year}>
                <header><strong>{year} 年</strong><span>{months.reduce((sum, month) => sum + month.records.reduce((daySum, day) => daySum + day.entries.length, 0), 0)} 条</span></header>
                {months.flatMap(({ records }) => records).map(({ date, entries }) => {
                  const weekday = "日一二三四五六".charAt(new Date(`${date}T00:00:00`).getDay());
                  const dayMeta = (linkedProjectId?: string, attachments?: Array<{ id: string }>) => {
                    const project = linkedProjectId ? projectNameFor(linkedProjectId) : "";
                    return (project || attachments?.length) ? (
                      <span className="work-record-meta">{project && <span className="work-record-project">{project}</span>}{attachments?.length ? <span className="work-record-attach-count">{attachments.length} 个附件</span> : null}</span>
                    ) : null;
                  };
                  return (
                    <article key={date} className="work-record-day">
                      <header className="work-record-day-head"><strong>{Number(date.slice(5, 7))}月{Number(date.slice(8, 10))}日 <span className="work-record-weekday">周{weekday}</span></strong><span>{entries.length} 条</span></header>
                      <div className="work-record-day-entries">{entries.map(({ id, content, occurredAt, linkedProjectId, attachments }) => (
                        <div className="work-record-entry" key={id}><button className="work-record-entry-open" type="button" onClick={() => { setSelectedLogDate(date); setModal("dayOverview"); }}><span className="work-record-line">{occurredAt && <time className="work-record-time">{occurredAt}</time>}<span className="work-record-content">{content}</span></span>{dayMeta(linkedProjectId, attachments)}</button><details className="row-menu work-record-menu"><summary aria-label={`${date} 工作记录的编辑功能`}>•••</summary><div><button type="button" onClick={() => { setSelectedLogDate(date); setEditingWorkRecordId(id); setDayLogMode("edit"); setModal("dayLog"); }}>编辑</button><button className="danger-text" type="button" onClick={() => removeWorkRecord(date, id)}>删除</button></div></details></div>
                      ))}</div>
                    </article>
                  );
                })}
              </section>
            ))}</div>
            {!query && !projectFilter && totalMonthCount > workRecordMonthLimit && (
              <button className="secondary-button full" type="button" onClick={() => setWorkRecordMonthLimit((limit) => limit + 6)}>加载更早的记录</button>
            )}
            </>) : <EmptyState title={query ? "没有匹配的工作记录" : "暂无工作记录"} />}
          </section>
          <section className={`panel matters-panel ${matters.length ? "" : "is-empty"}`}>
            <div className="panel-heading">
              <div><h2>事项清单</h2></div>
              <div className="heading-actions"><span className="matter-list-count">{matters.filter((item) => !item.done).length} 进行中</span><button className="secondary-button small" type="button" onClick={() => { setEditingMatterId(""); setModal("matter"); }}>＋ 新增</button></div>
            </div>
            {matters.length ? <div className="matter-list">{matters.map((matter) => (
            <article className={matter.done ? "done" : ""} key={matter.id || matter.text}>
              <div className="matter-content">
                <div><span>{matter.kind === "service" ? "原服务类事项" : matter.kind === "administrative" ? "内部事项" : "其他事项"}</span>{matter.done && <em>已完成</em>}</div>
                <h2>{matter.text}</h2>
                <p><strong>当前阶段：</strong>{matter.stage || "暂未填写"}</p>
                {matter.note && <small>{matter.note}</small>}
              </div>
              <details className="row-menu matter-row-menu">
                <summary aria-label={`${matter.text}的编辑功能`}>•••</summary>
                <div>
                  <button type="button" onClick={() => { setEditingMatterId(String(matter.id || "")); setModal("matter"); }}>编辑</button>
                  {matter.kind === "service" && <button type="button" onClick={() => convertMatterToServiceProject(matter)}>转为服务项目</button>}
                   <button className="danger-text" type="button" onClick={() => {
                     const matterIdentity = todoKey(matter);
                     const matterIndex = todos.findIndex((item) => todoKey(item) === matterIdentity);
                     setTodos((items) => items.filter((item) => todoKey(item) !== matterIdentity));
                     trashAdd({
                       id: String(matter.id || matterIdentity),
                       type: "matter",
                       label: matter.text,
                       extra: { index: matterIndex },
                       payload: matter,
                     });
                     showToast("事项已移到回收站");
                   }}>删除</button>
                </div>
              </details>
            </article>
            ))}</div> : <EmptyState title="暂无事项" />}
          </section>
        </div>
      </>
    );
  };

  const renderTools = () => (
    <>
      <section className="page-title-row"><div><h1>百宝箱</h1></div></section>
      <div className="tool-launcher">
        <button className="tool-app" type="button" onClick={() => setModal("toolDate")}><span className="tool-app-icon date">⌁</span><strong>日期计算</strong></button>
        <button className="tool-app" type="button" onClick={() => setModal("toolSettlement")}><span className="tool-app-icon settlement">%</span><strong>审减比例计算</strong></button>
        <button className="tool-app" type="button" onClick={() => setModal("toolCalculator")}><span className="tool-app-icon calculator">＋</span><strong>计算器</strong></button>
        <button className="tool-app" type="button" onClick={() => setModal("toolBackup")}><span className="tool-app-icon backup">▣</span><strong>数据备份</strong></button>
        <button className="tool-app" type="button" onClick={() => setModal("trash")}><span className="tool-app-icon trash">♻</span><strong>回收站</strong></button>
        <button className="tool-app" type="button" onClick={() => setModal("appearance")}><span className="tool-app-icon appearance">◐</span><strong>外观主题</strong></button>
      </div>
    </>
  );

  const crumb = page === "home" ? "工作台" : page === "projects" ? "项目" : page === "projectDetail" ? `项目 › ${currentProject?.name || "项目详情"}` : page === "attendance" ? "考勤" : page === "calendar" ? "工作日历" : page === "nonProjects" ? "工作记录" : "百宝箱";
  // 回收站列表：仅在打开弹窗时读取，操作后通过 trashTick 触发重读。
  void trashTick;
  const trashEntries = modal === "trash" ? readTrash(activeOwnerRef.current) : [];
  const navItems: Array<{ id: AppPage; label: string; mark: string }> = [
    { id: "home", label: "工作台", mark: "⌂" },
    { id: "projects", label: "项目", mark: "项" },
    { id: "nonProjects", label: "工作记录", mark: "录" },
    { id: "attendance", label: "考勤", mark: "休" },
    { id: "tools", label: "百宝箱", mark: "＋" },
  ];
  const accessGranted = Boolean(user || guestMode);

  if (!hydrated) return <main className="loading-screen"><span>工作助手</span></main>;

  return (
    <>
    <div className={`app-root ${accessGranted ? "" : "access-locked"}`} aria-hidden={!accessGranted}>
      <div className="app-shell">
        <aside className="sidebar">
        <div className="brand"><span>办</span><div><strong>工作助手</strong><small>个人项目管理台</small></div></div>
        <nav aria-label="主要导航">
          {navItems.map((item) => (
            <button key={item.id} className={(page === item.id || (item.id === "projects" && page === "projectDetail")) ? "active" : ""} type="button" onClick={() => navigate(item.id)}>
              <span>{item.mark}</span>{item.label}
            </button>
          ))}
        </nav>
        <div className="sidebar-tools">
          <button type="button" onClick={() => void exportData()}>备份数据</button>
          <button type="button" onClick={() => fileInputRef.current?.click()}>恢复数据</button>
          <input ref={fileInputRef} type="file" accept=".json,application/json" hidden onChange={importData} />
        </div>
        <button className="account-card" type="button" onClick={() => setModal("account")}>
          <span>{user?.email?.slice(0, 1).toUpperCase() || "访"}</span>
          <div><strong>{user?.email?.split("@")[0] || "访客空间"}</strong><small>{user ? syncLabel : "仅保存在此设备"}</small></div>
        </button>
      </aside>

      <main className="main-area">
        <header className="topbar">
          <div><span className="mobile-brand">工作助手</span><span className="topbar-crumb">{crumb}</span></div>
          <div className="topbar-actions">
            <span className={`sync-state ${syncLabel.includes("失败") ? "error" : ""}`}>{syncLabel}</span>
            <button className="secondary-button compact" type="button" onClick={manualSync}>同步</button>
            <button className="search-button" type="button" onClick={() => { setSearchQuery(""); setModal("search"); }} aria-label="搜索">⌕</button>
            <button className="avatar-button" type="button" onClick={() => setModal("account")} aria-label="打开个人中心">{user?.email?.slice(0, 1).toUpperCase() || "访"}</button>
          </div>
        </header>
        {syncError && user && (
          <div className="sync-error-banner" role="alert">
            <span>网络异常，暂时无法同步，数据已保存在本机</span>
            <button type="button" onClick={() => void syncWithCloud(true)}>重试</button>
          </div>
        )}
        <div className="content">
          {page === "home" && renderHome()}
          {page === "projects" && renderProjects()}
          {page === "projectDetail" && renderProjectDetail()}
          {page === "attendance" && renderAttendance()}
          {page === "calendar" && <><section className="page-title-row"><div><h1>工作日历</h1></div></section>{renderCalendar()}</>}
          {page === "nonProjects" && renderMatters()}
          {page === "tools" && renderTools()}
        </div>
      </main>
      </div>

      <nav className="bottom-nav" aria-label="手机端导航">
        {navItems.map((item) => (
          <button
            key={item.id}
            className={(page === item.id || (item.id === "projects" && page === "projectDetail")) ? "active" : ""}
            type="button"
            aria-current={(page === item.id || (item.id === "projects" && page === "projectDetail")) ? "page" : undefined}
            onClick={() => navigate(item.id)}
          >
            <span>{item.mark}</span>{item.label}
          </button>
        ))}
      </nav>
    </div>

      {undoState ? <div className="toast undo-toast" role="status"><span>{undoState.message}</span><button type="button" onClick={undoLastDeletion}>撤销</button></div>
        : toast && <div className="toast" role="status">{toast}</div>}

      <Modal open={modal === "toolDate"} title="日期计算" onClose={() => setModal(null)}>
        <form className="tool-form" onSubmit={(event) => {
          event.preventDefault();
          const form = new FormData(event.currentTarget);
          const start = String(form.get("start") || "");
          const end = String(form.get("end") || "");
          const result = Math.round(Math.abs(new Date(end).getTime() - new Date(start).getTime()) / 86400000);
          const box = event.currentTarget.querySelector<HTMLElement>("[data-result]");
          if (box) box.textContent = Number.isFinite(result) ? `相差 ${result} 天` : "请选择两个日期";
        }}>
          <label className="field-label">开始日期<SmartDateInput name="start" required /></label>
          <label className="field-label">结束日期<SmartDateInput name="end" required /></label>
          <button className="primary-button" type="submit">计算</button>
          <strong className="tool-result" data-result />
        </form>
      </Modal>

      <Modal open={modal === "toolSettlement"} title="审减比例计算" onClose={() => setModal(null)}>
        <form className="tool-form settlement-tool" onSubmit={(event) => {
          event.preventDefault();
          const form = new FormData(event.currentTarget);
          const submittedAmount = String(form.get("submittedAmount") || "");
          const approvedAmount = String(form.get("approvedAmount") || "");
          const contractAmount = String(form.get("contractAmount") || "");
          const submitted = safeNumber(submittedAmount);
          const approved = safeNumber(approvedAmount);
          const contract = safeNumber(contractAmount);
          const change = contract - approved;
          const contractRatio = contract > 0 ? change / contract * 100 : NaN;
          const submittedRatio = submitted > 0 ? (submitted - approved) / submitted * 100 : NaN;
          if (!Number.isFinite(contractRatio)) {
            showToast("请填写合同金额");
            return;
          }
          const record: SettlementRecord = {
            id: makeId(),
            createdAt: new Date().toISOString(),
            submittedAmount,
            approvedAmount,
            contractAmount,
            change: Math.round((change + Number.EPSILON) * 100) / 100,
            contractRatio: Math.round((Math.abs(contractRatio) + Number.EPSILON) * 100) / 100,
            submittedRatio: Number.isFinite(submittedRatio) ? Math.round((Math.abs(submittedRatio) + Number.EPSILON) * 100) / 100 : 0,
          };
          setSettlementRecords((records) => [record, ...records]);
          event.currentTarget.reset();
        }}>
          <label className="field-label">报审金额（元）<input name="submittedAmount" type="number" min="0" step="0.01" required /></label>
          <label className="field-label">审定金额（元）<input name="approvedAmount" type="number" min="0" step="0.01" required /></label>
          <label className="field-label">合同金额（元）<input name="contractAmount" type="number" min="0" step="0.01" required /></label>
          <button className="primary-button" type="submit">计算并保存</button>
        </form>
        {settlementRecords.length > 0 && (
          <div className="settlement-history">
            <h3>历史记录</h3>
            {settlementRecords.map((record) => {
              const label = record.change >= 0 ? "审减" : "调增";
              return (
                <div className="settlement-record" key={record.id}>
                  <div>
                    <span>{label} <strong>{Math.abs(record.change).toLocaleString("zh-CN")}</strong> 元</span>
                    <small>占合同价 <strong>{record.contractRatio}%</strong> · 占报审价 <strong>{record.submittedRatio}%</strong> · {new Date(record.createdAt).toLocaleString("zh-CN", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" })}</small>
                    <small className="settlement-detail">报审 {safeNumber(record.submittedAmount).toLocaleString("zh-CN")} · 审定 {safeNumber(record.approvedAmount).toLocaleString("zh-CN")} · 合同 {safeNumber(record.contractAmount).toLocaleString("zh-CN")} 元</small>
                  </div>
                  <button className="icon-button compact" type="button" onClick={() => setSettlementRecords((records) => records.filter((item) => item.id !== record.id))} aria-label="删除记录">✕</button>
                </div>
              );
            })}
          </div>
        )}
      </Modal>

      <Modal open={modal === "toolCalculator"} title="计算器" onClose={() => setModal(null)}>
        <div className="calculator-tool">
          <input aria-label="计算结果" value={calculatorDisplay} onChange={(event) => setCalculatorDisplay(event.target.value)} inputMode="decimal" />
          <div className="calculator-keys">
            {["7", "8", "9", "÷", "4", "5", "6", "×", "1", "2", "3", "−", "0", ".", "(", "+"].map((key) => <button key={key} type="button" onClick={() => setCalculatorDisplay((value) => `${value}${key}`)}>{key}</button>)}
            <button className="calculator-clear" type="button" onClick={() => setCalculatorDisplay("")}>C</button>
            <button type="button" onClick={() => setCalculatorDisplay((value) => value.slice(0, -1))}>⌫</button>
            <button type="button" onClick={() => setCalculatorDisplay((value) => `${value})`)}>）</button>
            <button className="calculator-equals" type="button" onClick={() => {
              const result = evaluateBasicCalculation(calculatorDisplay.replace(/−/g, "-"));
              if (result === null) { showToast("请输入有效的计算式"); return; }
              setCalculatorDisplay(String(Math.round((result + Number.EPSILON) * 100000000) / 100000000));
            }}>=</button>
          </div>
        </div>
      </Modal>

      <Modal open={modal === "toolBackup"} title="数据备份" onClose={() => setModal(null)}>
        <div className="tool-form">
          <label className="field-label checkbox-field backup-encrypt-toggle"><input type="checkbox" checked={encryptBackupEnabled} onChange={(event) => setEncryptBackupEnabled(event.target.checked)} /> 加密备份（推荐，防止文件泄露）</label>
          {encryptBackupEnabled && <label className="field-label">备份密码<input type="password" value={backupPassword} onChange={(event) => setBackupPassword(event.target.value)} placeholder="恢复备份时需要输入该密码" /></label>}
          <button className="primary-button full" type="button" onClick={() => void exportData(encryptBackupEnabled)}>{encryptBackupEnabled ? "下载加密备份" : "下载当前完整备份"}</button>
          <button className="secondary-button full" type="button" onClick={() => fileInputRef.current?.click()}>从备份文件恢复</button>
          <button className="secondary-button full" type="button" onClick={exportPreSyncBackup}>下载最近同步前备份</button>
          <button className="secondary-button full" type="button" onClick={exportPreImportBackup}>下载最近导入前备份</button>
          <button className="secondary-button full" type="button" onClick={exportMigrationBackup}>下载升级前备份</button>
        </div>
      </Modal>

      <Modal open={modal === "trash"} title="回收站" onClose={() => setModal(null)} wide>
        <div className="trash-panel">
          <p className="e2e-note">删除的内容会先进回收站，可随时恢复或彻底删除；最多保留最近 50 条。恢复后会自动同步回云端，其他设备也会同步回来。</p>
          <button className="secondary-button full" type="button" onClick={clearTrash}>清空回收站</button>
          {trashEntries.length ? (
            <div className="record-list">
              {trashEntries.map((entry) => {
                const time = new Date(entry.deletedAt).toLocaleString("zh-CN", { hour12: false });
                return (
                  <article className="record-row" key={entry.id}>
                    <div><strong>{TRASH_TYPE_LABEL[entry.type]} · {entry.label}</strong><p>{time}{entry.parentLabel ? ` · 所属：${entry.parentLabel}` : ""}</p></div>
                    <div className="button-row">
                      <button className="secondary-button small" type="button" onClick={() => restoreTrashEntry(entry)}>恢复</button>
                      <button className="danger-text" type="button" onClick={() => { if (window.confirm(`彻底删除“${entry.label}”？此操作不可恢复。`)) { trashRemoveEntry(entry.id); setTrashTick((tick) => tick + 1); showToast("已彻底删除"); } }}>彻底删除</button>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : <EmptyState title="回收站是空的" />}
        </div>
      </Modal>

      <Modal open={modal === "backupPassword"} title="输入备份密码" onClose={() => { setPendingEncryptedBackup(null); setModal(null); }}>
        <div className="tool-form">
          <p className="e2e-note">该备份文件已加密，请输入导出时设置的备份密码。</p>
          <label className="field-label">备份密码<input type="password" value={backupPassword} onChange={(event) => setBackupPassword(event.target.value)} autoFocus /></label>
          <div className="form-actions"><button className="secondary-button" type="button" onClick={() => { setPendingEncryptedBackup(null); setModal(null); }}>取消</button><button className="primary-button" type="button" onClick={confirmDecryptBackup}>解密并继续</button></div>
        </div>
      </Modal>

      <Modal open={modal === "importPreview"} title="恢复数据预览" onClose={() => { setPendingImport(null); setModal(null); }}>
        {pendingImport && <div className="import-preview">
          <div className="import-file-name">{pendingImport.fileName}</div>
          <dl>
            <div><dt>项目</dt><dd>{pendingImport.summary.projects}</dd></div>
            <div><dt>待办与事项</dt><dd>{pendingImport.summary.todos}</dd></div>
            <div><dt>工作记录</dt><dd>{pendingImport.summary.workRecords}</dd></div>
            <div><dt>加班记录</dt><dd>{pendingImport.summary.overtime}</dd></div>
            <div><dt>休假记录</dt><dd>{pendingImport.summary.leave}</dd></div>
          </dl>
          {(pendingImport.summary.projectReplacements > 0 || pendingImport.summary.todoDuplicates > 0) && (
            <div className="import-merge-note">
              {pendingImport.summary.projectReplacements > 0 && <span>{pendingImport.summary.projectReplacements} 个同编号项目将以备份内容更新</span>}
              {pendingImport.summary.todoDuplicates > 0 && <span>{pendingImport.summary.todoDuplicates} 条重复待办将自动去重</span>}
            </div>
          )}
          <div className="form-actions">
            <button className="secondary-button" type="button" onClick={() => { setPendingImport(null); setModal(null); }}>取消</button>
            <button className="primary-button" type="button" onClick={confirmImport}>确认合并恢复</button>
          </div>
        </div>}
      </Modal>

      {!accessGranted && (
        <div className="auth-gate" role="presentation">
          <section className="auth-gate-card" role="dialog" aria-modal="true" aria-label={authMode === "login" ? "登录工作助手" : authMode === "register" ? "注册工作助手" : authMode === "forgot" ? "重置密码" : "邮箱验证码登录"}>
            <div className="auth-gate-brand"><span>办</span><div><strong>工作助手</strong><small>你的个人项目与工作管理台</small></div></div>
            {authResolved ? (
              <>
                <header><h1>{authMode === "login" ? "登录" : authMode === "register" ? "创建账号" : authMode === "forgot" ? "重置密码" : "邮箱验证码登录"}</h1></header>
                {authMode === "otp" ? (
                  <form onSubmit={handleOtpSubmit}>
                    <label className="field-label">邮箱<input name="email" type="email" required autoComplete="email" autoFocus value={otpEmail} onChange={(event) => setOtpEmail(event.target.value)} /></label>
                    {otpSent && <label className="field-label">验证码<input name="code" type="text" inputMode="numeric" autoComplete="one-time-code" value={otpCode} onChange={(event) => setOtpCode(event.target.value)} required placeholder="6 位验证码" /></label>}
                    {authError && <p className="form-error">{authError}</p>}
                    <button className="primary-button full" type="submit">{otpSent ? "登录" : "发送验证码"}</button>
                    {otpSent && <button className="text-button centered" type="button" onClick={() => { setOtpSent(false); setOtpCode(""); setAuthError(""); }}>换个邮箱或重新发送</button>}
                    <button className="text-button centered" type="button" onClick={() => switchAuthMode("login")}>返回密码登录</button>
                  </form>
                ) : (
                  <form onSubmit={authMode === "forgot" ? handleForgotPassword : handleAuth}>
                    <label className="field-label">邮箱<input name="email" type="email" required autoComplete="email" autoFocus /></label>
                    {authMode !== "forgot" && <label className="field-label">密码<input name="password" type="password" minLength={6} required autoComplete={authMode === "login" ? "current-password" : "new-password"} /></label>}
                    {authError && <p className="form-error">{authError}</p>}
                    <button className="primary-button full" type="submit">{authMode === "forgot" ? "发送重置邮件" : authMode === "login" ? "登录" : "注册账号"}</button>
                    {authMode === "forgot" ? (
                      <button className="text-button centered" type="button" onClick={() => switchAuthMode("login")}>返回登录</button>
                    ) : (
                      <>
                        <button className="text-button centered" type="button" onClick={() => switchAuthMode(authMode === "login" ? "register" : "login")}>{authMode === "login" ? "没有账号？注册" : "已有账号？登录"}</button>
                        {authMode === "login" && (
                          <>
                            <button className="text-button centered" type="button" onClick={() => switchAuthMode("forgot")}>忘记密码？</button>
                            <button className="text-button centered" type="button" onClick={() => switchAuthMode("otp")}>邮箱验证码登录</button>
                          </>
                        )}
                      </>
                    )}
                  </form>
                )}
                <div className="auth-divider"><span>或</span></div>
                <button className="guest-entry" type="button" onClick={() => { setGuestMode(true); setAuthError(""); }}>进入独立访客空间</button>
              </>
            ) : <div className="auth-connecting"><span /><strong>正在连接账号服务</strong></div>}
          </section>
        </div>
      )}

      <Modal open={modal === "todoCreate"} title="新增待办" onClose={() => setModal(null)}>
        <form className="todo-editor-form" onSubmit={(event) => {
          event.preventDefault();
          const form = new FormData(event.currentTarget);
          const text = String(form.get("text") || "").trim();
          if (!text) return;
          const linkedProjectId = String(form.get("linkedProjectId") || "");
          const linkedStageIdInput = String(form.get("linkedStageId") || "");
          const linkedStageId = linkedProjectId && projects.some((project) =>
            String(project.id) === linkedProjectId && (project.stages || []).some((stage) => String(stage.id || "") === linkedStageIdInput))
            ? linkedStageIdInput
            : "";
          const eventCategory = form.get("eventCategory") as ProjectEventCategory;
          const todo: Todo = {
            id: makeId(),
            ownerId: activeOwnerRef.current,
            text,
            done: false,
            kind: "todo",
            scheduledDate: String(form.get("scheduledDate") || ""),
            createDate: new Date().toISOString(),
            linkedProjectId: linkedProjectId || undefined,
            linkedEventId: linkedProjectId ? makeId() : undefined,
            eventCategory: eventCategory || "other",
            linkedStageId: linkedStageId || undefined,
          };
          setTodos((items) => [todo, ...items]);
          if (todo.linkedProjectId && todo.linkedEventId) {
            setProjects((items) => reconcileTodoProjectEvent(items, todo, todo.linkedEventId || ""));
          }
          setModal(null);
          showToast("待办已添加");
        }}>
          <label className="field-label">待办内容<textarea name="text" rows={7} required autoFocus={typeof window === "undefined" || !window.matchMedia("(max-width: 760px)").matches} /></label>
          <div className="field-label"><span>事件日期（可选）</span><TodoDateRail value={todoFormDate} onChange={setTodoFormDate} /></div>
          <label className="field-label">关联项目<select name="linkedProjectId" value={todoFormProjectId} onChange={(event) => { setTodoFormProjectId(event.target.value); setTodoFormStageId(""); }}><option value="">不关联</option>{projects.filter((project) => project.status !== "trashed").map((project) => <option key={project.id} value={String(project.id)}>{project.name}</option>)}</select></label>
          <div className="form-grid two">
            <label className="field-label">事件类型<select name="eventCategory" defaultValue="other">{Object.entries(EVENT_CATEGORY_LABEL).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
            <label className="field-label">归入阶段（可选）<select name="linkedStageId" value={todoFormStageId} disabled={!todoFormProjectId} onChange={(event) => setTodoFormStageId(event.target.value)}><option value="">不归入阶段</option>{projects.find((project) => String(project.id) === todoFormProjectId)?.stages.filter((stage) => stage.id).map((stage) => <option key={stage.id} value={String(stage.id)}>{stage.name}</option>)}</select></label>
          </div>
          <div className="form-actions"><button className="secondary-button" type="button" onClick={() => setModal(null)}>取消</button><button className="primary-button" type="submit">添加待办</button></div>
        </form>
      </Modal>

      <Modal open={modal === "todoCompletion"} title="完成待办" onClose={() => { setModal(null); setPendingTodoCompletionId(""); }}>
        {pendingTodoCompletion && <form className="todo-completion-form" onSubmit={(event) => {
          event.preventDefault();
          const recordDate = String(new FormData(event.currentTarget).get("recordDate") || localDateKey());
          completeTodo(todoKey(pendingTodoCompletion), recordDate);
        }}>
          <div className="completion-task"><span>本次完成</span><strong>{pendingTodoCompletion.text}</strong></div>
          <div className="completion-note"><strong>归入工作记录</strong></div>
          <label className="field-label">归入日期<SmartDateInput name="recordDate" defaultValue={localDateKey()} /></label>
          <div className="form-actions"><button className="secondary-button" type="button" onClick={() => { setModal(null); setPendingTodoCompletionId(""); }}>取消</button><button className="primary-button" type="submit">完成并归入记录</button></div>
        </form>}
      </Modal>

      <Modal open={modal === "todoHistory"} title="待办历史归档" onClose={() => setModal(null)}>
        <div className="record-list">
          {doneTodos.length ? doneTodos.map((todo) => {
            const index = todos.indexOf(todo);
            return <article className="record-row" key={todo.id || index}><div><strong>{todo.text}</strong><p>{todo.completedAt ? `完成于 ${todo.completedAt}` : "早期记录"}</p></div><details className="row-menu record-row-menu"><summary aria-label={`${todo.text}历史待办的编辑功能`}>•••</summary><div><button type="button" onClick={() => { setEditingTodoId(todoKey(todo)); setModal("todoEdit"); }}>编辑待办</button><button type="button" onClick={() => toggleTodo(index)}>恢复为未完成</button><button className="danger-text" type="button" onClick={() => removeTodo(index)}>删除待办</button></div></details></article>;
          }) : <EmptyState title="暂无历史记录" />}
        </div>
      </Modal>

      <Modal open={modal === "todoEdit"} title="修改待办" onClose={() => { setModal(null); setEditingTodoId(""); }}>
        {editingTodo && <form className="todo-editor-form" key={editingTodoId} onSubmit={(event) => {
          event.preventDefault();
          const form = new FormData(event.currentTarget);
          const text = String(form.get("text") || "").trim();
          if (!text) return;
          const linkedProjectId = String(form.get("linkedProjectId") || "");
          const linkedStageIdInput = String(form.get("linkedStageId") || "");
          const linkedStageId = linkedProjectId && projects.some((project) =>
            String(project.id) === linkedProjectId && (project.stages || []).some((stage) => String(stage.id || "") === linkedStageIdInput))
            ? linkedStageIdInput
            : "";
          const eventCategory = form.get("eventCategory") as ProjectEventCategory;
          const updatedTodo: Todo = {
            ...editingTodo,
            id: editingTodo.id || makeId(),
            ownerId: activeOwnerRef.current,
            text,
            scheduledDate: String(form.get("scheduledDate") || ""),
            linkedProjectId: linkedProjectId || undefined,
            linkedEventId: linkedProjectId ? editingTodo.linkedEventId || makeId() : undefined,
            eventCategory: eventCategory || "other",
            linkedStageId: linkedStageId || undefined,
          };
          setTodos((items) => items.map((todo) => todoKey(todo) === editingTodoId ? updatedTodo : todo));
          setProjects((items) =>
            reconcileTodoProjectEvent(items, updatedTodo, updatedTodo.linkedEventId || ""));
          setModal(null);
          setEditingTodoId("");
          showToast("待办已修改");
        }}>
          <label className="field-label">待办内容<textarea name="text" rows={7} defaultValue={editingTodo.text} required autoFocus={typeof window === "undefined" || !window.matchMedia("(max-width: 760px)").matches} /></label>
          <div className="field-label"><span>事件日期（可选）</span><TodoDateRail value={todoFormDate} onChange={setTodoFormDate} /></div>
          <label className="field-label">关联项目<select name="linkedProjectId" value={todoFormProjectId} onChange={(event) => { setTodoFormProjectId(event.target.value); setTodoFormStageId(""); }}><option value="">不关联</option>{projects.filter((project) => project.status !== "trashed").map((project) => <option key={project.id} value={String(project.id)}>{project.name}</option>)}</select></label>
          <div className="form-grid two">
            <label className="field-label">事件类型<select name="eventCategory" defaultValue={editingTodoCategory}>{Object.entries(EVENT_CATEGORY_LABEL).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
            <label className="field-label">归入阶段（可选）<select name="linkedStageId" value={todoFormStageId} disabled={!todoFormProjectId} onChange={(event) => setTodoFormStageId(event.target.value)}><option value="">不归入阶段</option>{projects.find((project) => String(project.id) === todoFormProjectId)?.stages.filter((stage) => stage.id).map((stage) => <option key={stage.id} value={String(stage.id)}>{stage.name}</option>)}</select></label>
          </div>
          <div className="form-actions"><button className="secondary-button" type="button" onClick={() => { setModal(null); setEditingTodoId(""); }}>取消</button><button className="primary-button" type="submit">保存修改</button></div>
        </form>}
      </Modal>

      <Modal open={modal === "matter"} title={editingMatter ? "编辑事项" : "新增事项"} onClose={() => { setModal(null); setEditingMatterId(""); }}>
        <form key={`matter_${editingMatterId}`} onSubmit={(event) => {
          event.preventDefault();
          const form = new FormData(event.currentTarget);
          const record: Todo = {
            id: editingMatter?.id || makeId(),
            ownerId: activeOwnerRef.current,
            text: String(form.get("name") || "").trim(),
            done: form.get("status") === "completed",
            kind: editingMatter?.kind === "service"
              ? "service"
              : form.get("kind") as "administrative" | "other",
            stage: String(form.get("stage") || "").trim(),
            note: String(form.get("note") || "").trim(),
            createDate: editingMatter?.createDate || new Date().toISOString(),
            completedAt: form.get("status") === "completed" ? new Date().toISOString() : undefined,
          };
          if (!record.text) return;
          setTodos((items) => editingMatter
            ? items.map((item) => item.id === editingMatter.id ? record : item)
            : [record, ...items]);
          setModal(null);
          setEditingMatterId("");
          showToast("事项已保存");
        }}>
          <label className="field-label">事项名称<input name="name" defaultValue={editingMatter?.text || ""} required autoFocus /></label>
          <div className="form-grid two">
            <label className="field-label">事项类型<select name="kind" defaultValue={editingMatter?.kind === "other" ? "other" : "administrative"} disabled={editingMatter?.kind === "service"}>{editingMatter?.kind === "service" && <option value="service">原服务类事项</option>}<option value="administrative">内部事项</option><option value="other">其他事项</option></select></label>
            <label className="field-label">状态<select name="status" defaultValue={editingMatter?.done ? "completed" : "active"}><option value="active">持续推进</option><option value="completed">已经完成</option></select></label>
          </div>
          <label className="field-label">当前阶段<input name="stage" defaultValue={editingMatter?.stage || ""} /></label>
          <label className="field-label">备注<textarea name="note" defaultValue={editingMatter?.note || ""} rows={4} /></label>
          <div className="form-actions"><button className="secondary-button" type="button" onClick={() => { setModal(null); setEditingMatterId(""); }}>取消</button><button className="primary-button" type="submit">保存事项</button></div>
        </form>
      </Modal>

      <Modal open={modal === "dayOverview"} title={selectedLogDate} onClose={() => { setSearchHit(null); setModal(null); }}>
        <div className="day-project-events day-work-record-view">
          <div className="day-work-record-heading"><strong>工作记录</strong></div>
          {[...(calendar[selectedLogDate] || [])].sort((left, right) =>
            (left.occurredAt || "99:99").localeCompare(right.occurredAt || "99:99") ||
            String(left.createdAt || "").localeCompare(String(right.createdAt || ""))).map((record) => {
              const linkedName = record.linkedProjectId
                ? projects.find((project) => String(project.id) === String(record.linkedProjectId))?.name
                : "";
              const meta = [
                record.amount ? `金额 ${record.amount} 元` : "",
                record.organization ? `单位 ${record.organization}` : "",
                record.contact ? `联系人 ${record.contact}` : "",
                record.nextStep ? `下一步：${record.nextStep}` : "",
                record.dueDate ? `截止 ${record.dueDate}` : "",
              ].filter(Boolean).join(" · ");
              const hit = searchHit?.recordId === record.id ? searchHit : null;
              const contentHit = hit && hit.kind === "content";
              const attachmentHit = hit && hit.kind === "attachment";
              const fieldHit = hit && hit.kind === "field";
              return (
                <div className="day-work-record-item" key={record.id}>
                  <p>· {record.occurredAt ? `${record.occurredAt} ` : ""}{contentHit ? renderTextWithHit(record.content, hit.sentence) : record.content}{linkedName ? `（${linkedName}）` : ""}</p>
                  {meta ? <small className="day-work-meta">{fieldHit ? <mark className="search-hit-target">{meta}</mark> : meta}</small> : null}
                  {record.attachments?.length ? (
                    <div className="work-record-attachment-list">
                      {record.attachments.map((attachment) => (
                        <details className="work-record-attachment" key={attachment.id} open={Boolean(attachmentHit && hit.attachmentId === attachment.id)}>
                          <summary><span>📄 {attachment.name}</span><small>{attachment.content.length} 字</small></summary>
                          <pre>{attachmentHit && hit.attachmentId === attachment.id ? renderTextWithHit(attachment.content, hit.sentence) : attachment.content}</pre>
                        </details>
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            })}
        </div>
        {leaveRecordsForDate(selectedLogDate).length > 0 && (
          <div className="day-project-events day-leave">
            <strong>当天休假</strong>
            {leaveRecordsForDate(selectedLogDate).map((record) => (
              <div key={record.id || `${record.date}_${record.type}_${record.days}`}><span>{record.type}</span><strong>{record.days} 天{record.note ? ` · ${record.note}` : ""}</strong></div>
            ))}
          </div>
        )}
        {activeTodos.some((todo) => todo.scheduledDate === selectedLogDate) && (
          <div className="day-project-events day-todos">
            <strong>当天待办</strong>
            {activeTodos.filter((todo) => todo.scheduledDate === selectedLogDate).map((todo) => <div key={todoKey(todo)}><span>我的待办</span><strong>{todo.text}</strong></div>)}
          </div>
        )}
        {calendarProjectEventsForDate(selectedLogDate).length > 0 && (
          <div className="day-project-events">
            <strong>当天项目事项</strong>
            {calendarProjectEventsForDate(selectedLogDate).map(({ project, event }) => <button key={`${project.id}_${event.id}`} type="button" onClick={() => { setCurrentProjectId(project.id); setProjectTab("timeline"); setModal(null); navigate("projectDetail"); }}><span>{project.name}</span><strong>{event.title}</strong><small>{event.dueDate === selectedLogDate && event.occurredOn !== selectedLogDate ? "当天截止" : EVENT_STATUS_LABEL[event.status]}</small></button>)}
          </div>
        )}
        {!(calendar[selectedLogDate] || []).length && !activeTodos.some((todo) => todo.scheduledDate === selectedLogDate) && !calendarProjectEventsForDate(selectedLogDate).length && <EmptyState title="无记录" />}
      </Modal>

      <Modal open={modal === "dayLog"} title={dayLogMode === "edit" ? "编辑工作记录" : "录入工作记录"} onClose={() => setModal(null)}>
        <form onSubmit={(event) => {
          event.preventDefault();
          const form = new FormData(event.currentTarget);
          const recordDate = String(form.get("recordDate") || selectedLogDate);
          const content = String(form.get("content") || "").trim();
          const occurredAt = String(form.get("occurredAt") || "").trim() || undefined;
          const linkedProjectId = String(form.get("linkedProjectId") || "").trim() || undefined;
          const amount = String(form.get("amount") || "").trim() || undefined;
          const organization = String(form.get("organization") || "").trim() || undefined;
          const contact = String(form.get("contact") || "").trim() || undefined;
          const nextStep = String(form.get("nextStep") || "").trim() || undefined;
          const dueDate = String(form.get("dueDate") || "").trim() || undefined;
          const attachments = draftAttachments.length ? draftAttachments : undefined;
          setCalendar((items) => {
            const next = { ...items };
            const previousDateRecords = [...(next[selectedLogDate] || [])];
            if (dayLogMode === "edit" && editingWorkRecordId) {
              const editingRecord = previousDateRecords.find((record) => record.id === editingWorkRecordId);
              const remainingRecords = previousDateRecords.filter((record) => record.id !== editingWorkRecordId);
              if (remainingRecords.length) next[selectedLogDate] = remainingRecords;
              else delete next[selectedLogDate];
              if (content && editingRecord) {
                const updatedRecord = {
                  ...editingRecord,
                  ownerId: activeOwnerRef.current,
                  content,
                  ...(occurredAt ? { occurredAt } : { occurredAt: undefined }),
                  ...(linkedProjectId ? { linkedProjectId } : { linkedProjectId: undefined }),
                  ...(amount ? { amount } : { amount: undefined }),
                  ...(organization ? { organization } : { organization: undefined }),
                  ...(contact ? { contact } : { contact: undefined }),
                  ...(nextStep ? { nextStep } : { nextStep: undefined }),
                  ...(dueDate ? { dueDate } : { dueDate: undefined }),
                  ...(attachments ? { attachments } : { attachments: undefined }),
                };
                next[recordDate] = [...(next[recordDate] || []), updatedRecord];
              }
              return next;
            }
            if (content) {
              next[recordDate] = [...(next[recordDate] || []), {
                id: makeId(),
                ownerId: activeOwnerRef.current,
                content,
                ...(occurredAt ? { occurredAt } : {}),
                ...(linkedProjectId ? { linkedProjectId } : {}),
                ...(amount ? { amount } : {}),
                ...(organization ? { organization } : {}),
                ...(contact ? { contact } : {}),
                ...(nextStep ? { nextStep } : {}),
                ...(dueDate ? { dueDate } : {}),
                ...(attachments ? { attachments } : {}),
                createdAt: new Date().toISOString(),
              }];
            }
            return next;
          });
          setSelectedLogDate(recordDate);
          setEditingWorkRecordId("");
          setModal(null);
          showToast("工作记录已保存");
        }}>
          <label className="field-label">日期<SmartDateInput name="recordDate" defaultValue={selectedLogDate} required /></label>
          <label className="field-label">发生时间（可选）<input name="occurredAt" type="time" defaultValue={dayLogMode === "edit" && editingWorkRecordId ? calendar[selectedLogDate]?.find((record) => record.id === editingWorkRecordId)?.occurredAt || "" : ""} /></label>
          <label className="field-label">关联项目（可选）<select name="linkedProjectId" defaultValue={dayLogMode === "edit" && editingWorkRecordId ? calendar[selectedLogDate]?.find((record) => record.id === editingWorkRecordId)?.linkedProjectId || "" : ""}><option value="">不关联</option>{projects.filter((project) => project.status !== "trashed").map((project) => <option key={project.id} value={String(project.id)}>{project.name}</option>)}</select></label>
          <details className="advanced-fields work-record-more">
            <summary>更多字段（金额、单位、联系人、下一步、截止）</summary>
            <div className="form-grid two">
              <label className="field-label">金额（元）<input name="amount" type="number" min="0" step="0.01" defaultValue={dayLogMode === "edit" && editingWorkRecordId ? calendar[selectedLogDate]?.find((record) => record.id === editingWorkRecordId)?.amount || "" : ""} /></label>
              <label className="field-label">单位<input name="organization" defaultValue={dayLogMode === "edit" && editingWorkRecordId ? calendar[selectedLogDate]?.find((record) => record.id === editingWorkRecordId)?.organization || "" : ""} /></label>
              <label className="field-label">联系人<input name="contact" defaultValue={dayLogMode === "edit" && editingWorkRecordId ? calendar[selectedLogDate]?.find((record) => record.id === editingWorkRecordId)?.contact || "" : ""} /></label>
              <label className="field-label">下一步<input name="nextStep" defaultValue={dayLogMode === "edit" && editingWorkRecordId ? calendar[selectedLogDate]?.find((record) => record.id === editingWorkRecordId)?.nextStep || "" : ""} /></label>
              <label className="field-label">截止日期<SmartDateInput name="dueDate" defaultValue={dayLogMode === "edit" && editingWorkRecordId ? calendar[selectedLogDate]?.find((record) => record.id === editingWorkRecordId)?.dueDate || "" : ""} /></label>
            </div>
          </details>
          <div className="work-record-attachments">
            <div className="attachments-heading">
              <strong>文件内容（不占空间）</strong>
              <div className="attachments-actions">
                <button className="secondary-button small" type="button" onClick={() => setPasteEditorOpen((open) => !open)}>＋ 粘贴文本</button>
                <button className="secondary-button small" type="button" onClick={() => attachmentInputRef.current?.click()}>＋ 从文件添加</button>
              </div>
            </div>
            <input ref={attachmentInputRef} type="file" multiple accept=".txt,.md,.markdown,.csv,.json,.xml,.log,.js,.ts,.jsx,.tsx,.html,.css,.yml,.yaml,.ini,.conf,.sql,.py,.java,.c,.cpp,.h,.sh,.bat,.ps1,.docx,.xlsx,.pptx,.doc,.wps,.et,.dps,.pdf,text/*" hidden onChange={handleAttachmentFiles} />
            {pasteEditorOpen && (
              <div className="paste-editor">
                <input className="paste-name" placeholder="名称（可选，默认“粘贴文本_日期”）" value={pasteName} onChange={(event) => setPasteName(event.target.value)} aria-label="粘贴文本名称" />
                <textarea className="paste-text" placeholder="把文件中的文字复制到这里粘贴，任意格式（含 .wps/.doc/PDF）都能保存" value={pasteText} onChange={(event) => setPasteText(event.target.value)} rows={5} aria-label="粘贴的文字内容" />
                <div className="paste-actions">
                  <button className="secondary-button small" type="button" onClick={() => { setPasteEditorOpen(false); setPasteName(""); setPasteText(""); }}>取消</button>
                  <button className="primary-button small" type="button" onClick={addPastedAttachment}>保存文本</button>
                </div>
              </div>
            )}
            {draftAttachments.length ? (
              <div className="attachment-list">
                {draftAttachments.map((attachment) => (
                  <div className="attachment-item" key={attachment.id}>
                    <div><strong>{attachment.name}</strong><small>{attachment.content.length} 字</small></div>
                    <button className="danger-text" type="button" onClick={() => setDraftAttachments((items) => items.filter((item) => item.id !== attachment.id))}>移除</button>
                  </div>
                ))}
              </div>
            ) : <p className="compact-empty">支持直接读取 txt、md、Word(.docx)、Excel(.xlsx)、PPT(.pptx) 的文字；旧版 .doc/.wps 等可另存为 docx，或直接用“粘贴文本”保存。</p>}
          </div>
          <label className="field-label">工作记录<textarea name="content" defaultValue={dayLogMode === "edit" && editingWorkRecordId ? calendar[selectedLogDate]?.find((record) => record.id === editingWorkRecordId)?.content || "" : ""} rows={8} required autoFocus /></label>
          <div className="form-actions"><button className="secondary-button" type="button" onClick={() => setModal(null)}>取消</button><button className="primary-button" type="submit">保存记录</button></div>
        </form>
      </Modal>

      <Modal open={modal === "createProject"} title="新建项目" onClose={() => { setModal(null); setNewProjectEntryMode("full"); }}>
        <form className="project-create-form" onSubmit={(event) => {
          event.preventDefault();
          const form = new FormData(event.currentTarget);
          const name = String(form.get("name") || "").trim();
          if (!name) return;
          const selectedKind = form.get("kind") as "special" | "odd" | "serviceSpecial" | "serviceInternal";
          const isService = selectedKind === "serviceSpecial" || selectedKind === "serviceInternal";
          const kind = selectedKind === "special" || selectedKind === "serviceSpecial" ? "special" : "odd";
          const project = createProjectFromTemplate({
            name,
            kind,
            projectClass: isService ? "service" : "engineering",
            heritage: !isService && kind === "special" && form.get("heritage") === "yes",
            fundingMode: form.get("fundingMode") as "single" | "mixed",
            estimatedAmount: String(form.get("estimatedAmount") || ""),
            fundingSource: form.get("fundingSource") as "unit" | "district" | "cultural" | "other",
            startStageName: newProjectEntryMode === "current"
              ? String(form.get("startStageName") || "")
              : undefined,
          });
          setProjects((items) => [project, ...items]);
          setModal(null);
          setCurrentProjectId(project.id);
          setProjectTab("overview");
          navigate("projectDetail");
          showToast(newProjectEntryMode === "current" ? "项目已从当前阶段开始记录" : "项目已创建");
        }}>
          <label className="field-label">项目名称<input name="name" required autoFocus /></label>
          <label className="field-label">项目类型<select name="kind" value={newProjectKind} onChange={(event) => {
            const kind = event.target.value as "special" | "odd" | "serviceSpecial" | "serviceInternal";
            setNewProjectKind(kind);
            setNewProjectHeritage(kind === "special");
          }}><option value="special">工程项目 · 专项资金工程</option><option value="odd">工程项目 · 内部资金工程</option><option value="serviceSpecial">服务项目 · 专项资金服务</option><option value="serviceInternal">服务项目 · 内部资金服务</option></select></label>
          <label className="field-label">申报预算（元）<input name="estimatedAmount" type="number" min="0" step="0.01" /></label>
          {newProjectKind === "special" || newProjectKind === "serviceSpecial" ? <div className="conditional-fields">
            <label className="field-label">专项资金来源<select name="fundingSource" defaultValue="cultural"><option value="cultural">文物专项资金</option><option value="district">区财政资金</option><option value="other">其他专项资金</option><option value="unit">单位资金参与</option></select></label>
            <label className="field-label">资金结构<select name="fundingMode" defaultValue="single"><option value="single">单一资金</option><option value="mixed">拼盘资金</option></select></label>
            {newProjectKind === "special" && <label className="field-label">是否启用文物口监督站流程<select name="heritage" value={newProjectHeritage ? "yes" : "no"} onChange={(event) => setNewProjectHeritage(event.target.value === "yes")}><option value="yes">是，加入监督站节点</option><option value="no">否</option></select></label>}
          </div> : null}
          <label className="field-label">从哪里开始记录<select value={newProjectEntryMode} onChange={(event) => setNewProjectEntryMode(event.target.value as "full" | "current")}><option value="full">从项目开始，逐步记录</option><option value="current">从当前阶段开始，前期暂不补录</option></select></label>
          {newProjectEntryMode === "current" && (
            <label className="field-label">目前处于哪个阶段<select name="startStageName" defaultValue={newProjectStageNames[0]}>{newProjectStageNames.map((name) => <option key={name} value={name}>{name}</option>)}</select></label>
          )}
          <div className="form-actions"><button className="secondary-button" type="button" onClick={() => setModal(null)}>取消</button><button className="primary-button" type="submit">创建并进入项目</button></div>
        </form>
      </Modal>

      <Modal open={modal === "projectRecycleBin"} title="项目回收站" onClose={() => setModal(null)} wide>
        <div className="record-list">
          {projects.some((project) => project.status === "trashed") ? projects.filter((project) => project.status === "trashed").map((project) => (
            <article className="record-row" key={project.id}>
              <div><span className={`project-type ${project.type}`}>{projectTypeLabel(project)}</span><strong>{project.name}</strong></div>
              <div className="row-actions">
                <button type="button" onClick={() => updateProject(project.id, (item) => ({ ...item, status: "active" }))}>恢复项目</button>
                 <button className="danger-text" type="button" onClick={() => {
                   if (window.confirm(`确定永久删除“${project.name}”吗？永久删除后只能从备份文件恢复。`)) {
                     setProjects((items) => items.filter((item) => String(item.id) !== String(project.id)));
                     showToast("项目已永久删除");
                   }
                 }}>永久删除</button>
              </div>
            </article>
          )) : <EmptyState title="回收站是空的" />}
        </div>
      </Modal>

      <Modal open={modal === "renameProject"} title="修改项目名称" onClose={() => setModal(null)}>
        <form onSubmit={(event) => {
          event.preventDefault();
          const name = String(new FormData(event.currentTarget).get("name") || "").trim();
          if (name) updateCurrentProject((project) => ({ ...project, name }));
          setModal(null);
        }}>
          <label className="field-label">项目名称<input name="name" defaultValue={currentProject?.name} required autoFocus /></label>
          <div className="form-actions"><button className="secondary-button" type="button" onClick={() => setModal(null)}>取消</button><button className="primary-button" type="submit">保存</button></div>
        </form>
      </Modal>

      <Modal open={modal === "projectEvent"} title={editingProjectEvent ? "修改事件记录" : "记录事件"} onClose={() => { setModal(null); setEditingProjectEventId(""); }} wide>
        <form key={`project_event_${editingProjectEventId || "new"}`} onSubmit={(event) => {
          event.preventDefault();
          const form = new FormData(event.currentTarget);
          const title = String(form.get("title") || "").trim();
          if (!title) return;
          const amount = String(form.get("amount") || "");
          const category = form.get("category") as ProjectEventCategory;
          const sourceStageId = String(form.get("sourceStageId") || "");
          const customLabels = form.getAll("customLabel").map(String);
          const customTypes = form.getAll("customType").map(String) as FieldType[];
          const customValues = form.getAll("customValue").map(String);
          const nextStepInput = String(form.get("nextStep") || "").trim();
          const automaticNegotiationStep =
            category === "negotiation" && safeNumber(amount) > 0
              ? safeNumber(amount) >= 50000
                ? "金额变化达到5万元，建议先走党组会"
                : "金额变化低于5万元，建议先走主任办公会"
              : "";
          const record: ProjectEvent = {
            id: editingProjectEvent?.id || makeId(),
            occurredOn: String(form.get("occurredOn") || localDateKey()),
            recordedAt: editingProjectEvent?.recordedAt || new Date().toISOString(),
            title,
            note: String(form.get("note") || "").trim(),
            status: form.get("status") as ProjectEventStatus,
            category,
            amount,
            organization: String(form.get("organization") || "").trim(),
            contact: String(form.get("contact") || "").trim(),
            nextStep: nextStepInput || automaticNegotiationStep,
            dueDate: String(form.get("dueDate") || ""),
            completedAt: form.get("status") === "done" ? editingProjectEvent?.completedAt || new Date().toISOString() : undefined,
            sourceStageId: sourceStageId || undefined,
            fields: customLabels.map((label, index) => ({
              id: editingProjectEvent?.fields?.[index]?.id || makeId(),
              label: label.trim(),
              type: customTypes[index] || "text",
              value: customValues[index] || "",
            })).filter((field) => field.label || field.value),
          };
          updateCurrentProject((project) => ({
            ...project,
            events: editingProjectEvent
              ? (project.events || []).map((item) => item.id === editingProjectEvent.id ? record : item)
              : [record, ...(project.events || [])],
            stages: project.stages,
            nextAction: record.nextStep ? {
              title: record.nextStep,
              status: record.status === "waiting" ? "waiting" : "todo",
              dueDate: record.dueDate,
              waitingFor: record.status === "waiting" ? record.organization : undefined,
            } : project.nextAction,
          }));
          setModal(null);
          setEditingProjectEventId("");
          showToast(editingProjectEvent ? "事件记录已修改" : "已保存事件记录");
        }}>
          <div className="form-grid two">
            <label className="field-label">发生日期<SmartDateInput name="occurredOn" defaultValue={editingProjectEvent?.occurredOn || localDateKey()} required /></label>
            <label className="field-label">状态<select name="status" defaultValue={editingProjectEvent?.status || "done"}>{Object.entries(EVENT_STATUS_LABEL).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
          </div>
          <label className="field-label">发生了什么<input name="title" defaultValue={editingProjectEvent?.title || ""} required autoFocus /></label>
          <div className="form-grid two">
            <label className="field-label">事件类型<select name="category" defaultValue={editingProjectEvent?.category || "other"}>{Object.entries(EVENT_CATEGORY_LABEL).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
            <label className="field-label">涉及金额（元）<input name="amount" type="number" step="0.01" defaultValue={editingProjectEvent?.amount || ""} /></label>
            <label className="field-label">归入阶段（可选）<select name="sourceStageId" defaultValue={editingProjectEvent?.sourceStageId || ""}><option value="">不归入阶段，只保留为事件</option>{currentProject?.stages.map((stage) => <option key={stage.id || stage.name} value={stage.id}>{stage.name}</option>)}</select></label>
            <label className="field-label">相关单位／部门<input name="organization" defaultValue={editingProjectEvent?.organization || ""} /></label>
            <label className="field-label">联系人<input name="contact" defaultValue={editingProjectEvent?.contact || ""} /></label>
          </div>
          <label className="field-label">备注<textarea name="note" defaultValue={editingProjectEvent?.note || ""} rows={4} /></label>
          <details className="advanced-fields" open={Boolean(editingProjectEvent?.nextStep || editingProjectEvent?.dueDate || editingProjectEvent?.fields?.length)}>
            <summary>设置下一步和自定义信息</summary>
            <div className="form-grid two">
              <label className="field-label">下一步<input name="nextStep" defaultValue={editingProjectEvent?.nextStep || ""} /></label>
            <label className="field-label">截止日期<SmartDateInput name="dueDate" defaultValue={editingProjectEvent?.dueDate || ""} /></label>
            </div>
            <EventCustomFields initialFields={editingProjectEvent?.fields || []} />
          </details>
          <div className="form-actions"><button className="secondary-button" type="button" onClick={() => { setModal(null); setEditingProjectEventId(""); }}>取消</button><button className="primary-button" type="submit">{editingProjectEvent ? "保存修改" : "保存事件"}</button></div>
        </form>
      </Modal>

      <Modal open={modal === "nextAction"} title="设置项目下一步" onClose={() => setModal(null)}>
        <form key={`${currentProject?.id}_${currentProject?.nextAction?.title}`} onSubmit={(event) => {
          event.preventDefault();
          const form = new FormData(event.currentTarget);
          const title = String(form.get("title") || "").trim();
          updateCurrentProject((project) => ({
            ...project,
            nextAction: title ? {
              title,
              status: form.get("status") as "todo" | "waiting" | "paused",
              dueDate: String(form.get("dueDate") || ""),
              waitingFor: String(form.get("waitingFor") || ""),
              note: String(form.get("note") || ""),
            } : undefined,
          }));
          setModal(null);
          showToast(title ? "项目下一步已更新" : "已恢复流程自动建议");
        }}>
          <label className="field-label">下一步做什么<input name="title" defaultValue={currentProject?.nextAction?.title || getSuggestedNextAction(currentProject || ({ stages: [] } as unknown as Project))?.title || ""} autoFocus /></label>
          <div className="form-grid two">
            <label className="field-label">状态<select name="status" defaultValue={currentProject?.nextAction?.status || "todo"}><option value="todo">待处理</option><option value="paused">暂停</option></select></label>
          <label className="field-label">截止日期<SmartDateInput name="dueDate" defaultValue={currentProject?.nextAction?.dueDate || ""} /></label>
          </div>
          <label className="field-label">反馈方／单位<input name="waitingFor" defaultValue={currentProject?.nextAction?.waitingFor || ""} /></label>
          <label className="field-label">备注<textarea name="note" rows={3} defaultValue={currentProject?.nextAction?.note || ""} /></label>
          <div className="form-actions"><button className="secondary-button" type="button" onClick={() => setModal(null)}>取消</button><button className="primary-button" type="submit">保存</button></div>
        </form>
      </Modal>

      <Modal open={modal === "projectSettings"} title="项目设置" onClose={() => setModal(null)}>
        {currentProject && <form key={`settings_${currentProject.id}`} onSubmit={(event) => {
          event.preventDefault();
          const form = new FormData(event.currentTarget);
          const projectClass = form.get("projectClass") as "engineering" | "service";
          const projectKind = form.get("projectKind") as "special" | "odd";
          updateCurrentProject((project) => ({
            ...project,
            name: String(form.get("name") || project.name).trim(),
            status: form.get("status") as Project["status"],
            type: projectKind,
            projectKind,
            projectClass,
            heritage: projectClass === "engineering" && form.get("heritage") === "yes",
            fundingMode: form.get("fundingMode") as "single" | "mixed",
            estimatedAmount: String(form.get("estimatedAmount") || ""),
            budgetReviewAmount: String(form.get("budgetReviewAmount") || ""),
          }));
          setModal(null);
          showToast("项目设置已保存");
        }}>
          <label className="field-label">项目名称<input name="name" defaultValue={currentProject.name} required /></label>
          <div className="form-grid two">
            <label className="field-label">项目状态<select name="status" defaultValue={currentProject.status || "active"}><option value="active">推进中</option><option value="paused">暂停</option><option value="completed">已完成</option></select></label>
            <label className="field-label">项目大类<select name="projectClass" defaultValue={currentProject.projectClass || "engineering"}><option value="engineering">工程项目</option><option value="service">服务项目</option></select></label>
            <label className="field-label">{currentProject.projectClass === "service" ? "服务资金类别" : "工程资金类别"}<select name="projectKind" defaultValue={currentProject.projectKind || (currentProject.type === "special" ? "special" : "odd")}><option value="special">{currentProject.projectClass === "service" ? "专项资金服务" : "专项资金工程"}</option><option value="odd">{currentProject.projectClass === "service" ? "内部资金服务" : "内部资金工程"}</option></select></label>
            <label className="field-label">资金结构<select name="fundingMode" defaultValue={currentProject.fundingMode || "single"}><option value="single">单一资金</option><option value="mixed">拼盘资金</option></select></label>
            <label className="field-label">文物口监督站流程<select name="heritage" defaultValue={currentProject.heritage ? "yes" : "no"}><option value="yes">启用</option><option value="no">不启用</option></select></label>
            <label className="field-label">申报预算（元）<input name="estimatedAmount" type="number" min="0" step="0.01" defaultValue={currentProject.estimatedAmount || ""} /></label>
            <label className="field-label">{currentProject.projectClass === "service" ? "项目预算（元）" : "预算评审金额／最终批复金额（元）"}<input name="budgetReviewAmount" type="number" min="0" step="0.01" defaultValue={currentProject.budgetReviewAmount || ""} /></label>
          </div>
          <div className="form-actions"><button className="secondary-button" type="button" onClick={() => setModal(null)}>取消</button><button className="primary-button" type="submit">保存设置</button></div>
        </form>}
      </Modal>

      <Modal open={modal === "funding"} title={editingFundingSource ? "修改资金来源" : "添加资金来源"} onClose={() => { setModal(null); setEditingFundingSourceId(""); }}>
        <form key={`funding_${editingFundingSourceId || "new"}`} onSubmit={(event) => {
          event.preventDefault();
          const form = new FormData(event.currentTarget);
          const source = form.get("source") as "unit" | "district" | "cultural" | "other";
          const fundingId = editingFundingSource?.id || makeId();
          updateCurrentProject((project) => ({
            ...project,
            funds: editingFundingSource
              ? (project.funds || []).map((fund) => fund.id === editingFundingSource.id ? {
                  ...fund,
                  source,
                  name: String(form.get("name") || "").trim() || (source === "unit" ? "单位自有资金" : source === "district" ? "区财政资金" : source === "cultural" ? "文物专项资金" : "其他专项资金"),
                } : fund)
              : [...(project.funds || []), {
                  id: fundingId,
                  source,
                  name: String(form.get("name") || "").trim() || (source === "unit" ? "单位自有资金" : source === "district" ? "区财政资金" : source === "cultural" ? "文物专项资金" : "其他专项资金"),
                  approvedAmount: "",
                  receivedAmount: "",
                  annualPlans: [],
                }],
          }));
          if (editingFundingSource) {
            setModal(null);
            setEditingFundingSourceId("");
            showToast("资金来源已修改");
            return;
          }
          setEditingFundingSourceId(fundingId);
          setEditingFundingPlanId("");
          setModal("fundingYear");
        }}>
          <label className="field-label">资金来源<select name="source" defaultValue={editingFundingSource?.source || "cultural"}><option value="cultural">文物专项资金</option><option value="district">区财政资金</option><option value="unit">单位自有资金</option><option value="other">其他专项资金</option></select></label>
          <label className="field-label">来源名称<input name="name" defaultValue={editingFundingSource?.name || ""} /></label>
          <div className="form-actions"><button className="secondary-button" type="button" onClick={() => { setModal(null); setEditingFundingSourceId(""); }}>取消</button><button className="primary-button" type="submit">{editingFundingSource ? "保存修改" : "下一步：年度计划"}</button></div>
        </form>
      </Modal>

      <Modal open={modal === "fundingYear"} title={editingFundingPlan ? "修改年度资金计划" : "添加年度资金计划"} onClose={() => setModal(null)}>
        {currentProject && <form key={`${editingFundingSourceId}_${editingFundingPlanId || "new"}`} onSubmit={(event) => {
          event.preventDefault();
          const form = new FormData(event.currentTarget);
          const year = String(form.get("year") || "").trim();
          if (!/^\d{4}$/.test(year)) {
            showToast("请填写4位年度，例如2026");
            return;
          }
          const duplicate = currentProject.funds
            ?.find((fund) => fund.id === editingFundingSourceId)
            ?.annualPlans?.some((plan) => plan.year === year && plan.id !== editingFundingPlanId);
          if (duplicate) {
            showToast("该资金来源已经有这个年度的计划");
            return;
          }
          const planId = editingFundingPlanId || makeId();
          const nextPlan = {
            id: planId,
            year,
            requestedAmount: String(form.get("requestedAmount") || ""),
            receivedAmount: String(form.get("requestedAmount") || ""),
            receivedOn: String(form.get("receivedOn") || ""),
            deadline: String(form.get("deadline") || `${year}-11-30`),
            note: String(form.get("note") || "").trim(),
          };
          updateCurrentProject((project) => ({
            ...project,
            funds: (project.funds || []).map((fund) => fund.id === editingFundingSourceId ? {
              ...fund,
              annualPlans: editingFundingPlanId
                ? (fund.annualPlans || []).map((plan) => plan.id === editingFundingPlanId ? nextPlan : plan)
                : [...(fund.annualPlans || []), nextPlan],
            } : fund),
          }));
          setModal(null);
          setEditingFundingSourceId("");
          setEditingFundingPlanId("");
          showToast("年度资金计划已保存");
        }}>
          <div className="form-grid two">
            <label className="field-label">年度<input name="year" type="number" min="2000" max="2100" step="1" defaultValue={editingFundingPlan?.year || localDateKey().slice(0, 4)} required /></label>
            <label className="field-label">当年支付截止日期<SmartDateInput name="deadline" defaultValue={editingFundingPlan?.deadline || ""} /></label>
            <label className="field-label">当年申请／下达金额（元）<input name="requestedAmount" type="number" min="0" step="0.01" defaultValue={editingFundingPlan?.requestedAmount || ""} /></label>
            <label className="field-label">到账日期<SmartDateInput name="receivedOn" defaultValue={editingFundingPlan?.receivedOn || ""} /></label>
          </div>
          <label className="field-label">备注<input name="note" defaultValue={editingFundingPlan?.note || ""} /></label>
          <div className="form-actions"><button className="secondary-button" type="button" onClick={() => setModal(null)}>取消</button><button className="primary-button" type="submit">保存年度计划</button></div>
        </form>}
      </Modal>

      <Modal open={modal === "fundingPaymentPreview"} title={fundingPaymentPreview ? `${fundingPaymentPreview.plan.year} 年度 · 已关联支付` : "已关联支付"} onClose={() => { setModal(null); setFundingPaymentPreviewPlanId(""); }}>
        {fundingPaymentPreview ? <div className="funding-payment-preview">
          <dl className="funding-payment-preview-summary">
            <div><dt>资金来源</dt><dd>{fundingPaymentPreview.fund.name}</dd></div>
            <div><dt>已关联支付</dt><dd>{fundingPlanPaid(currentProject as Project, fundingPaymentPreview.plan.id).toLocaleString("zh-CN")} 元</dd></div>
            <div><dt>关联笔数</dt><dd>{fundingPaymentPreview.payments.length} 笔</dd></div>
          </dl>
          <div className="funding-payment-preview-list">
            {fundingPaymentPreview.payments.map((link) => <article key={link.payment.id}>
              <div><time>交单 {link.payment.date}</time><strong>{link.contractName || "未命名合同"}</strong><small>{link.procurementName}{link.party ? ` · ${link.party}` : ""}</small></div>
              <div><span>{PAYMENT_KIND_LABEL[link.payment.kind]}</span><b>{safeNumber(link.payment.amount).toLocaleString("zh-CN")} 元</b>{link.payment.note && <small>{link.payment.note}</small>}</div>
            </article>)}
          </div>
        </div> : <EmptyState title="暂无关联支付" />}
      </Modal>

      <Modal open={modal === "fundingUnpaidPreview"} title={fundingUnpaidPreview ? `${fundingUnpaidPreview.plan.year} 年度 · 已关联未支付` : "已关联未支付"} onClose={() => { setModal(null); setFundingUnpaidPreviewPlanId(""); }}>
        {fundingUnpaidPreview ? <div className="funding-payment-preview">
          <dl className="funding-payment-preview-summary">
            <div><dt>资金来源</dt><dd>{fundingUnpaidPreview.fund.name}</dd></div>
            <div><dt>待交单节点</dt><dd>{fundingUnpaidPreview.schedules.length} 笔</dd></div>
            <div><dt>计划待支付</dt><dd>{fundingUnpaidPreview.amount.toLocaleString("zh-CN")} 元</dd></div>
          </dl>
          <div className="funding-payment-preview-list">
            {fundingUnpaidPreview.schedules.map((item) => <article key={item.plan.id}>
              <div><strong>{item.contractName || "未命名合同"}</strong><small>{item.procurementName}{item.party ? ` · ${item.party}` : ""}</small></div>
              <div><span>{item.planIndex === 0 && (item.plan.ratio === "100" || item.plan.ratio === "100.0") ? "一次性 · 100%" : `第 ${item.planIndex + 1} 笔 · ${item.plan.ratio}%`}</span><b>{safeNumber(item.amount).toLocaleString("zh-CN")} 元</b></div>
            </article>)}
          </div>
        </div> : <EmptyState title="暂无已关联未支付节点" />}
      </Modal>

      <Modal open={modal === "fundingClawbackPreview"} title={fundingClawbackPreview ? `${fundingClawbackPreview.plan.year} 年度 · 资金余额构成` : "资金余额构成"} onClose={() => { setModal(null); setFundingClawbackPreviewPlanId(""); }}>
        {fundingClawbackPreview ? <div className="funding-payment-preview">
          <dl className="funding-payment-preview-summary">
            <div><dt>资金来源</dt><dd>{fundingClawbackPreview.fund.name}</dd></div>
            <div><dt>下达金额</dt><dd>{fundingClawbackPreview.received.toLocaleString("zh-CN")} 元</dd></div>
            <div><dt>已关联支付</dt><dd>{fundingClawbackPreview.paid.toLocaleString("zh-CN")} 元</dd></div>
            <div><dt>{localDateKey() > fundingClawbackPreview.plan.deadline ? "预计收回" : "尚待支付"}</dt><dd>{fundingClawbackPreview.remaining.toLocaleString("zh-CN")} 元</dd></div>
          </dl>
          <div className="funding-payment-preview-list">
            {fundingClawbackPreview.payments.map((item) => <article key={item.payment.id}>
              <div><time>{item.payment.date}</time><strong>{item.contractName || "未命名合同"}</strong><small>{item.procurementName}</small></div>
              <div><span>从下达金额中扣减</span><b>- {safeNumber(item.payment.amount).toLocaleString("zh-CN")} 元</b></div>
            </article>)}
          </div>
        </div> : <EmptyState title="暂无资金余额" />}
      </Modal>

      <Modal open={modal === "procurement"} title={editingProcurement ? "修改采购包" : "新建采购包"} onClose={() => { setModal(null); setEditingProcurementId(""); }} wide>
        <form key={`procurement_${editingProcurementId || "new"}`} onSubmit={(event) => {
          event.preventDefault();
          const form = new FormData(event.currentTarget);
          const minutesPrefix = String(form.get("minutesPrefix") || "").trim();
          const minutesNumber = String(form.get("minutesNumber") || "").replace(/\D/g, "").slice(0, 2);
          const nextProcurement: ProcurementPackage = {
            id: editingProcurement?.id || makeId(),
            name: String(form.get("name") || "").trim(),
            kind: form.get("kind") as ProcurementPackage["kind"],
            status: form.get("status") as ProjectEventStatus,
            contracts: editingProcurement?.contracts || [],
            basis: (form.get("basis") as ProcurementBasisKind) || undefined,
            minutesRef: minutesPrefix ? (minutesNumber ? `${minutesPrefix}${minutesNumber}号` : minutesPrefix) : undefined,
            procurementForm: (form.get("procurementForm") as ProcurementForm) || undefined,
            announceDate: String(form.get("announceDate") || ""),
            evaluationDate: String(form.get("evaluationDate") || ""),
            awardAnnounceDate: String(form.get("awardAnnounceDate") || ""),
            awardLink: String(form.get("awardLink") || "").trim() || undefined,
            announceLink: String(form.get("announceLink") || "").trim() || undefined,
            procurementResult: (form.get("procurementResult") as ProcurementResult) || undefined,
            intentAnnounceDate: String(form.get("intentAnnounceDate") || ""),
            approvalDocName: String(form.get("approvalDocName") || "").trim() || undefined,
            approvalDocDate: String(form.get("approvalDocDate") || ""),
          };
          updateCurrentProject((project) => ({
            ...project,
            procurements: editingProcurement
              ? (project.procurements || []).map((item) => item.id === editingProcurement.id ? nextProcurement : item)
              : [...(project.procurements || []), nextProcurement],
          }));
          setModal(null);
          setEditingProcurementId("");
          showToast(editingProcurement ? "采购包已修改" : "采购包已建立");
        }}>
          <label className="field-label">采购包名称<input name="name" defaultValue={editingProcurement?.name || ""} required autoFocus /></label>
          <div className="form-grid two">
            <label className="field-label">类型<select name="kind" defaultValue={editingProcurement?.kind || "design"}><option value="design">设计</option><option value="supervision">监理</option><option value="cost">造价咨询</option><option value="agency">招标代理</option><option value="construction">施工</option><option value="other">其他</option></select></label>
            <label className="field-label">状态<select name="status" defaultValue={editingProcurement?.status || "todo"}>{Object.entries(EVENT_STATUS_LABEL).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
          </div>
          <div className="form-grid two">
            <label className="field-label">采购依据<select name="basis" value={procurementBasis} onChange={(event) => setProcurementBasis(event.target.value as ProcurementBasisKind | "")}><option value="">未选择</option><option value="meeting">会议纪要</option><option value="approval">请示签批</option></select></label>
            <label className="field-label">采购形式<select name="procurementForm" value={procurementForm} onChange={(event) => setProcurementForm(event.target.value as ProcurementForm | "")}><option value="">未选择</option><option value="direct">直接委托</option><option value="compare">比选</option><option value="consultation">竞争性磋商</option><option value="openTender">公开招标</option></select></label>
          </div>
          {procurementBasis === "meeting" && (
            <label className="field-label">会议纪要编号
              <span className="minutes-ref-input">
                <input name="minutesPrefix" aria-label="会议纪要前缀（含年份）" defaultValue={splitMinutesRef(editingProcurement?.minutesRef).prefix || `圆党会〔${localDateKey().slice(0, 4)}〕`} placeholder="圆党会〔2026〕" />
                <input name="minutesNumber" aria-label="会议纪要编号数字" inputMode="numeric" autoComplete="off" defaultValue={splitMinutesRef(editingProcurement?.minutesRef).number} maxLength={2} pattern="\d{0,2}" />
                <i>号</i>
              </span>
            </label>
          )}
          {procurementBasis === "approval" && (
            <div className="form-grid two">
              <label className="field-label">请示文件名称<input name="approvalDocName" defaultValue={editingProcurement?.approvalDocName || ""} /></label>
              <label className="field-label">请示日期<SmartDateInput name="approvalDocDate" defaultValue={editingProcurement?.approvalDocDate || ""} /></label>
            </div>
          )}
          {procurementForm && procurementForm !== "direct" && (
            <>
              {(procurementForm === "consultation" || procurementForm === "openTender") && (
                <label className="field-label">意向公开日期<SmartDateInput name="intentAnnounceDate" defaultValue={editingProcurement?.intentAnnounceDate || ""} /></label>
              )}
              <div className="form-grid two">
                <label className="field-label">发布采购公告日期<SmartDateInput name="announceDate" defaultValue={editingProcurement?.announceDate || ""} /></label>
                <label className="field-label">评标日期<SmartDateInput name="evaluationDate" defaultValue={editingProcurement?.evaluationDate || ""} /></label>
                <label className="field-label">中标公示日期<SmartDateInput name="awardAnnounceDate" defaultValue={editingProcurement?.awardAnnounceDate || ""} /></label>
                <label className="field-label">采购结果<select name="procurementResult" defaultValue={editingProcurement?.procurementResult || ""}><option value="">未选择</option><option value="normal">正常</option><option value="rejected">流标</option><option value="voided">废标</option></select></label>
              </div>
              <label className="field-label">公告链接<input name="announceLink" type="url" defaultValue={editingProcurement?.announceLink || ""} placeholder="https://…" /></label>
              <label className="field-label">中标公示链接<input name="awardLink" type="url" defaultValue={editingProcurement?.awardLink || ""} placeholder="https://…" /></label>
            </>
          )}
          <div className="form-actions"><button className="secondary-button" type="button" onClick={() => { setModal(null); setEditingProcurementId(""); }}>取消</button><button className="primary-button" type="submit">{editingProcurement ? "保存修改" : "建立采购包"}</button></div>
        </form>
      </Modal>

      <Modal open={modal === "contract"} title={editingContract ? "修改合同" : "添加合同"} onClose={() => { setModal(null); setEditingProcurementId(""); setEditingContractId(""); }}>
        <form key={`contract_${editingProcurementId}_${editingContractId || "new"}`} onSubmit={(event) => {
          event.preventDefault();
          const form = new FormData(event.currentTarget);
          if (editingContract && editingContract.payments.length > contractInstallmentCount) {
            showToast("付款次数不能少于已有付款记录");
            return;
          }
          const amountStatus = form.get("amountStatus") as "confirmed" | "pending";
          if (amountStatus === "pending" && editingContract?.payments.length) {
            showToast("已有付款记录的合同不能改为金额待定");
            return;
          }
          const contractAmount = String(form.get("amount") || "");
          const planAmountTotal = amountStatus === "confirmed" ? Array.from({ length: contractInstallmentCount }, (_, index) => safeNumber(String(form.get(`planAmount_${index}`) || "")))
            .reduce((sum, amount) => sum + amount, 0) : 0;
          if (amountStatus === "confirmed" && safeNumber(contractAmount) <= 0) {
            showToast("请先填写合同金额");
            return;
          }
          if (amountStatus === "confirmed" && Math.abs(planAmountTotal - safeNumber(contractAmount)) > 0.01) {
            showToast("各次付款金额合计应等于合同金额");
            return;
          }
          const paymentPlan = amountStatus === "confirmed" ? Array.from({ length: contractInstallmentCount }, (_, index) => ({
            id: editingContract?.paymentPlan?.[index]?.id || makeId(),
            amount: String(form.get(`planAmount_${index}`) || ""),
            ratio: planRatioFromAmount(String(form.get(`planAmount_${index}`) || ""), contractAmount),
            fundingPlanId: String(form.get(`planFundingPlan_${index}`) || "") || undefined,
          })) : [];
          const nextContract: ContractRecord = {
            id: editingContract?.id || makeId(),
            name: String(form.get("name") || "").trim(),
            party: String(form.get("party") || "").trim(),
            amount: contractAmount,
            amountStatus,
            signedOn: String(form.get("signedOn") || ""),
            paymentPlan,
            payments: editingContract?.payments || [],
            settlementStatus: editingContract?.settlementStatus,
            settlementAuditAmount: editingContract?.settlementAuditAmount,
            settlementAuditDate: editingContract?.settlementAuditDate,
            settlementNote: editingContract?.settlementNote,
          };
          updateCurrentProject((project) => {
            const nextProject = {
              ...project,
              procurements: (project.procurements || []).map((procurement) => procurement.id === editingProcurementId ? {
                ...procurement,
                contracts: editingContract
                  ? procurement.contracts.map((contract) => contract.id === editingContract.id ? nextContract : contract)
                  : [...procurement.contracts, nextContract],
              } : procurement),
            };
            return syncContractParticipant(nextProject, editingProcurementId, nextContract);
          });
          setModal(null);
          setEditingProcurementId("");
          setEditingContractId("");
          showToast(editingContract ? "合同已修改" : "合同已保存");
        }}>
          <label className="field-label">合同名称<input name="name" defaultValue={editingContract?.name || ""} required autoFocus /></label>
          <label className="field-label">合同单位<input name="party" defaultValue={editingContract?.party || ""} /></label>
          <div className="form-grid two"><label className="field-label">合同金额状态<select name="amountStatus" value={contractAmountStatus} onChange={(event) => setContractAmountStatus(event.target.value as "confirmed" | "pending")}><option value="confirmed">金额已确定</option><option value="pending">待中标后补充</option></select></label><label className="field-label">签订日期<SmartDateInput name="signedOn" defaultValue={editingContract?.signedOn || ""} /></label></div>
          {contractAmountStatus === "confirmed" ? <><label className="field-label">合同金额（元）<input name="amount" type="number" min="0" step="0.01" defaultValue={editingContract?.amount || ""} onInput={(event) => updatePlanRatios(event.currentTarget.form as HTMLFormElement, contractInstallmentCount)} /></label>
          <label className="field-label">付款次数<select value={contractInstallmentCount} onChange={(event) => { const count = Math.max(1, Math.min(4, Number(event.target.value) || 1)); const form = event.currentTarget.form; setContractInstallmentCount(count); window.requestAnimationFrame(() => form && updatePlanRatios(form, count)); }}>{contractInstallmentCount > 4 && <option value={contractInstallmentCount}>{contractInstallmentCount} 次（历史记录）</option>}{Array.from({ length: 4 }, (_, index) => <option key={index + 1} value={index + 1}>{index + 1} 次</option>)}</select></label>
          <div className="contract-plan-fields">
            {Array.from({ length: contractInstallmentCount }, (_, index) => {
              const plan = editingContract?.paymentPlan?.[index];
              const planAmount = plan?.amount ?? (plan ? String(plannedPaymentAmount(editingContract as ContractRecord, plan)) : "");
              const automaticAmount = contractInstallmentCount === 1 || index === contractInstallmentCount - 1;
              const defaultFundingPlanId = plan?.fundingPlanId || (paymentFundingPlans.length === 1 ? paymentFundingPlans[0].plan.id : "");
              return <section className="contract-plan-item" key={plan?.id || index}><strong>{contractInstallmentCount === 1 ? "一次性付款" : index === contractInstallmentCount - 1 ? "尾款（自动）" : `第 ${index + 1} 次`}</strong><label className="field-label">关联年度资金<select name={`planFundingPlan_${index}`} defaultValue={defaultFundingPlanId}><option value="">暂不关联</option>{paymentFundingPlans.map(({ fund, plan: fundingPlan }) => <option key={fundingPlan.id} value={fundingPlan.id}>{fundingPlan.year} 年 · {fund.name}</option>)}</select></label><label className="field-label">付款金额（元）<input name={`planAmount_${index}`} type="number" min="0" step="0.01" defaultValue={planAmount} required readOnly={automaticAmount} onInput={(event) => updatePlanRatios(event.currentTarget.form as HTMLFormElement, contractInstallmentCount)} /></label><label className="field-label">付款比例（自动）<input name={`ratio_${index}`} type="text" defaultValue={plan?.ratio || ""} readOnly tabIndex={-1} /></label></section>;
            })}
          </div></> : <input name="amount" type="hidden" value="" />}
          <div className="form-actions"><button className="secondary-button" type="button" onClick={() => { setModal(null); setEditingProcurementId(""); setEditingContractId(""); }}>取消</button><button className="primary-button" type="submit">{editingContract ? "保存修改" : "保存合同"}</button></div>
        </form>
      </Modal>

      <Modal open={modal === "contractAmount"} title="补充合同金额" onClose={() => { setModal(null); setEditingProcurementId(""); setEditingContractId(""); }}>
        {editingContract ? <form key={`contract_amount_${editingContract.id}_${contractInstallmentCount}`} onSubmit={(event) => {
          event.preventDefault();
          const form = new FormData(event.currentTarget);
          const amount = String(form.get("amount") || "");
          if (safeNumber(amount) <= 0) { showToast("请填写合同金额"); return; }
          const amounts = Array.from({ length: contractInstallmentCount }, (_, index) => String(form.get(`planAmount_${index}`) || ""));
          const completed = completeContractPlanAmounts(amount, amounts, contractInstallmentCount);
          if (Math.abs(completed.reduce((sum, value) => sum + safeNumber(value), 0) - safeNumber(amount)) > 0.01) {
            showToast("各次付款金额合计应等于合同金额"); return;
          }
          const paymentPlan = completed.map((value, index) => ({
            id: editingContract.paymentPlan?.[index]?.id || makeId(),
            amount: String(value),
            ratio: planRatioFromAmount(value, amount),
            fundingPlanId: String(form.get(`planFundingPlan_${index}`) || "") || undefined,
          }));
          updateCurrentProject((project) => updateContractWithParticipant(project, editingProcurementId, editingContract.id, (contract) => ({ ...contract, amount, amountStatus: "confirmed", paymentPlan })));
          setModal(null); setEditingProcurementId(""); setEditingContractId(""); showToast("合同金额与付款计划已补充");
        }}>
          <div className="contract-amount-summary"><strong>{editingContract.name}</strong><small>{editingContract.party || "未填写合同单位"}</small></div>
          <div className="form-grid two"><label className="field-label">合同金额（元）<input name="amount" type="number" min="0" step="0.01" defaultValue={editingContract.amount || ""} required onInput={(event) => updatePlanRatios(event.currentTarget.form as HTMLFormElement, contractInstallmentCount)} /></label><label className="field-label">付款次数<select value={contractInstallmentCount} onChange={(event) => setContractInstallmentCount(Math.max(1, Math.min(4, Number(event.target.value) || 1)))}>{Array.from({ length: 4 }, (_, index) => <option key={index + 1} value={index + 1}>{index + 1} 次</option>)}</select></label></div>
          <div className="contract-plan-fields">{Array.from({ length: contractInstallmentCount }, (_, index) => <section className="contract-plan-item" key={index}><strong>{contractInstallmentCount === 1 ? "一次性付款" : index === contractInstallmentCount - 1 ? "尾款（自动）" : `第 ${index + 1} 笔`}</strong><label className="field-label">关联年度资金<select name={`planFundingPlan_${index}`} defaultValue={editingContract.paymentPlan?.[index]?.fundingPlanId || (paymentFundingPlans.length === 1 ? paymentFundingPlans[0].plan.id : "")}><option value="">暂不关联</option>{paymentFundingPlans.map(({ fund, plan }) => <option key={plan.id} value={plan.id}>{plan.year} 年 · {fund.name}</option>)}</select></label><label className="field-label">付款金额（元）<input name={`planAmount_${index}`} type="number" min="0" step="0.01" defaultValue={editingContract.paymentPlan?.[index]?.amount || ""} readOnly={contractInstallmentCount === 1 || index === contractInstallmentCount - 1} required onInput={(event) => updatePlanRatios(event.currentTarget.form as HTMLFormElement, contractInstallmentCount)} /></label><label className="field-label">付款比例（自动）<input name={`ratio_${index}`} readOnly tabIndex={-1} defaultValue={editingContract.paymentPlan?.[index]?.ratio || ""} /></label></section>)}</div>
          <div className="form-actions"><button className="secondary-button" type="button" onClick={() => setModal(null)}>取消</button><button className="primary-button" type="submit">保存金额</button></div>
        </form> : <EmptyState title="未找到合同" />}
      </Modal>

      <Modal open={modal === "contractSettlement"} title="施工合同结算审定" onClose={() => { setModal(null); setEditingProcurementId(""); setEditingContractId(""); }}>
        {editingContract ? <form onSubmit={(event) => {
          event.preventDefault();
          const form = new FormData(event.currentTarget);
          const amount = String(form.get("settlementAuditAmount") || "");
          if (safeNumber(amount) <= 0) { showToast("请填写审定金额"); return; }
          updateCurrentProject((project) => updateContractWithParticipant(project, editingProcurementId, editingContract.id, (contract) => ({
            ...contract,
            settlementStatus: "confirmed",
            settlementAuditAmount: amount,
            settlementAuditDate: String(form.get("settlementAuditDate") || ""),
            settlementNote: String(form.get("settlementNote") || ""),
          })));
          setModal(null); setEditingProcurementId(""); setEditingContractId(""); showToast("结算审定金额已保存");
        }}>
          <div className="contract-amount-summary"><strong>{editingContract.name}</strong><small>原合同金额 {safeNumber(editingContract.amount).toLocaleString("zh-CN")} 元</small></div>
          <div className="form-grid two"><label className="field-label">结算审定金额（元）<input name="settlementAuditAmount" type="number" min="0" step="0.01" value={contractSettlementAmount} onChange={(event) => setContractSettlementAmount(event.target.value)} required /></label><label className="field-label">审计报告日期<SmartDateInput name="settlementAuditDate" defaultValue={editingContract.settlementAuditDate || ""} /></label></div>
          <label className="field-label">备注<input name="settlementNote" defaultValue={editingContract.settlementNote || ""} /></label>
          <div className="settlement-calculation"><span>审减金额</span><strong>{contractSettlementAmount ? Math.max(0, safeNumber(editingContract.amount) - safeNumber(contractSettlementAmount)).toLocaleString("zh-CN") : "—"} 元</strong></div>
          <div className="form-actions"><button className="secondary-button" type="button" onClick={() => setModal(null)}>取消</button><button className="primary-button" type="submit">保存审定</button></div>
        </form> : <EmptyState title="未找到施工合同" />}
      </Modal>

      <Modal className="payment-modal" open={modal === "payment"} title={editingPayment ? "修改付款" : "记录付款"} onClose={() => { setModal(null); setEditingProcurementId(""); setEditingContractId(""); setEditingPaymentId(""); }}>
        <form key={`payment_${editingProcurementId}_${editingContractId}_${editingPaymentId || "new"}`} onSubmit={(event) => {
          event.preventDefault();
          const form = new FormData(event.currentTarget);
          const kind = form.get("kind") as PaymentRecord["kind"];
          const paymentDate = String(form.get("date") || localDateKey());
          if (kind === "final" && editingProcurement?.kind === "construction" && !safeNumber(editingContract?.settlementAuditAmount)) {
            showToast("施工合同请先记录结算审定金额"); return;
          }
          const archiveReceiptStatus = form.get("archiveReceiptStatus") as PaymentRecord["archiveReceiptStatus"];
          const performanceConfirmationStatus = form.get("performanceConfirmationStatus") as PaymentRecord["performanceConfirmationStatus"];
          if ((kind === "final" || kind === "oneTime") && (!archiveReceiptStatus || !performanceConfirmationStatus)) {
            showToast("请选择履约确认单和归档单状态"); return;
          }
          const nextPayment: PaymentRecord = {
            id: editingPayment?.id || makeId(),
            date: paymentDate,
            amount: String(form.get("amount") || ""),
            kind,
            note: String(form.get("note") || ""),
            fundingPlanId: String(form.get("fundingPlanId") || "") || undefined,
            scheduleId: String(form.get("scheduleId") || "") || undefined,
            archiveReceiptStatus,
            performanceConfirmationStatus,
            archiveReceiptConfirmed: archiveReceiptStatus === "complete",
            performanceConfirmationConfirmed: performanceConfirmationStatus === "complete",
          };
          updateCurrentProject((project) => updateContractWithParticipant(
            project,
            editingProcurementId,
            editingContractId,
            (contract) => ({
              ...contract,
              payments: editingPayment
                ? contract.payments.map((payment) => payment.id === editingPayment.id ? nextPayment : payment)
                : [...contract.payments, nextPayment],
            }),
          ));
          setModal(null);
          setEditingProcurementId("");
          setEditingContractId("");
          setEditingPaymentId("");
          showToast(editingPayment ? "付款记录已修改" : "付款记录已保存");
        }}>
          <div className="form-grid two">
            <label className="field-label">资金使用申请单交单时间<SmartDateInput name="date" defaultValue={editingPayment?.date || localDateKey()} required onChange={(nextDate, form) => {
              const fundingPlanSelect = form?.elements.namedItem("fundingPlanId") as HTMLSelectElement | null;
              const scheduleSelect = form?.elements.namedItem("scheduleId") as HTMLSelectElement | null;
              if (fundingPlanSelect) fundingPlanSelect.value = automaticPaymentFundingPlanId(currentProject, editingContract, scheduleSelect?.value || "", nextDate);
            }} /></label>
            <label className="field-label">付款类别<select name="kind" value={paymentKind} onChange={(event) => {
              const nextKind = event.currentTarget.value as PaymentRecord["kind"];
              if (nextKind === "final" && editingProcurement?.kind === "construction" && !safeNumber(editingContract?.settlementAuditAmount)) {
                showToast("施工尾款需先记录结算审定金额"); return;
              }
              setPaymentKind(nextKind);
              const form = event.currentTarget.form;
              const scheduleSelect = form?.elements.namedItem("scheduleId") as HTMLSelectElement | null;
              const scheduleId = automaticPaymentScheduleId(editingContract, nextKind, editingPayment?.id);
              if (!scheduleSelect) return;
              scheduleSelect.value = scheduleId;
              const fundingPlanSelect = form?.elements.namedItem("fundingPlanId") as HTMLSelectElement | null;
              const paymentDate = (form?.elements.namedItem("date") as HTMLInputElement | null)?.value || localDateKey();
              if (fundingPlanSelect) fundingPlanSelect.value = automaticPaymentFundingPlanId(currentProject, editingContract, scheduleId, paymentDate);
              const plan = (editingContract?.paymentPlan || []).find((item) => item.id === scheduleId);
              const amountInput = form?.elements.namedItem("amount") as HTMLInputElement | null;
              if (amountInput && editingContract) amountInput.value = String(nextKind === "final" && editingProcurement?.kind === "construction"
                ? settlementFinalPaymentAmount(editingContract, editingPayment?.id)
                : plan ? plannedPaymentAmount(editingContract, plan) : amountInput.value);
            }}><option value="oneTime">一次性付款</option><option value="first">首付款</option><option value="progress">进度款</option><option value="final">尾款</option><option value="warranty">质保金</option><option value="other">其他</option></select></label>
            {(editingContract?.paymentPlan || []).length ? <label className="field-label">付款节点<select name="scheduleId" defaultValue={editingPayment?.scheduleId || automaticPaymentScheduleId(editingContract, paymentKind, editingPayment?.id)} required onChange={(event) => {
              const plan = (editingContract?.paymentPlan || []).find((item) => item.id === event.currentTarget.value);
              const fundingPlanSelect = event.currentTarget.form?.elements.namedItem("fundingPlanId") as HTMLSelectElement | null;
              const paymentDate = (event.currentTarget.form?.elements.namedItem("date") as HTMLInputElement | null)?.value || localDateKey();
              if (fundingPlanSelect) fundingPlanSelect.value = automaticPaymentFundingPlanId(currentProject, editingContract, event.currentTarget.value, paymentDate);
              const amountInput = event.currentTarget.form?.elements.namedItem("amount") as HTMLInputElement | null;
              if (amountInput && editingContract) amountInput.value = String(paymentKind === "final" && editingProcurement?.kind === "construction"
                ? settlementFinalPaymentAmount(editingContract, editingPayment?.id)
                : plan ? plannedPaymentAmount(editingContract, plan) : amountInput.value);
            }}><option value="" disabled>选择付款节点</option>{(editingContract?.paymentPlan || []).map((plan, index) => {
              const linkedPayment = paymentForPlan(editingContract as ContractRecord, plan, index);
              const locked = Boolean(linkedPayment && linkedPayment.id !== editingPayment?.id);
              return <option key={plan.id} value={plan.id} disabled={locked}>{paymentScheduleLabel(editingContract as ContractRecord, plan, index, paymentKind)}{locked ? "（已提交）" : ""}</option>;
            })}</select></label> : null}
            <label className="field-label">关联资金年度<select name="fundingPlanId" defaultValue={editingPayment?.fundingPlanId || automaticPaymentFundingPlanId(currentProject, editingContract, editingPayment?.scheduleId || automaticPaymentScheduleId(editingContract, paymentKind, editingPayment?.id), editingPayment?.date || localDateKey())}><option value="">未关联资金年度</option>{paymentFundingPlans.map(({ fund, plan }) => <option key={plan.id} value={plan.id}>{plan.year} 年 · {fund.name}</option>)}</select></label>
            <label className="field-label">付款金额（元）<input name="amount" type="number" min="0" step="0.01" defaultValue={editingPayment?.amount || automaticPaymentAmount(editingContract, paymentKind, editingPayment?.id, editingProcurement?.kind)} required /></label>
          </div>
          <label className="field-label">备注<input name="note" defaultValue={editingPayment?.note || ""} /></label>
          {(paymentKind === "final" || paymentKind === "oneTime") && <div className="final-payment-checks"><strong>{paymentKind === "oneTime" ? "一次性付款材料" : "尾款材料"}</strong><div className="form-grid two"><label className="field-label">合同履行确认单<select name="performanceConfirmationStatus" defaultValue={editingPayment ? paymentPerformanceStatus(editingPayment) : "complete"} required><option value="complete">已完成</option><option value="pending">暂欠，后补</option></select></label><label className="field-label">归档单<select name="archiveReceiptStatus" defaultValue={editingPayment ? paymentArchiveStatus(editingPayment) : "complete"} required><option value="complete">已取得</option><option value="pending">暂欠，后补</option><option value="notRequired">不需要归档单</option></select></label></div></div>}
          <div className="form-actions"><button className="secondary-button" type="button" onClick={() => { setModal(null); setEditingProcurementId(""); setEditingContractId(""); setEditingPaymentId(""); }}>取消</button><button className="primary-button" type="submit">{editingPayment ? "保存修改" : "保存付款记录"}</button></div>
        </form>
      </Modal>

      <Modal open={modal === "stageStart"} title="定位当前阶段" onClose={() => setModal(null)}>
        {currentProject?.stages.length ? (
          <form onSubmit={(event) => {
            event.preventDefault();
            const stageId = String(new FormData(event.currentTarget).get("stageId") || "");
            const targetIndex = currentProject.stages.findIndex((stage) => stage.id === stageId);
            if (targetIndex < 0) return;
            updateCurrentProject((project) => ({
              ...project,
              stages: project.stages.map((stage, index) => {
                if (index < targetIndex && stage.status !== "done" && stage.status !== "skipped") {
                  return { ...stage, status: "historical" };
                }
                if (index === targetIndex) return { ...stage, status: "processing" };
                if (index > targetIndex && (stage.status === "historical" || stage.status === "processing")) {
                  return { ...stage, status: "waiting" };
                }
                return stage;
              }),
            }));
            setModal(null);
            showToast("当前阶段已定位，前期未填写内容不会要求补录");
          }}>
            <label className="field-label">当前阶段<select name="stageId" defaultValue={currentProject.stages.find((stage) => stage.status === "processing")?.id || currentProject.stages[0]?.id}>{currentProject.stages.map((stage) => <option key={stage.id || stage.name} value={stage.id}>{stage.name}</option>)}</select></label>
            <div className="form-actions"><button className="secondary-button" type="button" onClick={() => setModal(null)}>取消</button><button className="primary-button" type="submit">设为当前阶段</button></div>
          </form>
        ) : <EmptyState title="尚未建立阶段" />}
      </Modal>

      <Modal open={modal === "stagePicker"} title="添加阶段" onClose={() => setModal(null)} wide>
        <div className="node-groups">
          {Object.entries(NODE_CATEGORIES).map(([category, names]) => {
            const customMap = nodeConfig._customNodeMap as Record<string, string[]> | undefined;
            return (
              <section key={category}><h3>{category}</h3><div className="node-grid">
                {[...names, ...(customMap?.[category] || [])].map((name) => (
                  <button key={name} type="button" onClick={() => {
                    updateCurrentProject((project) => ({ ...project, stages: [...project.stages, { id: makeId(), name, status: "waiting", category: "other", config: getNodeFields(name), data: {} }] }));
                    setModal(null);
                    showToast(`已添加“${name}”节点`);
                  }}>＋ {name}</button>
                ))}
              </div></section>
            );
          })}
        </div>
      </Modal>

      <Modal open={modal === "stageEditor"} title={editingStageIndex !== null ? `${currentProject?.stages[editingStageIndex]?.name || ""} — 录入` : "节点录入"} onClose={() => setModal(null)}>
        {editingStageIndex !== null && currentProject?.stages[editingStageIndex] && (
          <form onSubmit={(event) => {
            event.preventDefault();
            const form = new FormData(event.currentTarget);
            updateCurrentProject((project) => {
              const stages = clone(project.stages);
              const stage = stages[editingStageIndex];
              const data: Record<string, string> = {};
              stage.config.forEach((field) => { data[field.key] = String(form.get(field.key) || ""); });
              const values = Object.values(data);
              stage.data = { ...stage.data, ...data };
              stage.status = values.some(Boolean)
                ? (values.every(Boolean) ? "done" : "processing")
                : stage.status === "historical"
                  ? "historical"
                  : "waiting";
              const occurredOn = stage.config.find((field) => field.type === "date" && data[field.key])?.key;
              const existingEvent = (project.events || []).find((item) => item.sourceStageId === stage.id);
              const linkedEvent = occurredOn ? {
                id: existingEvent?.id || makeId(),
                occurredOn: data[occurredOn],
                recordedAt: existingEvent?.recordedAt || new Date().toISOString(),
                title: stage.name,
                note: stage.guidance || "",
                status: stage.status === "done" ? "done" as const : "doing" as const,
                category: stage.category || "other",
                completedAt: stage.status === "done" ? new Date().toISOString() : undefined,
                sourceStageId: stage.id,
              } : null;
              const events = linkedEvent
                ? existingEvent
                  ? (project.events || []).map((item) => item.id === existingEvent.id ? { ...item, ...linkedEvent } : item)
                  : [linkedEvent, ...(project.events || [])]
                : project.events || [];
              const numericField = (keys: string[]) => {
                const match = stage.config.find((field) => field.type === "number" && keys.some((key) => field.key.toLowerCase().includes(key)));
                return match ? data[match.key] : "";
              };
              const isBudgetReview = stage.name.includes("预算评审") || stage.name.includes("财政评审");
              return {
                ...project,
                stages,
                events,
                budgetReviewAmount: isBudgetReview ? numericField(["review", "audit", "amount"]) || project.budgetReviewAmount : project.budgetReviewAmount,
                nextAction: project.nextAction?.title === stage.name && stage.status === "done" ? undefined : project.nextAction,
              };
            });
            setModal(null);
            showToast("节点信息已保存");
          }}>
            {currentProject.stages[editingStageIndex].config.length ? currentProject.stages[editingStageIndex].config.map((field) => (
              <label className="field-label" key={field.key}>{field.label}
                {field.type === "select" ? (
                  <select name={field.key} defaultValue={currentProject.stages[editingStageIndex].data?.[field.key] || ""}><option value="">请选择</option>{field.options?.map((option) => <option key={option}>{option}</option>)}</select>
                ) : field.type === "date" ? (
                  <SmartDateInput name={field.key} defaultValue={currentProject.stages[editingStageIndex].data?.[field.key] || ""} />
                ) : <input name={field.key} type={field.type === "number" ? "number" : "text"} step={field.type === "number" ? "0.01" : undefined} defaultValue={currentProject.stages[editingStageIndex].data?.[field.key] || ""} />}
              </label>
            )) : <EmptyState title="暂无节点字段" />}
            <div className="form-actions"><button className="secondary-button" type="button" onClick={() => setModal(null)}>取消</button><button className="primary-button" type="submit">保存</button></div>
          </form>
        )}
      </Modal>

      <Modal open={modal === "stageConfig"} title="调整本项目节点字段" onClose={() => setModal(null)}>
        {editingStageIndex !== null && currentProject?.stages[editingStageIndex] && (
          <FieldEditor
            fields={currentProject.stages[editingStageIndex].config}
            onChange={(fields) => updateCurrentProject((project) => {
              const stages = clone(project.stages);
              stages[editingStageIndex].config = fields;
              return { ...project, stages };
            })}
          />
        )}
      </Modal>

      <Modal open={modal === "nodeLibrary"} title="全局节点库" onClose={() => { setModal(null); setSelectedConfigNode(""); }} wide>
        {selectedConfigNode ? (
          <>
            <button className="back-button" type="button" onClick={() => setSelectedConfigNode("")}>← 返回节点列表</button>
            <h3 className="config-node-title">{selectedConfigNode}</h3>
            <FieldEditor fields={getNodeFields(selectedConfigNode)} onChange={(fields) => setNodeConfig((config) => ({ ...config, [selectedConfigNode]: fields }))} />
          </>
        ) : (
          <>
            <div className="node-groups">
              {Object.entries(NODE_CATEGORIES).map(([category, names]) => {
                const customMap = nodeConfig._customNodeMap as Record<string, string[]> | undefined;
                return <section key={category}><h3>{category}</h3><div className="node-grid">{[...names, ...(customMap?.[category] || [])].map((name) => <button key={name} type="button" onClick={() => setSelectedConfigNode(name)}>{name}</button>)}</div></section>;
              })}
            </div>
            <form className="inline-create" onSubmit={(event) => {
              event.preventDefault();
              const form = new FormData(event.currentTarget);
              const category = String(form.get("category"));
              const name = String(form.get("name") || "").trim();
              if (!name || nodeConfig[name]) return;
              setNodeConfig((config) => {
                const customMap = clone((config._customNodeMap as Record<string, string[]>) || {});
                customMap[category] = [...(customMap[category] || []), name];
                return { ...config, _customNodeMap: customMap, [name]: [{ key: `custom_${Date.now()}`, label: "完成时间", type: "date" }] };
              });
              setSelectedConfigNode(name);
            }}>
              <strong>新增自定义节点</strong>
              <select name="category">{Object.keys(NODE_CATEGORIES).map((category) => <option key={category}>{category}</option>)}</select>
              <input name="name" required />
              <button className="primary-button small" type="submit">新增</button>
            </form>
          </>
        )}
      </Modal>

      <Modal open={modal === "service"} title={editingService ? "修改参建单位" : "录入参建单位"} onClose={() => { setModal(null); setEditingServiceId(""); }}>
        <form key={`service_${editingServiceId || "new"}`} onSubmit={(event) => {
          event.preventDefault();
          const form = new FormData(event.currentTarget);
          const service: Service = {
            id: editingService?.id || makeId(),
            type: editingServiceContract ? editingService?.type || "其他" : String(form.get("type")),
            name: editingServiceContract ? editingServiceContract.party : String(form.get("name") || "").trim(),
            amount: editingServiceContract ? editingServiceContract.amount : String(form.get("amount") || ""),
            contact: String(form.get("contact") || ""),
            progress: editingServiceContract
              ? contractPaymentProgress(editingServiceContract)
              : Math.min(100, Math.max(0, Number(form.get("progress") || 0))),
            linkedContractId: editingService?.linkedContractId,
            linkedProcurementId: editingService?.linkedProcurementId,
          };
          if (!service.name) return;
          updateCurrentProject((project) => ({
            ...project,
            services: editingService
              ? (project.services || []).map((item) => item.id === editingService.id ? service : item)
              : [...(project.services || []), service],
          }));
          setModal(null);
          setEditingServiceId("");
          showToast(editingService ? "参建单位已修改" : "参建单位已保存");
        }}>
          <label className="field-label">单位类型<select name="type" defaultValue={editingService?.type || "施工单位"} disabled={Boolean(editingServiceContract)}><option>施工单位</option><option>设计单位</option><option>监理单位</option><option>招标代理</option><option>造价咨询</option><option>其他</option></select></label>
          <label className="field-label">单位名称<input name="name" defaultValue={editingServiceContract?.party || editingService?.name || ""} required autoFocus readOnly={Boolean(editingServiceContract)} /></label>
          <label className="field-label">合同金额（元）<input name="amount" type="number" min="0" step="0.01" defaultValue={editingServiceContract?.amount || editingService?.amount || ""} readOnly={Boolean(editingServiceContract)} /></label>
          <label className="field-label">联系人与电话<input name="contact" defaultValue={editingService?.contact || ""} /></label>
          <label className="field-label">当前履约进度（%）<input name="progress" type="number" min="0" max="100" defaultValue={editingServiceContract ? contractPaymentProgress(editingServiceContract) : editingService?.progress || 0} readOnly={Boolean(editingServiceContract)} /></label>
          <div className="form-actions"><button className="secondary-button" type="button" onClick={() => { setModal(null); setEditingServiceId(""); }}>取消</button><button className="primary-button" type="submit">保存</button></div>
        </form>
      </Modal>

      <Modal open={modal === "overtime"} title={editingOvertime ? "修改加班记录" : "录入加班"} onClose={() => { setModal(null); setEditingOvertimeId(""); }}>
        <form key={`overtime_${editingOvertimeId || "new"}`} onSubmit={(event) => {
          event.preventDefault();
          const form = new FormData(event.currentTarget);
          const record: OvertimeRecord = {
            id: editingOvertime?.id || makeId(),
            date: String(form.get("date")),
            days: String(form.get("days")),
            note: String(form.get("note") || ""),
            status: editingOvertime?.status || "active",
            usedDate: editingOvertime?.usedDate,
            holiday: form.get("holiday") === "on",
          };
          setAttendance((data) => ({
            ...data,
            records: editingOvertime
              ? data.records.map((item) => item.id === editingOvertime.id ? record : item)
              : [...data.records, record],
          }));
          setModal(null);
          setEditingOvertimeId("");
          showToast(editingOvertime ? "加班记录已修改" : "加班记录已保存");
        }}>
          <label className="field-label">日期<SmartDateInput name="date" defaultValue={editingOvertime?.date || localDateKey()} required /></label>
          <label className="field-label">可倒休天数<input name="days" type="number" min="0.5" step="0.5" defaultValue={editingOvertime?.days || ""} required /></label>
          <label className="field-label checkbox-field"><input name="holiday" type="checkbox" defaultChecked={editingOvertime?.holiday || false} /> 法定节假日加班（不计倒休）</label>
          <label className="field-label">备注<input name="note" defaultValue={editingOvertime?.note || ""} /></label>
          <div className="form-actions"><button className="secondary-button" type="button" onClick={() => { setModal(null); setEditingOvertimeId(""); }}>取消</button><button className="primary-button" type="submit">保存</button></div>
        </form>
      </Modal>

      <Modal open={modal === "useOvertime"} title="标记已倒休" onClose={() => setModal(null)}>
        <form onSubmit={(event) => {
          event.preventDefault();
          const usedDate = String(new FormData(event.currentTarget).get("date"));
          if (editingStageIndex !== null) setAttendance((data) => ({ ...data, records: data.records.map((record, index) => index === editingStageIndex ? { ...record, status: "used", usedDate } : record) }));
          setModal(null);
        }}>
          <label className="field-label">倒休日期<SmartDateInput name="date" defaultValue={localDateKey()} required /></label>
          <div className="form-actions"><button className="secondary-button" type="button" onClick={() => setModal(null)}>取消</button><button className="primary-button" type="submit">确认已休</button></div>
        </form>
      </Modal>

      <Modal open={modal === "leave"} title={editingStageIndex !== null && attendance.leaveRecords[editingStageIndex] ? "编辑休假" : "录入休假"} onClose={() => { setModal(null); setEditingStageIndex(null); }}>
        <form key={`${modal}_${editingStageIndex}`} onSubmit={(event) => {
          event.preventDefault();
          const form = new FormData(event.currentTarget);
          const record: LeaveRecord = { id: attendance.leaveRecords[editingStageIndex || -1]?.id || makeId(), type: form.get("type") as LeaveRecord["type"], date: String(form.get("date")), days: String(form.get("days")), note: String(form.get("note") || "") };
          setAttendance((data) => {
            if (editingStageIndex !== null && data.leaveRecords[editingStageIndex]) return { ...data, leaveRecords: data.leaveRecords.map((item, index) => index === editingStageIndex ? record : item) };
            return { ...data, leaveRecords: [...data.leaveRecords, record] };
          });
          setEditingStageIndex(null);
          setModal(null);
        }}>
          <label className="field-label">类型<select name="type" defaultValue={editingStageIndex !== null ? attendance.leaveRecords[editingStageIndex]?.type : "年假"}><option>年假</option><option>存休</option><option>其他</option></select></label>
          <label className="field-label">日期<SmartDateInput name="date" defaultValue={editingStageIndex !== null ? attendance.leaveRecords[editingStageIndex]?.date : localDateKey()} required /></label>
          <label className="field-label">天数<input name="days" type="number" min="0.5" step="0.5" defaultValue={editingStageIndex !== null ? attendance.leaveRecords[editingStageIndex]?.days : ""} required /></label>
          <label className="field-label">说明<input name="note" defaultValue={editingStageIndex !== null ? attendance.leaveRecords[editingStageIndex]?.note : ""} /></label>
          <div className="form-actions"><button className="secondary-button" type="button" onClick={() => { setEditingStageIndex(null); setModal(null); }}>取消</button><button className="primary-button" type="submit">保存</button></div>
        </form>
      </Modal>

      <Modal open={modal === "attendanceSettings"} title="编辑初始额度" onClose={() => setModal(null)}>
        <form onSubmit={(event) => {
          event.preventDefault();
          const form = new FormData(event.currentTarget);
          setAttendance((data) => ({
            ...data,
            annual: String(form.get("annual") || "0"),
            saved: String(form.get("saved") || "0"),
          }));
          setModal(null);
          showToast("初始额度已更新");
        }}>
          <div className="form-grid two">
            <label className="field-label">年假（天）<input name="annual" type="number" min="0" step="0.5" defaultValue={attendance.annual} /></label>
            <label className="field-label">存休（天）<input name="saved" type="number" min="0" step="0.5" defaultValue={attendance.saved} /></label>
          </div>
          <div className="form-actions"><button className="secondary-button" type="button" onClick={() => setModal(null)}>取消</button><button className="primary-button" type="submit">保存额度</button></div>
        </form>
        <div className="attendance-adjustments">
          <div className="adjustments-heading"><strong>额度变更记录</strong><button className="secondary-button small" type="button" onClick={() => setModal("attendanceAdjust")}>＋ 调整额度</button></div>
          {(attendance.adjustments || []).length ? (
            <div className="record-list">
              {[...(attendance.adjustments || [])].sort((a, b) => b.date.localeCompare(a.date)).map((adjustment) => (
                <div className="record-row" key={adjustment.id}>
                  <div><span className="status-pill leave">{adjustment.kind === "annual" ? "年假" : "存休"}</span><strong>{adjustment.date} · {safeNumber(adjustment.days) >= 0 ? "＋" : ""}{adjustment.days} 天</strong>{adjustment.note && <p>{adjustment.note}</p>}</div>
                  <button className="danger-text" type="button" onClick={() => removeAttendanceAdjustment(adjustment)}>删除</button>
                </div>
              ))}
            </div>
          ) : <p className="compact-empty">还没有额度变更记录。追加或扣减额度时使用“＋ 调整额度”。</p>}
        </div>
      </Modal>

      <Modal open={modal === "attendanceAdjust"} title="调整额度" onClose={() => setModal(null)}>
        <form onSubmit={(event) => {
          event.preventDefault();
          const form = new FormData(event.currentTarget);
          const kind = String(form.get("kind") || "annual") as "annual" | "saved";
          const days = String(form.get("days") || "").trim();
          if (!days) return;
          setAttendance((data) => ({
            ...data,
            adjustments: [...(data.adjustments || []), {
              id: makeId(),
              date: localDateKey(),
              kind,
              days,
              note: String(form.get("note") || "").trim() || undefined,
            }],
          }));
          setModal(null);
          showToast("额度变更已记录");
        }}>
          <label className="field-label">类型<select name="kind" defaultValue="annual"><option value="annual">年假</option><option value="saved">存休</option></select></label>
          <label className="field-label">变更天数<input name="days" type="number" step="0.5" placeholder="正数增加，负数扣减，如 3 或 -1.5" required autoFocus /></label>
          <label className="field-label">说明<input name="note" placeholder="如：领导追加年假 3 天" /></label>
          <div className="form-actions"><button className="secondary-button" type="button" onClick={() => setModal(null)}>取消</button><button className="primary-button" type="submit">保存</button></div>
        </form>
      </Modal>

      <Modal open={modal === "account"} title={user ? "个人中心" : authMode === "login" ? "云端账号登录" : "注册新账号"} onClose={() => setModal(null)}>
        {user ? (
          <div className="account-panel">
            <div className="account-identity"><span>{user.email?.slice(0, 1).toUpperCase()}</span><div><strong>{user.email}</strong><small>{syncLabel}</small></div></div>
            <dl><div><dt>项目数</dt><dd>{linkedProjects.length}</dd></div><div><dt>待办数</dt><dd>{activeTodos.length}</dd></div></dl>
            <button className="primary-button full" type="button" onClick={manualSync}>立即同步</button>
            <button className="secondary-button full" type="button" onClick={runSecurityCheck}>数据安全自检</button>
            {guestPendingSummary && (
              <div className="guest-import-card">
                <strong>访客空间还有未导入的数据</strong>
                <p>{guestPendingSummary.projects} 个项目 · {guestPendingSummary.todos} 条待办 · {guestPendingSummary.workRecords} 条工作记录 · {guestPendingSummary.overtime} 条加班 · {guestPendingSummary.leave} 条休假</p>
                <button className="primary-button small" type="button" onClick={openGuestImport}>预览并导入访客数据</button>
              </div>
            )}
            <div className="local-note">
              <strong>本地数据备份</strong>
              <p>{lastBackupAt ? `上次导出备份：${new Date(lastBackupAt).toLocaleString("zh-CN", { hour12: false })}` : "还没有导出过备份，建议定期导出备份文件。"}</p>
            </div>
            <div className="change-email-box">
              <strong>更换登录邮箱</strong>
              <p>数据保留在当前账号；确认邮件发送到新邮箱后，点击邮件里的链接即可生效。</p>
              <input type="email" placeholder="新邮箱（真实邮箱）" value={newEmail} onChange={(event) => setNewEmail(event.target.value)} aria-label="新邮箱" />
              <input type="password" placeholder="当前密码（确认身份）" value={changeEmailPassword} onChange={(event) => setChangeEmailPassword(event.target.value)} aria-label="当前密码" />
              <button className="secondary-button full" type="button" onClick={handleChangeEmail}>发送确认邮件</button>
            </div>
            <div className="device-section">
              <div className="adjustments-heading"><strong>登录设备</strong><button className="secondary-button small" type="button" onClick={signOutOtherDevices}>退出其他设备</button></div>
              {devices.length ? (
                <div className="record-list">
                  {devices.map((device) => {
                    const isCurrent = device.device_id === (localStorage.getItem(KEYS.deviceId) || "");
                    return (
                      <div className="record-row" key={device.device_id}>
                        <div><strong>{device.device_name || "未知设备"}</strong><p>最近活跃：{new Date(device.last_seen_at).toLocaleString("zh-CN", { hour12: false })}</p></div>
                        <small>{isCurrent ? "本机" : device.revoked_at ? "已强制下线" : ""}</small>
                      </div>
                    );
                  })}
                </div>
              ) : <p className="compact-empty">暂无设备记录（需先在 Supabase 执行设备管理 SQL）</p>}
            </div>
            <div className="e2e-section">
              <div className="adjustments-heading"><strong>端到端加密</strong></div>
              {e2eSettings ? (
                <>
                  <p className="e2e-status">已开启：云端数据仅本机可解密，平台无法查看。</p>
                  <button className="secondary-button full" type="button" onClick={() => { setE2ePassword(""); setRecoveryInput(""); setRecoveryPassword(""); setModal("recovery"); }}>重新解锁 / 用恢复码重绑</button>
                </>
              ) : (
                <>
                  <p className="e2e-status">未开启：云端数据以明文保存，平台可读。</p>
                  <button className="secondary-button full" type="button" onClick={() => { setE2ePassword(""); setModal("e2eEnable"); }}>开启端到端加密</button>
                </>
              )}
            </div>
            {user.email === ADMIN_EMAIL && <button className="secondary-button full" type="button" onClick={async () => {
              if (!cloud) return;
              const result = await cloud.rpc("get_app_usage");
              if (result.error) {
                showToast(`账号使用情况读取失败：${result.error.message}`);
                return;
              }
              setAdminRows((result.data || []) as AdminUsageRow[]);
              setModal("admin");
            }}>账号使用情况</button>}
            <button className="danger-button full" type="button" onClick={logout}>退出登录</button>
          </div>
        ) : (
          <form onSubmit={handleAuth}>
            <label className="field-label">邮箱<input name="email" type="email" required autoComplete="email" /></label>
            <label className="field-label">密码<input name="password" type="password" minLength={6} required autoComplete={authMode === "login" ? "current-password" : "new-password"} /></label>
            {authError && <p className="form-error">{authError}</p>}
            <button className="primary-button full" type="submit">{authMode === "login" ? "登录并同步" : "注册账号"}</button>
            <button className="text-button centered" type="button" onClick={() => { setAuthMode(authMode === "login" ? "register" : "login"); setAuthError(""); }}>{authMode === "login" ? "没有账号？注册" : "已有账号？登录"}</button>
          </form>
        )}
      </Modal>

      <Modal open={modal === "appearance"} title="外观主题" onClose={() => setModal(null)}>
        <div className="appearance-panel">
          <section className="appearance-section">
            <h3>主题模式</h3>
            <div className="appearance-options">
              {([["system", "跟随系统"], ["light", "浅色"], ["dark", "深色"], ["contrast", "高对比"]] as const).map(([value, label]) => (
                <button key={value} type="button" className={appearance.theme === value ? "active" : ""} onClick={() => setAppearance((current) => ({ ...current, theme: value }))}>{label}</button>
              ))}
            </div>
          </section>
          <section className="appearance-section">
            <h3>主题色</h3>
            <div className="appearance-options palette">
              {([["green", "翠绿", "#0b7a52"], ["blue", "蓝", "#44729b"], ["plum", "梅子", "#70516f"], ["terracotta", "陶土", "#9a4d3d"]] as const).map(([value, label, color]) => (
                <button key={value} type="button" className={appearance.accent === value ? "active" : ""} onClick={() => setAppearance((current) => ({ ...current, accent: value }))}>
                  <span style={{ background: color }} />{label}
                </button>
              ))}
            </div>
          </section>
          <section className="appearance-section">
            <h3>字体大小</h3>
            <div className="appearance-options">
              <button type="button" className={appearance.fontSize === "standard" ? "active" : ""} onClick={() => setAppearance((current) => ({ ...current, fontSize: "standard" }))}>标准</button>
              <button type="button" className={appearance.fontSize === "large" ? "active" : ""} onClick={() => setAppearance((current) => ({ ...current, fontSize: "large" }))}>大号</button>
            </div>
          </section>
          <section className="appearance-section">
            <h3>界面密度</h3>
            <div className="appearance-options">
              {([["compact", "紧凑"], ["standard", "标准"], ["comfortable", "宽松"]] as const).map(([value, label]) => (
                <button key={value} type="button" className={appearance.density === value ? "active" : ""} onClick={() => setAppearance((current) => ({ ...current, density: value }))}>{label}</button>
              ))}
            </div>
          </section>
          <p className="compact-empty">外观设置保存在本机，切换后立即生效。</p>
        </div>
      </Modal>

      <Modal open={modal === "search"} title="搜索" onClose={() => setModal(null)}>
        <div className="search-panel">
          <input className="search-input" type="search" placeholder="搜索项目、工作记录、待办、事项…（空格分隔多个关键词）" value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} autoFocus />
          {searchResults ? (
            <div className="search-results">
              {searchResults.projectMatches.length > 0 && (
                <section className="search-group"><h3>项目</h3>{searchResults.projectMatches.map((project) => (
                  <button type="button" key={project.id} onClick={() => { setModal(null); openProject(project.id); }}><strong>{project.name}</strong><small>{projectTypeLabel(project)}</small></button>
                ))}</section>
              )}
              {searchResults.todoMatches.length > 0 && (
                <section className="search-group"><h3>待办</h3>{searchResults.todoMatches.map((todo) => (
                  <button type="button" key={todoKey(todo)} onClick={() => { setModal(null); setEditingTodoId(todoKey(todo)); setModal("todoEdit"); }}><strong>{todo.text}</strong><small>{todo.scheduledDate || "未安排日期"}</small></button>
                ))}</section>
              )}
              {searchResults.workMatches.length > 0 && (
                <section className="search-group"><h3>工作记录</h3>{searchResults.workMatches.map((record) => (
                  <button type="button" key={record.id} onClick={() => {
                    setSearchHit({ recordId: record.id, sentence: record.hitSentence, attachmentId: record.hitAttachmentId, kind: record.hitKind });
                    setSelectedLogDate(record.date);
                    setModal("dayOverview");
                  }}><strong>{record.date}{record.occurredAt ? ` ${record.occurredAt}` : ""}{record.hitAttachmentId ? " · 附件命中" : ""}</strong><small className="search-snippet">{renderSearchHighlight(record.snippet, debouncedQuery)}</small></button>
                ))}</section>
              )}
              {searchResults.matterMatches.length > 0 && (
                <section className="search-group"><h3>事项</h3>{searchResults.matterMatches.map((matter) => (
                  <button type="button" key={matter.id || matter.text} onClick={() => { setModal(null); setEditingMatterId(String(matter.id || "")); setModal("matter"); }}><strong>{matter.text}</strong><small>{matter.kind === "service" ? "服务类事项" : matter.kind === "administrative" ? "内部事项" : "其他事项"}</small></button>
                ))}</section>
              )}
              {!searchResults.projectMatches.length && !searchResults.todoMatches.length && !searchResults.workMatches.length && !searchResults.matterMatches.length && (
                <EmptyState title="没有找到匹配的内容" />
              )}
            </div>
          ) : (
            <p className="compact-empty">输入关键词开始搜索，可搜项目、待办、工作记录和事项。</p>
          )}
        </div>
      </Modal>

      <Modal open={modal === "e2eEnable"} title="开启端到端加密" onClose={() => setModal(null)}>
        <div className="e2e-panel">
          <p className="e2e-note">开启后，云端数据将只有你的密码能解开（平台也无法查看）。请先输入当前登录密码确认身份。</p>
          <label className="field-label">登录密码<input type="password" value={e2ePassword} onChange={(event) => setE2ePassword(event.target.value)} autoFocus /></label>
          <p className="e2e-warn">开启后会显示一次恢复码，请务必抄下来妥善保存；恢复码和密码都丢失时，数据将无法恢复。</p>
          <div className="form-actions"><button className="secondary-button" type="button" onClick={() => setModal(null)}>取消</button><button className="primary-button" type="button" onClick={enableE2E}>开启加密</button></div>
        </div>
      </Modal>

      <Modal open={modal === "recoveryCode"} title="保存恢复码" onClose={() => setModal(null)}>
        <div className="e2e-panel">
          <p className="e2e-note">以下恢复码只显示这一次，请抄写到安全的地方（不要存在这个应用里）。</p>
          <div className="recovery-code-box">{recoveryCodeShown}</div>
          <div className="form-actions"><button className="primary-button" type="button" onClick={() => { setRecoveryCodeShown(""); setModal(null); }}>我已保存恢复码</button></div>
        </div>
      </Modal>

      <Modal open={modal === "recovery"} title="解锁加密数据" dismissable={!pendingRecovery} onClose={() => { setPendingRecovery(false); setModal(null); }}>
        <div className="e2e-panel">
          <p className="e2e-note">本设备还没有加密钥匙。输入登录密码即可解锁；如果忘记密码，请点击下方“忘记密码”使用恢复码。</p>
          <label className="field-label">登录密码<input type="password" value={e2ePassword} onChange={(event) => setE2ePassword(event.target.value)} autoFocus /></label>
          <button className="text-button" type="button" onClick={() => setShowRecoveryFields((visible) => !visible)}>{showRecoveryFields ? "收起恢复方式" : "忘记密码？"}</button>
          {showRecoveryFields && (
            <>
              <label className="field-label">恢复码（可选）<input value={recoveryInput} onChange={(event) => setRecoveryInput(event.target.value)} placeholder="开启加密时抄下的恢复码" /></label>
              <label className="field-label">新的登录密码（用恢复码时填写）<input type="password" value={recoveryPassword} onChange={(event) => setRecoveryPassword(event.target.value)} placeholder="重置密码后设置的新密码" /></label>
            </>
          )}
          <div className="form-actions">{!pendingRecovery && <button className="secondary-button" type="button" onClick={() => { setPendingRecovery(false); setModal(null); }}>取消</button>}<button className="primary-button" type="button" onClick={unlockE2E}>解锁</button></div>
        </div>
      </Modal>

      <Modal open={modal === "securityCheck"} title="数据安全自检" onClose={() => setModal(null)}>
        <div className="security-report">
          {securityReport.length
            ? securityReport.map((line, index) => <p key={index}>{line}</p>)
            : <p className="compact-empty">检查进行中…</p>}
        </div>
      </Modal>

      <Modal open={modal === "admin"} title="账号使用情况" onClose={() => setModal("account")} wide>
        <div className="admin-usage-summary"><strong>{adminRows.length}</strong><span>个账号</span></div>
        {adminRows.length ? <div className="admin-table">
          <div className="admin-table-head"><span>账号</span><span>首次记录</span><span>最近打开 / 刷新</span></div>
          {adminRows.map((row) => <div key={row.user_id}>
            <strong>{row.email}</strong>
            <time>{new Date(row.first_seen_at).toLocaleString("zh-CN", { hour12: false })}</time>
            <time>{new Date(row.last_seen_at).toLocaleString("zh-CN", { hour12: false })}</time>
          </div>)}
        </div> : <EmptyState title="暂无账号使用记录" />}
      </Modal>
    </>
  );
}

function FieldEditor({ fields, onChange }: { fields: ConfigField[]; onChange: (fields: ConfigField[]) => void }) {
  return (
    <div className="field-editor">
      <div className="configured-fields">
        {fields.length ? fields.map((field, index) => (
          <div key={field.key}>
            <div><strong>{field.label}</strong><span>{field.type}{field.options?.length ? ` · ${field.options.join("、")}` : ""}</span></div>
            <div className="configured-field-actions">
              <button className="icon-button compact" type="button" disabled={index === 0} onClick={() => onChange(moveListItem(fields, index, -1))} aria-label={`上移${field.label}`}>↑</button>
              <button className="icon-button compact" type="button" disabled={index === fields.length - 1} onClick={() => onChange(moveListItem(fields, index, 1))} aria-label={`下移${field.label}`}>↓</button>
              <button className="danger-text" type="button" onClick={() => onChange(fields.filter((_, itemIndex) => itemIndex !== index))}>删除</button>
            </div>
          </div>
        )) : <EmptyState title="暂无字段" />}
      </div>
      <form className="inline-create" onSubmit={(event) => {
        event.preventDefault();
        const form = new FormData(event.currentTarget);
        const label = String(form.get("label") || "").trim();
        const type = form.get("type") as FieldType;
        const options = String(form.get("options") || "").split(/[,，]/).map((item) => item.trim()).filter(Boolean);
        if (!label) return;
        onChange([...fields, { key: `custom_${Date.now()}`, label, type, options: type === "select" ? options : undefined }]);
        event.currentTarget.reset();
      }}>
        <strong>新增字段</strong>
        <input name="label" required />
        <select name="type" defaultValue="date"><option value="date">日期</option><option value="number">金额／数字</option><option value="text">文字</option><option value="select">下拉选项</option></select>
        <input name="options" />
        <button className="primary-button small" type="submit">添加字段</button>
      </form>
    </div>
  );
}

function EventCustomFields({ initialFields = [] }: { initialFields?: Array<{ id: string; label: string; type: FieldType; value: string }> }) {
  const [fields, setFields] = useState<Array<{ id: string; type: FieldType; label?: string; value?: string }>>(
    () => initialFields.map((field) => ({ ...field })),
  );
  return (
    <div className="event-custom-fields">
      <div className="custom-field-head">
        <div><strong>自定义信息</strong><span>日期、金额、文字和选项都可以自行增加</span></div>
        <button className="secondary-button small" type="button" onClick={() => setFields((items) => [...items, { id: makeId(), type: "text" }])}>＋ 添加一项</button>
      </div>
      {fields.map((field) => (
        <div className="custom-field-row" key={field.id}>
          <input name="customLabel" aria-label="自定义字段名称" defaultValue={field.label || ""} />
          <select
            name="customType"
            aria-label="自定义字段类型"
            value={field.type}
            onChange={(event) => setFields((items) => items.map((item) => item.id === field.id ? { ...item, type: event.target.value as FieldType } : item))}
          >
            <option value="text">文字</option>
            <option value="date">日期</option>
            <option value="number">金额／数字</option>
            <option value="select">选项文字</option>
          </select>
          {field.type === "date"
            ? <SmartDateInput name="customValue" defaultValue={field.value || ""} />
            : <input name="customValue" aria-label="自定义字段内容" type={field.type === "number" ? "number" : "text"} step={field.type === "number" ? "0.01" : undefined} defaultValue={field.value || ""} />}
          <button className="danger-text" type="button" onClick={() => setFields((items) => items.filter((item) => item.id !== field.id))}>删除</button>
        </div>
      ))}
    </div>
  );
}
